import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { debounce, requestIdleCallback } from './utils/performanceHelpers';
import {
    validateFileSize,
    readFileWithTimeout,
    compressImageSmart,
    processPDF,
    isBase64TooLarge,
    estimateBase64Size
} from './utils/attachmentProcessor';
import {
    calculateAttachmentCost
} from './utils/advancedTokenCostSystem';
import { Message, Role, Attachment, ChatSession, PdfAnnotation, User, GlobalConfig, PromoCode, AdBanner, MessageDensity, GeneratedMaterial } from './types';
import { sendMessageToGemini, sendMessageToGeminiStream, requiresAdvancedModel, generateQuiz, generateMockExam } from './services/geminiService';
import { GoogleGenerativeAI, HarmCategory, HarmBlockThreshold } from '@google/generative-ai';
import { getUserRank, getRankGradient, getRankBadge, determineUserTier } from './utils/rankUtils';
import { hasUnlimitedTokens, getTokenMultiplier, cleanupExpiredEffects } from './utils/promoEffectsManager';
import { validateModel, getValidatedModelForUser } from './utils/modelValidator';
import { logFeedback, FeedbackItem } from './services/feedbackService';
import { processFeedback as processAILearningFeedback } from './services/aiLearningService';
import { localKnowledgeService } from './services/localKnowledgeService';
import PersonaSelector from './components/PersonaSelector';
import { PersonaId, ResponseDepth, PERSONAS, DEPTHS, calculateTokenCost as calculatePersonaTokenCost } from './config/aiPersonaConfig';
import ChatMessage from './components/ChatMessage';
import VirtualizedMessageList from './components/VirtualizedMessageList';
// Eager Components (Core UI & Logic)
import ChatSearchBar from './components/ChatSearchBar';
import Sidebar from './components/Sidebar';
import { useGamification } from './components/GamificationWidget';
import { confettiStyles } from './components/AchievementCelebration';
import LoadingScreen from './components/LoadingScreen';
import { useConfig } from './contexts/ConfigContext';
import { useFirebase } from './hooks/useFirebase';
import { useToasts, ToastContainer } from './hooks/useToasts';
import { usePWA } from './hooks/usePWA';
import { useDeviceTracking } from './hooks/useDeviceTracking';
import { useSettings } from './hooks/useSettings';

import { BACKGROUND_PACKAGES, type BackgroundPackage } from './config/chatBackgrounds';
import HeaderTokenTimer from './components/HeaderTokenTimer';
import ProfileDropdown from './components/profile/ProfileDropdown';
import Header from './components/layout/Header';
import { InstallButton } from './components/InstallButton';
import type { FeedbackData } from './components/FeedbackModal';

// Services & Hooks
import { lucasQuestService, QuestData } from './services/lucasQuestService';
import {
    trackQuestion,
    trackQuizComplete,
    trackStudyTime,
    trackSession,
    updateStreak,
    checkAchievements,
    checkMilestones,
    awardXP,
    getGamificationSummary,
    initializeGamification,
    completeMasteryNode,
    unlockMasteryNode,
    awardTopicXP,
    loadGamificationState,
    saveQuizResult,
    saveMockExamResult
} from './services/gamificationService';
// import { CPA_MASTERY_PATH } from './config/masteryConfig'; // Deprecated in favor of dynamic syllabus
import type { MasteryTopic } from './config/masteryConfig';
import { defaultTourSteps } from './components/tourConfig';
import { QuizConfig, MockExamConfig } from './services/ai/config_types';
import { updateOnlineStatus } from './services/socialService';
import { calculateAttachmentTokenCost, TokenCostBreakdown } from './utils/tokenCostCalculator';
import { refundTokens, shouldRefund, analyzeResponseQuality, processSmartRefund } from './utils/tokenRefundSystem';
import {
    calculateTokenCost,
    analyzeComplexity,
    detectFeatures,
    loadTokenCostConfig,
    CostBreakdown,
    CostCalculationInput
} from './utils/advancedTokenCostSystem';
import {
    tokenEstimationService,
    estimateChatTokens,
    estimateQuizTokens,
    estimateExamTokens,
    EstimationResult,
    ComplexityLevel
} from './services/tokenEstimationService';
import {
    trackAttachmentMessage,
    checkAndIncrementAttachmentContext,
    clearAttachmentContext
} from './utils/attachmentContextTracker';
import { migrateConfig } from './utils/configMigration';
import { separateComputationFromExplanation } from './utils/computationSeparator';
import { SUGGESTED_PROMPTS, STANDARDS_OPTIONS, DEFAULT_CONFIG } from './constants';
import { startUserSync, startSessionsSync, startConfigSync, stopAllSync } from './services/realtimeService';
import { saveChatSession, saveGeneratedMaterial, getUserGeneratedMaterials, listenToAds, createTransaction } from './services/firebaseService';
import { checkAndReplenishOnLogin } from './services/tokenReplenishmentService';
import { initDeferredLoading, markInteractive } from './utils/performanceLoader';
import { performanceService, isNativePlatform } from './services/nativePerformanceService';
import { isMobileEnvironment, getRecommendedOptimizationLevel, optimizeAnimations } from './services/mobileOptimizationService';
import {
    checkForAPKUpdate,
    isRunningInAPK,
    shouldCheckForUpdates,
    markUpdateChecked,
    wasUpdateRecentlyDismissed,
    initializeAPKBuildInfo,
    subscribeToAPKUpdates,
    APKUpdateInfo
} from './services/apkUpdateService';
import { installPWA, isRunningAsPWA, isIOS, canInstall, getInstallState, onInstallStateChange } from './utils/pwaInstaller';
import { LucideIcon, Send, Paperclip, X, FileText, ZoomIn, ZoomOut, StickyNote, ArrowRight, CheckSquare, Square, Lock, Activity, Layout, AlignJustify, Maximize, Crown, Wand2, Plus, Settings, Image, GraduationCap, Baby, BookOpen, Sparkles, FileQuestion, PlayCircle, BrainCircuit, AlertTriangle, ChevronLeft, ChevronRight, Brain, Pause, Folder, Download, Zap, Star, Users, Coins, FolderOpen } from 'lucide-react';

// Lazy Loaded Components
const SessionContentModal = React.lazy(() => import('./components/SessionContentModal'));
const AnnouncementDisplay = React.lazy(() => import('./components/AnnouncementDisplay'));
const LucasQuestNotification = React.lazy(() => import('./components/LucasQuestNotification'));
const LucasQuestInterface = React.lazy(() => import('./components/LucasQuestInterface'));
const GamificationWidget = React.lazy(() => import('./components/GamificationWidget'));
const GamificationModal = React.lazy(() => import('./components/GamificationModal'));
const AchievementCelebration = React.lazy(() => import('./components/AchievementCelebration'));
const ScrollToTop = React.lazy(() => import('./components/ScrollToTop'));
const AuthModal = React.lazy(() => import('./components/AuthModal'));
const GuidedTour = React.lazy(() => import('./components/GuidedTour'));
const PostOnboardingPWAPrompt = React.lazy(() => import('./components/PostOnboardingPWAPrompt'));
const PWAInstallLoadingScreen = React.lazy(() => import('./components/PWAInstallLoadingScreen'));
const OpenPWAPrompt = React.lazy(() => import('./components/OpenPWAPrompt'));
const GuestLimitModal = React.lazy(() => import('./components/GuestLimitModal'));
const ChangePasswordModal = React.lazy(() => import('./components/ChangePasswordModal'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const UpgradeModal = React.lazy(() => import('./components/UpgradeModal'));
const UsageModal = React.lazy(() => import('./components/UsageModal'));
const QuizInterface = React.lazy(() => import('./components/QuizInterface'));
const QuizConfigModal = React.lazy(() => import('./components/QuizConfigModal'));
const MockExamConfigModal = React.lazy(() => import('./components/MockExamConfigModal'));
const ExamInterface = React.lazy(() => import('./components/ExamInterface'));
const TokenCostModal = React.lazy(() => import('./components/TokenCostModal'));
const TokenRefreshBar = React.lazy(() => import('./components/TokenRefreshBar'));
const AttachmentCostModal = React.lazy(() => import('./components/AttachmentCostModal'));
const TokenCostConfirmationModal = React.lazy(() => import('./components/TokenCostConfirmationModal'));
const AdDisplay = React.lazy(() => import('./components/AdDisplay'));
const LandingPageV2 = React.lazy(() => import('./components/LandingPageV2'));
const PremiumDocumentViewer = React.lazy(() => import('./components/PremiumDocumentViewer'));
const FirebaseMigration = React.lazy(() => import('./components/FirebaseMigration'));
const UsageAlertModal = React.lazy(() => import('./components/UsageAlertModal'));
const ExportModal = React.lazy(() => import('./components/ExportModal'));
const WelcomeGreeting = React.lazy(() => import('./components/WelcomeGreeting'));
const GenerateMaterialsModal = React.lazy(() => import('./components/GenerateMaterialsModal'));
const PracticeProblemsConfigModal = React.lazy(() => import('./components/PracticeProblemsConfigModal'));
const StudyGuideConfigModal = React.lazy(() => import('./components/StudyGuideConfigModal'));
const SocialModal = React.lazy(() => import('./components/SocialModal'));
const ReadablesConfigModal = React.lazy(() => import('./components/ReadablesConfigModal'));
const ReadablesLibrary = React.lazy(() => import('./components/ReadablesLibrary'));
const GeneratedMaterialsLibrary = React.lazy(() => import('./components/GeneratedMaterialsLibrary'));
const QuickBitesConfigModal = React.lazy(() => import('./components/QuickBitesConfigModal'));
const QuickBitesViewer = React.lazy(() => import('./components/QuickBitesViewer'));
const EventModal = React.lazy(() => import('./components/EventModal'));
const EventBanner = React.lazy(() => import('./components/EventBanner'));
const DirectMessageModal = React.lazy(() => import('./components/DirectMessageModal'));
const NotificationsModal = React.lazy(() => import('./components/NotificationsModal'));
const GroupStudyModal = React.lazy(() => import('./components/GroupStudyModal'));
const ForceUpdateChecker = React.lazy(() => import('./components/ForceUpdateChecker'));
const StreamlinedOnboarding = React.lazy(() => import('./components/StreamlinedOnboarding'));
const MasteryChecklist = React.lazy(() => import('./components/MasteryChecklist'));
const ExtendPremiumModal = React.lazy(() => import('./components/ExtendPremiumModal'));
const TokenPurchaseModal = React.lazy(() => import('./components/TokenPurchaseModal'));
const CustomerSupportModal = React.lazy(() => import('./components/CustomerSupportModal'));
const ReferralModal = React.lazy(() => import('./components/ReferralModal'));
const MaintenanceScreen = React.lazy(() => import('./components/MaintenanceScreen'));
const TokenConsumptionAnimation = React.lazy(() => import('./components/TokenConsumptionAnimation'));
const PostQuizReviewOffer = React.lazy(() => import('./components/PostQuizReviewOffer'));
const FeedbackModal = React.lazy(() => import('./components/FeedbackModal'));
const UserDashboardModal = React.lazy(() => import('./components/UserDashboardModal'));
const PreliminaryExamModal = React.lazy(() => import('./components/mastery/PreliminaryExamModal'));
const TopicExamModal = React.lazy(() => import('./components/mastery/TopicExamModal'));
const MasteryPathModal = React.lazy(() => import('./components/mastery/MasteryPathModal'));
const TopicGuidePanel = React.lazy(() => import('./components/mastery/TopicGuidePanel'));
const GenerationLoadingModal = React.lazy(() => import('./components/GenerationLoadingModal'));
const LevelUpModal = React.lazy(() => import('./components/LevelUpModal'));
const APKUpdateModal = React.lazy(() => import('./components/APKUpdateModal'));
const OfflineIndicator = React.lazy(() => import('./components/OfflineIndicator'));
const ConnectionStatus = React.lazy(() => import('./components/ConnectionStatus'));
const StreakFrozenModal = React.lazy(() => import('./components/StreakFrozenModal').then(module => ({ default: module.StreakFrozenModal })));
const LowBalanceModal = React.lazy(() => import('./components/monetization/LowBalanceModal'));

// Study Components
const StudyDashboard = React.lazy(() => import('./components/study/StudyDashboard'));
const FlashcardReviewModal = React.lazy(() => import('./components/study/FlashcardReviewModal'));
const ErrorJournalModal = React.lazy(() => import('./components/study/ErrorJournalModal'));
const MootCourtModal = React.lazy(() => import('./components/study/MootCourtModal'));
const DocumentDraftingModal = React.lazy(() => import('./components/study/DocumentDraftingModal'));
const StudyModeIntroModal = React.lazy(() => import('./components/study/StudyModeIntroModal'));
const QuizModal = React.lazy(() => import('./components/study/QuizModal'));
const MockExamModal = React.lazy(() => import('./components/study/MockExamModal'));
const CodalBrowserModal = React.lazy(() => import('./components/study/CodalBrowserModal'));
const StandardsBrowserModal = React.lazy(() => import('./components/study/StandardsBrowserModal'));

import { extractReferencedAttachments } from './services/aiService';
import { sessionManager } from './utils/sessionManager';
import { connectionManager } from './utils/connectionManager';
import { checkAndCleanupIfNeeded, logStorageUsage } from './utils/storageCleanup';
import { estimateTokenCost, shouldShowTokenConfirmation, TokenEstimate } from './services/preEmptiveTokenEstimator';
import { feedbackRewardService, processFeedbackReward, hasMessageBeenRewarded, FeedbackRewardResult } from './services/feedbackRewardService';
import { analyzeComplexity as advancedAnalyzeComplexity } from './services/advancedComplexityAnalyzer';
import { generateMaterialPDF, downloadMaterialPDF } from './services/materialsPDFService';
import { smartRecommendationService, SmartRecommendation } from './services/smartRecommendationService';
import SmartRecommendationPopup from './components/SmartRecommendationPopup';

const App: React.FC = () => {
    // Firebase hook
    const firebase = useFirebase();

    // PWA hook for update management and installation
    const pwa = usePWA();

    // User settings from ProfileDropdown (localStorage-backed, reactive)
    const appSettings = useSettings();



    const [showUpdateDismissed, setShowUpdateDismissed] = useState(false);

    // APK Auto-Update State
    const [apkUpdateInfo, setApkUpdateInfo] = useState<APKUpdateInfo | null>(null);

    // Low Balance Modal State
    const [isLowBalanceModalOpen, setIsLowBalanceModalOpen] = useState(false);
    const [lowBalanceNeeded, setLowBalanceNeeded] = useState(0);
    const [showAPKUpdateModal, setShowAPKUpdateModal] = useState(false);

    // Level Up State
    const [levelUpData, setLevelUpData] = useState<{ oldLevel: number; newLevel: any; rewards: { tokens: number; unlocks: string[] } } | null>(null);

    // Preliminary Exam State
    const [isPreliminaryExamOpen, setIsPreliminaryExamOpen] = useState(false);
    const [selectedPreliminarySubject, setSelectedPreliminarySubject] = useState<string | null>(null);
    const [selectedTopicExamId, setSelectedTopicExamId] = useState<string | null>(null);
    const [selectedTopicExamSubjectId, setSelectedTopicExamSubjectId] = useState<string | null>(null);

    // Mastery Path Modal State
    const [isMasteryPathOpen, setIsMasteryPathOpen] = useState(false);
    // Floating Study Guide State
    const [activeGuideTopic, setActiveGuideTopic] = useState<MasteryTopic | null>(null);
    const [activeGuideSubjectId, setActiveGuideSubjectId] = useState<string | null>(null);
    const [dashboardInitialTab, setDashboardInitialTab] = useState<'overview' | 'stats' | 'leaderboard' | 'mastery' | 'store' | 'appearance'>('overview');
    const [storeInitialTab, setStoreInitialTab] = useState<'tokens' | 'unlimited' | 'premium' | 'max' | 'mastery' | undefined>(undefined);

    // Chat Background State
    const [chatBackground, setChatBackground] = useState<string | null>(null);

    useEffect(() => {
        const loadBackground = () => {
            try {
                const selectedId = localStorage.getItem('luca_user_background');

                // Import dynamically to get the latest definitions
                // Import dynamically to get the latest definitions
                // NO LONGER NEEDED - Static import from config

                if (!selectedId || selectedId === 'default') {
                    setChatBackground(null);
                    return;
                }

                // 1. PRIORITY: Check authoritative constants (Updates applied immediately)
                const officialBg = BACKGROUND_PACKAGES.find((bg: { id: string; preview: string }) => bg.id === selectedId);
                if (officialBg) {
                    setChatBackground(officialBg.preview);
                    return;
                }

                // 2. FALLBACK: Check saved/custom backgrounds (Legacy support)
                const savedBackgrounds = localStorage.getItem('luca_chat_backgrounds');
                if (savedBackgrounds) {
                    const backgrounds = JSON.parse(savedBackgrounds);
                    const selected = backgrounds.find((bg: any) => bg.id === selectedId);
                    if (selected) {
                        setChatBackground(selected.preview || selected.css);
                        return;
                    }
                }

                setChatBackground(null);
            } catch (error) {
                console.error('Failed to load chat background:', error);
                setChatBackground(null);
            }
        };

        loadBackground();

        const handleBackgroundChange = () => loadBackground();
        window.addEventListener('background_change', handleBackgroundChange);
        return () => window.removeEventListener('background_change', handleBackgroundChange);
    }, []);

    // Config & User State
    const {
        globalConfig,
        setGlobalConfig,
        refreshConfig,
        isLoading: isConfigLoading,
        uiConfig,
        featureConfig,
        contentConfig
    } = useConfig();
    const [config, setConfig] = useState<GlobalConfig>(DEFAULT_CONFIG);
    const [user, setUser] = useState<User | null>(() => {
        // Restore user from localStorage immediately to prevent logout on refresh
        try {
            const stored = localStorage.getItem('ledger_current_user') || localStorage.getItem('ledger_user');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (parsed && parsed.id && parsed.username) return parsed;
            }
        } catch (e) { /* will be restored in useEffect fallback */ }
        return null;
    });
    const [messageDensity, setMessageDensity] = useState<MessageDensity>('comfortable');
    const [isDarkMode, setIsDarkMode] = useState(true); // Dark mode state
    const [userCountry, setUserCountry] = useState<string>('Philippines'); // Default to PH

    // Device Tracking Hook
    useDeviceTracking(user, setUser);


    // Cache cleanup removed - reverted per user request

    // Check and cleanup localStorage on startup (prevents quota exceeded errors)
    useEffect(() => {
        try {
            // IMPORTANT: Run config migration FIRST (updates gemini-2.0-flash to gemini-2.5-flash-lite)
            migrateConfig();

            // Log storage usage in development
            if (process.env.NODE_ENV === 'development') {
                logStorageUsage();
            }
            // Proactively cleanup if storage is getting full
            checkAndCleanupIfNeeded();

            // Initialize deferred loading for non-critical services
            initDeferredLoading();
        } catch (e) {
            console.error('[App] Storage cleanup check failed:', e);
        }
    }, []);

    // APK Auto-Update: Check for updates on startup and periodically
    useEffect(() => {
        // Initialize APK build info (stores current build hash for comparison)
        initializeAPKBuildInfo();

        const checkAPKUpdate = async () => {
            // Only check if running in APK
            if (!isRunningInAPK()) {
                console.log('[APKUpdate] Not running in APK, skipping update check');
                return;
            }

            // Check if we should check for updates (avoid too frequent checks)
            if (!shouldCheckForUpdates()) {
                console.log('[APKUpdate] Too soon since last check, skipping');
                return;
            }

            // Check if user recently dismissed the update
            if (wasUpdateRecentlyDismissed()) {
                console.log('[APKUpdate] User recently dismissed update, skipping');
                return;
            }

            console.log('[APKUpdate] Checking for updates...');

            try {
                const updateInfo = await checkForAPKUpdate();

                if (updateInfo.available) {
                    console.log('[APKUpdate] Update available!', updateInfo);
                    setApkUpdateInfo(updateInfo);
                    setShowAPKUpdateModal(true);
                    markUpdateChecked();
                } else {
                    console.log('[APKUpdate] App is up to date');
                    markUpdateChecked();
                }
            } catch (error) {
                console.error('[APKUpdate] Update check failed:', error);
            }
        };

        // ✅ ENHANCED: Check on startup and periodically
        // Delay the initial check slightly to not interfere with initial app load
        const checkTimeout = setTimeout(() => {
            checkAPKUpdate();
        }, 3000); // Check 3 seconds after app loads

        // ✅ Also check periodically (every 30 minutes) when app is active
        const periodicCheckInterval = setInterval(() => {
            checkAPKUpdate();
        }, 30 * 60 * 1000); // Check every 30 minutes

        // ✅ Check when app comes back to foreground (important for mobile)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isRunningInAPK()) {
                console.log('[APKUpdate] App became visible, checking for updates...');
                checkAPKUpdate();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // ✅ REAL-TIME UPDATES: Subscribe to Firestore changes
        let unsubscribe = () => { };
        if (isRunningInAPK()) {
            unsubscribe = subscribeToAPKUpdates((updateInfo) => {
                if (updateInfo && updateInfo.available) {
                    console.log('[APKUpdate] Real-time update detected!', updateInfo);
                    setApkUpdateInfo(updateInfo);
                    setShowAPKUpdateModal(true);
                    markUpdateChecked();
                }
            });
        }

        return () => {
            clearTimeout(checkTimeout);
            clearInterval(periodicCheckInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            unsubscribe();
        };
    }, []);

    // Detect User Country (cached to avoid repeated API calls)
    useEffect(() => {
        const cachedCountry = localStorage.getItem('user_country');
        const cacheTime = localStorage.getItem('user_country_time');
        const ONE_DAY = 24 * 60 * 60 * 1000;

        if (cachedCountry && cacheTime && Date.now() - parseInt(cacheTime) < ONE_DAY) {
            setUserCountry(cachedCountry);
            return;
        }

        const fetchCountry = async () => {
            try {
                // Add timeout to prevent blocking page load
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

                const response = await fetch('https://ipapi.co/json/', {
                    signal: controller.signal
                });
                clearTimeout(timeoutId);

                const data = await response.json();
                if (data.country_name) {
                    setUserCountry(data.country_name);
                    localStorage.setItem('user_country', data.country_name);
                    localStorage.setItem('user_country_time', Date.now().toString());
                    console.log('User Country Detected:', data.country_name);
                }
            } catch (error) {
                console.log('Country detection skipped (timeout or error)');
                // Use default and don't block app
                setUserCountry('Philippines');
            }
        };

        // Defer country detection - don't block initial render
        setTimeout(() => fetchCountry(), 2000);
    }, []);

    // Welcome Modal State - Removed (integrated into UnifiedOnboarding)

    // Extend Premium Modal State
    const [isExtendPremiumOpen, setIsExtendPremiumOpen] = useState(false);

    // Customer Support Modal State
    const [isSupportOpen, setIsSupportOpen] = useState(false);

    // Token Purchase Modal State
    const [isTokenPurchaseOpen, setIsTokenPurchaseOpen] = useState(false);
    const [showLowTokenPrompt, setShowLowTokenPrompt] = useState(false);
    const [tokenPurchaseInitialTab, setTokenPurchaseInitialTab] = useState<'tokens' | 'unlimited' | 'premium' | 'max' | 'mastery'>('tokens');

    // Referral Modal State
    const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);

    // Ephemeral guest counter (for before they log in)
    const [guestMessageCount, setGuestMessageCount] = useState(0);

    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
    const [authMode, setAuthMode] = useState<'login' | 'register'>('register');
    const [showAdminPanel, setShowAdminPanel] = useState(false);

    // All sessions loaded from storage
    const [allSessions, setAllSessions] = useState<ChatSession[]>([]);
    // Sessions displayed to the current user
    const [displayedSessions, setDisplayedSessions] = useState<ChatSession[]>([]);

    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);

    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [attachments, setAttachments] = useState<Attachment[]>([]);

    // Token consumption animation state
    const [tokenAnimationData, setTokenAnimationData] = useState<{ tokens: number; visible: boolean }>({ tokens: 0, visible: false });
    const [isQuizConfigOpen, setIsQuizConfigOpen] = useState(false);
    const [quizInitialTopic, setQuizInitialTopic] = useState('');
    const [isMockExamConfigOpen, setIsMockExamConfigOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportTargetMessage, setExportTargetMessage] = useState<Message | undefined>(undefined);
    const [isExamActive, setIsExamActive] = useState(false);
    const [examData, setExamData] = useState<any>(null);

    // Sidebar State
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Profile Dropdown State
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const profileMenuRef = useRef<HTMLDivElement>(null);

    // PDF Split View State
    const [activePdf, setActivePdf] = useState<Attachment | null>(null);
    const [pdfZoom, setPdfZoom] = useState(1);
    const [pdfPage, setPdfPage] = useState(1);
    const [annotationText, setAnnotationText] = useState('');
    const [isAddingNote, setIsAddingNote] = useState(false);

    // Context Refresh Modal State
    const [isRefreshModalOpen, setIsRefreshModalOpen] = useState(false);
    const [selectedStandards, setSelectedStandards] = useState<string[]>([]);

    // Promo Codes for Upgrade Modal
    const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);

    // Ads State
    const [ads, setAds] = useState<AdBanner[]>([]);

    // Announcements State
    const [announcements, setAnnouncements] = useState<any[]>([]);

    // LUCA's Quest State
    const [activeQuest, setActiveQuest] = useState<QuestData | null>(null);
    const [isQuestInterfaceOpen, setIsQuestInterfaceOpen] = useState(false);
    const [questRewardMessage, setQuestRewardMessage] = useState<string>('');
    const [questQuestions, setQuestQuestions] = useState<any[]>([]);
    const [isGeneratingQuest, setIsGeneratingQuest] = useState(false);

    // Gamification State
    const [isGamificationModalOpen, setIsGamificationModalOpen] = useState(false);
    const [achievementUnlocks, setAchievementUnlocks] = useState<any[]>([]);
    const [xpGainAnimation, setXpGainAnimation] = useState<{ amount: number; visible: boolean }>({ amount: 0, visible: false });
    const [sessionStartTime] = useState(Date.now());

    // Premium Toggle State
    const [isPremiumModelEnabled, setIsPremiumModelEnabled] = useState(false);

    // Cost Confirmation State
    const [isCostConfirmationOpen, setIsCostConfirmationOpen] = useState(false);
    const [pendingMessage, setPendingMessage] = useState<{ text: string, attachments: Attachment[] } | null>(null);
    const [pendingAction, setPendingAction] = useState<(() => void) | null>(null); // Generic pending action
    const [estimatedCost, setEstimatedCost] = useState<{ tokens: number, limit: number, isComplex: boolean }>({ tokens: 0, limit: 0, isComplex: false });
    const [dontShowComplexModal, setDontShowComplexModal] = useState(false);

    // AI Customization State - Default to Assistant (standard) mode
    const [aiMode, setAiMode] = useState<'standard' | 'educator' | 'lawyer'>('standard');
    const [aiDepth, setAiDepth] = useState<'concise' | 'balanced' | 'detailed' | 'comprehensive'>('balanced');
    const [isPersonaSelectorOpen, setIsPersonaSelectorOpen] = useState(false);
    const [activeRecommendation, setActiveRecommendation] = useState<SmartRecommendation | null>(null);
    const [isAiMenuOpen, setIsAiMenuOpen] = useState(false);
    const [isPlusMenuOpen, setIsPlusMenuOpen] = useState(false);
    const [isGenerateMaterialsOpen, setIsGenerateMaterialsOpen] = useState(false);
    const [isPracticeProblemsConfigOpen, setIsPracticeProblemsConfigOpen] = useState(false);
    const [isStudyGuideConfigOpen, setIsStudyGuideConfigOpen] = useState(false);
    const [materialGenerationTopic, setMaterialGenerationTopic] = useState('');
    const [isReadablesConfigOpen, setIsReadablesConfigOpen] = useState(false);
    const [isReadablesLibraryOpen, setIsReadablesLibraryOpen] = useState(false);
    const [readableGenerating, setReadableGenerating] = useState(false);
    const [readableProgress, setReadableProgress] = useState(0);
    const [readableStatus, setReadableStatus] = useState('');
    // Feature Access Config
    const [featureAccessConfig, setFeatureAccessConfig] = useState<import('./types').FeatureAccessConfig | null>(null);
    // QuickBites state
    const [isQuickBitesConfigOpen, setIsQuickBitesConfigOpen] = useState(false);
    const [quickBitesGenerating, setQuickBitesGenerating] = useState(false);
    const [quickBitesProgress, setQuickBitesProgress] = useState(0);
    const [quickBitesStatus, setQuickBitesStatus] = useState('');
    const [quickBitesResult, setQuickBitesResult] = useState<any>(null);
    const [isQuickBitesViewerOpen, setIsQuickBitesViewerOpen] = useState(false);
    // Events state
    const [activeEvents, setActiveEvents] = useState<any[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
    const [socialInitialTab, setSocialInitialTab] = useState<'friends' | 'online' | 'requests' | 'leaderboard' | 'add'>('friends');
    const [isDirectMessageOpen, setIsDirectMessageOpen] = useState(false);
    const [messagingFriend, setMessagingFriend] = useState<any>(null);
    const [isNotificationsModalOpen, setIsNotificationsModalOpen] = useState(false);
    const [isGroupStudyModalOpen, setIsGroupStudyModalOpen] = useState(false);

    // Study Mode Modal States
    const [isStudyDashboardOpen, setIsStudyDashboardOpen] = useState(false);
    const [isFlashcardModalOpen, setIsFlashcardModalOpen] = useState(false);
    const [isErrorJournalModalOpen, setIsErrorJournalModalOpen] = useState(false);
    const [isMootCourtModalOpen, setIsMootCourtModalOpen] = useState(false);
    const [isDocumentDraftingModalOpen, setIsDocumentDraftingModalOpen] = useState(false);
    const [isStudyModeIntroOpen, setIsStudyModeIntroOpen] = useState(false);
    const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
    const [isMockExamModalOpen, setIsMockExamModalOpen] = useState(false);
    const [isCodalBrowserOpen, setIsCodalBrowserOpen] = useState(false);
    const [isStandardsBrowserOpen, setIsStandardsBrowserOpen] = useState(false);
    const [activeMasteryNodeId, setActiveMasteryNodeId] = useState<string | null>(null);

    const aiMenuRef = useRef<HTMLDivElement>(null);

    // Auto Budget Token State
    const [autoBudgetEnabled, setAutoBudgetEnabled] = useState(() => {
        const stored = localStorage.getItem('luca_auto_budget_enabled');
        return stored === 'true';
    });
    const [budgetPerRequest, setBudgetPerRequest] = useState(() => {
        const stored = localStorage.getItem('luca_budget_per_request');
        return stored ? parseInt(stored) : 1000;
    });

    // Session Content Modal State
    const [sessionContentModalOpen, setSessionContentModalOpen] = useState(false);
    const [selectedSessionForModal, setSelectedSessionForModal] = useState<ChatSession | null>(null);

    // AI Persona Dropdown State
    const [isAIPersonaOpen, setIsAIPersonaOpen] = useState(false);

    // Toast Notifications (using hook)
    const { toasts, showToast, removeToast } = useToasts();

    // Onboarding Modal State
    const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
    const [isUserDashboardOpen, setIsUserDashboardOpen] = useState(false);
    const [isMasteryChecklistOpen, setIsMasteryChecklistOpen] = useState(() => {
        // Don't auto-open if user hasn't completed onboarding yet
        return localStorage.getItem('luca_onboarding_completed') === 'true';
    });
    const [isStreakFrozenModalOpen, setIsStreakFrozenModalOpen] = useState(false);

    const closeAllModals = () => {
        setIsUserDashboardOpen(false);
        setIsStudyDashboardOpen(false);
        setIsFlashcardModalOpen(false);
        setIsErrorJournalModalOpen(false);
        setIsMootCourtModalOpen(false);
        setIsDocumentDraftingModalOpen(false);
        setIsQuizConfigOpen(false);
        setIsMockExamConfigOpen(false);
        setIsCodalBrowserOpen(false);
        setIsStandardsBrowserOpen(false);
        setIsSocialModalOpen(false);
        setIsNotificationsModalOpen(false);
        setIsGamificationModalOpen(false);
        setIsTokenPurchaseOpen(false);
        setIsExtendPremiumOpen(false);
        setIsSupportOpen(false);
        setIsUsageModalOpen(false);
        setIsRefreshModalOpen(false);
        setIsExportModalOpen(false);
        setIsGroupStudyModalOpen(false);
        setIsStudyModeIntroOpen(false);
        setIsStreakFrozenModalOpen(false);
    };

    const handleOpenSocial = (tab: 'friends' | 'online' | 'requests' | 'leaderboard' | 'add' = 'friends') => {
        setSocialInitialTab(tab);
        setIsSocialModalOpen(true);
    };

    const handleOpenDirectMessage = (friend: any) => {
        setMessagingFriend(friend);
        setIsDirectMessageOpen(true);
        setIsSocialModalOpen(false);
    };

    const handleOpenFeature = (featureId: string, data?: any) => {
        // Close all other modals first
        closeAllModals();

        // Handle session opening specifically
        if (featureId === 'session' && data) {
            handleSelectSession(data);
            return;
        }

        switch (featureId) {
            case 'study_dashboard': setIsStudyDashboardOpen(true); break;
            case 'flashcards': setIsFlashcardModalOpen(true); break;
            case 'error_journal': setIsErrorJournalModalOpen(true); break;
            case 'moot_court': setIsMootCourtModalOpen(true); break;
            case 'drafting': setIsDocumentDraftingModalOpen(true); break;
            case 'quiz': setIsQuizConfigOpen(true); break;
            case 'start-quiz':
                setIsQuizConfigOpen(true);
                if (data) {
                    setMaterialGenerationTopic(data.topic || '');
                    setQuizInitialTopic(data.topic || '');
                    if (data.masteryNodeId) {
                        setActiveMasteryNodeId(data.masteryNodeId);
                    }
                }
                break;
            case 'resume_quiz':
                if (data) {
                    if (typeof data === 'string') {
                        // If data is a string, treat it as a topic and open config
                        console.log('[App] Resuming quiz with topic:', data);
                        setQuizInitialTopic(data);
                        setIsQuizConfigOpen(true);
                    } else {
                        // If data is a full quiz object, use it directly
                        console.log('[App] Resuming quiz with data:', data);
                        setQuizData(data);
                        setIsQuizActive(true);
                    }
                } else {
                    // Fallback if no data provided
                    setIsQuizConfigOpen(true);
                }
                break;
            case 'mock_exam': setIsMockExamConfigOpen(true); break;
            case 'codal': setIsCodalBrowserOpen(true); break;
            case 'standards': setIsStandardsBrowserOpen(true); break;
            case 'files': /* TODO: Open files modal */ break;
            case 'social': setIsSocialModalOpen(true); break;
            case 'notifications': setIsNotificationsModalOpen(true); break;
        }
    };

    const handleOnboardingClose = (triggerTour: boolean = false) => {
        setIsOnboardingOpen(false);
        localStorage.setItem('luca_onboarding_completed', 'true');
        // Now open mastery checklist since onboarding is done
        setIsMasteryChecklistOpen(true);
        // Only trigger tour if explicitly requested and not already seen
        if (triggerTour && !localStorage.getItem('luca_tour_completed')) {
            setTimeout(() => setIsGuidedTourOpen(true), 600);
        }
    };

    // Guided Tour State (interactive UI walkthrough after onboarding)
    const [isGuidedTourOpen, setIsGuidedTourOpen] = useState(false);

    // Welcome Greeting State
    const [showWelcomeGreeting, setShowWelcomeGreeting] = useState(false);

    // PWA Install Flow State
    const [showPWAPrompt, setShowPWAPrompt] = useState(false);
    const [showPWALoading, setShowPWALoading] = useState(false);
    const [deferredPWAPrompt, setDeferredPWAPrompt] = useState<any>(null);

    // Loading State - Only show on very first app mount, never again
    // Store Purchase Handlers - Extracted for reuse
    const handlePurchaseTokens = useCallback(async (packageId: string, tokens: number, promoCode?: string, giftCode?: string) => {
        try {
            if (giftCode) {
                console.log(`[TokenPurchase] Gift code generated: ${giftCode}`);
                return { success: true, message: `Gift code created! Share it with a friend.` };
            }

            const freshUserData = localStorage.getItem('ledger_current_user');
            const freshUser: User = freshUserData ? JSON.parse(freshUserData) : user;

            if (!freshUser) {
                return { success: false, message: 'Please log in to purchase tokens.' };
            }

            const currentTokenBalance = freshUser.tokenBalance || 0;
            const newTokenBalance = currentTokenBalance + tokens;

            const updatedUser: User = {
                ...freshUser,
                tokenBalance: newTokenBalance,
                lastTokenPurchase: new Date().toISOString(),
                totalTokensPurchased: (freshUser.totalTokensPurchased || 0) + tokens,
                purchaseCount: (freshUser.purchaseCount || 0) + 1
            };

            localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
            const storedUsers = localStorage.getItem('ledger_users');
            if (storedUsers) {
                const users = JSON.parse(storedUsers);
                const updatedUsers = users.map((u: User) =>
                    u.id === updatedUser.id ? updatedUser : u
                );
                localStorage.setItem('ledger_users', JSON.stringify(updatedUsers));
            }

            setUser(updatedUser);

            if (firebase.updateUser) {
                firebase.updateUser(updatedUser.id, {
                    tokenBalance: newTokenBalance,
                    lastTokenPurchase: new Date().toISOString(),
                    totalTokensPurchased: (freshUser.totalTokensPurchased || 0) + tokens,
                    purchaseCount: (freshUser.purchaseCount || 0) + 1
                }).catch(err => console.error('[TokenPurchase] Firebase sync failed:', err));
            }

            // Create Transaction Record
            try {
                await createTransaction({
                    id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    userId: updatedUser.id,
                    type: 'token_purchase',
                    status: 'completed',
                    amount: tokens,
                    currency: 'tokens',
                    description: `Purchased ${tokens} tokens (${packageId})`,
                    metadata: {
                        packageId,
                        promoCode,
                        giftCode,
                        purchaseType: 'tokens'
                    },
                    createdAt: new Date().toISOString()
                });
            } catch (err) {
                console.error('[TokenPurchase] Failed to create transaction record:', err);
            }

            return { success: true, message: `${tokens.toLocaleString()} tokens added to your account!`, updatedUser };
        } catch (error) {
            console.error('[TokenPurchase] Failed:', error);
            return { success: false, message: 'Failed to credit tokens. Please contact support.' };
        }
    }, [user, setUser]);

    const handlePurchaseUnlimited = useCallback(async (packageId: string, durationMinutes: number, giftCode?: string) => {
        try {
            if (giftCode) {
                return { success: true, message: `Gift code created! Share it with a friend.` };
            }

            const freshUserData = localStorage.getItem('ledger_current_user');
            const freshUser: User = freshUserData ? JSON.parse(freshUserData) : user;

            if (!freshUser) return { success: false, message: 'Please log in to purchase.' };

            const expiryTime = new Date(Date.now() + durationMinutes * 60 * 1000).toISOString();

            const updatedUser: User = {
                ...freshUser,
                lastTokenPurchase: new Date().toISOString(),
                purchaseCount: (freshUser.purchaseCount || 0) + 1,
                activePromoEffects: {
                    ...freshUser.activePromoEffects,
                    unlimitedTokensUntil: expiryTime
                }
            };

            localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
            setUser(updatedUser);

            if (firebase.updateUser) {
                firebase.updateUser(updatedUser.id, {
                    lastTokenPurchase: new Date().toISOString(),
                    purchaseCount: (freshUser.purchaseCount || 0) + 1,
                    activePromoEffects: updatedUser.activePromoEffects
                }).catch(err => console.error('[UnlimitedPurchase] Firebase sync failed:', err));
            }

            // Create Transaction Record
            try {
                await createTransaction({
                    id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    userId: updatedUser.id,
                    type: 'subscription_purchase',
                    status: 'completed',
                    amount: 0,
                    currency: 'PHP',
                    description: `Purchased Unlimited Access (${durationMinutes} mins) - ${packageId}`,
                    metadata: {
                        packageId,
                        durationMinutes,
                        giftCode,
                        purchaseType: 'unlimited'
                    },
                    createdAt: new Date().toISOString()
                });
            } catch (err) {
                console.error('[UnlimitedPurchase] Failed to create transaction record:', err);
            }

            return { success: true, message: `Unlimited tokens activated until ${new Date(expiryTime).toLocaleString()}!`, updatedUser };
        } catch (error) {
            console.error('[UnlimitedPurchase] Failed:', error);
            return { success: false, message: 'Failed to activate. Please contact support.' };
        }
    }, [user, setUser]);

    const handlePurchasePremium = useCallback(async (packageId: string, durationDays: number, giftCode?: string) => {
        try {
            if (giftCode) return { success: true, message: `Gift code created! Share it with a friend.` };

            const freshUserData = localStorage.getItem('ledger_current_user');
            const freshUser: User = freshUserData ? JSON.parse(freshUserData) : user;

            if (!freshUser) return { success: false, message: 'Please log in to purchase.' };

            const now = new Date();
            let startDate = now.toISOString();
            let expiryDate: Date;

            if (freshUser.isPremium && freshUser.premiumExpiryDate) {
                const currentExpiry = new Date(freshUser.premiumExpiryDate);
                const baseDate = currentExpiry > now ? currentExpiry : now;
                expiryDate = new Date(baseDate.getTime() + durationDays * 24 * 60 * 60 * 1000);
                startDate = freshUser.premiumStartDate || now.toISOString();
            } else {
                expiryDate = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000);
            }

            const updatedUser: User = {
                ...freshUser,
                isPremium: true,
                accountTier: 'premium',
                premiumStartDate: startDate,
                premiumExpiryDate: expiryDate.toISOString(),
                lastTokenPurchase: new Date().toISOString(),
                purchaseCount: (freshUser.purchaseCount || 0) + 1
            };

            localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
            setUser(updatedUser);

            if (firebase.updateUser) {
                firebase.updateUser(updatedUser.id, {
                    isPremium: true,
                    accountTier: 'premium',
                    premiumStartDate: startDate,
                    premiumExpiryDate: expiryDate.toISOString(),
                    lastTokenPurchase: new Date().toISOString(),
                    purchaseCount: (freshUser.purchaseCount || 0) + 1
                }).catch(err => console.error('[PremiumPurchase] Firebase sync failed:', err));
            }

            // Create Transaction Record
            try {
                await createTransaction({
                    id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    userId: updatedUser.id,
                    type: 'subscription_purchase',
                    status: 'completed',
                    amount: 0,
                    currency: 'PHP',
                    description: `Purchased Premium Subscription (${durationDays} days) - ${packageId}`,
                    metadata: {
                        packageId,
                        durationDays,
                        giftCode,
                        purchaseType: 'premium'
                    },
                    createdAt: new Date().toISOString()
                });
            } catch (err) {
                console.error('[PremiumPurchase] Failed to create transaction record:', err);
            }

            return { success: true, message: `Premium activated until ${expiryDate.toLocaleDateString()}!`, updatedUser };
        } catch (error) {
            console.error('[PremiumPurchase] Failed:', error);
            return { success: false, message: 'Failed to activate. Please contact support.' };
        }
    }, [user, setUser]);

    const handlePurchaseMax = useCallback(async (packageId: string, durationDays: number, giftCode?: string) => {
        try {
            if (giftCode) return { success: true, message: `Gift code created! Share it with a friend.` };

            const freshUserData = localStorage.getItem('ledger_current_user');
            const freshUser: User = freshUserData ? JSON.parse(freshUserData) : user;

            if (!freshUser) return { success: false, message: 'Please log in to purchase.' };

            const now = new Date();
            let startDate = now.toISOString();
            let expiryDate: Date;

            if ((freshUser.isPremium || freshUser.isMaxTier) && freshUser.premiumExpiryDate) {
                const currentExpiry = new Date(freshUser.premiumExpiryDate);
                const baseDate = currentExpiry > now ? currentExpiry : now;
                expiryDate = new Date(baseDate.getTime() + durationDays * 24 * 60 * 60 * 1000);
                startDate = freshUser.premiumStartDate || now.toISOString();
            } else {
                expiryDate = new Date(now.getTime() + durationDays * 24 * 60 * 60 * 1000);
            }

            const updatedUser: User = {
                ...freshUser,
                isPremium: true,
                isMax: true,
                isMaxTier: true,
                accountTier: 'max',
                premiumStartDate: startDate,
                premiumExpiryDate: expiryDate.toISOString(),
                lastTokenPurchase: new Date().toISOString(),
                purchaseCount: (freshUser.purchaseCount || 0) + 1
            };

            localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
            setUser(updatedUser);

            if (firebase.updateUser) {
                firebase.updateUser(updatedUser.id, {
                    isPremium: true,
                    isMax: true,
                    isMaxTier: true,
                    accountTier: 'max',
                    premiumStartDate: startDate,
                    premiumExpiryDate: expiryDate.toISOString(),
                    lastTokenPurchase: new Date().toISOString(),
                    purchaseCount: (freshUser.purchaseCount || 0) + 1
                }).catch(err => console.error('[MaxPurchase] Firebase sync failed:', err));
            }

            // Create Transaction Record
            try {
                await createTransaction({
                    id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    userId: updatedUser.id,
                    type: 'subscription_purchase',
                    status: 'completed',
                    amount: 0,
                    currency: 'PHP',
                    description: `Purchased MAX Subscription (${durationDays} days) - ${packageId}`,
                    metadata: {
                        packageId,
                        durationDays,
                        giftCode,
                        purchaseType: 'max'
                    },
                    createdAt: new Date().toISOString()
                });
            } catch (err) {
                console.error('[MaxPurchase] Failed to create transaction record:', err);
            }

            return { success: true, message: `MAX tier activated until ${expiryDate.toLocaleDateString()}!`, updatedUser };
        } catch (error) {
            console.error('[MaxPurchase] Failed:', error);
            return { success: false, message: 'Failed to activate. Please contact support.' };
        }
    }, [user, setUser]);

    const handleBackgroundPurchase = useCallback(async (background: BackgroundPackage) => {
        try {
            const localExisting = localStorage.getItem('luca_chat_backgrounds');
            const localBackgrounds = localExisting ? JSON.parse(localExisting) : [];
            const userUnlocked = user?.unlockedBackgrounds || [];

            const isOwnedLocally = localBackgrounds.find((b: any) => b.id === background.id);
            const isOwnedByUser = userUnlocked.includes(background.id);

            if (isOwnedLocally || isOwnedByUser) return { success: false, message: 'You already own this background!' };

            if (user) {
                const updatedUser = {
                    ...user,
                    unlockedBackgrounds: [...(user.unlockedBackgrounds || []), background.id],
                    chatBackground: background.preview
                };
                setUser(updatedUser);
                localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
                if (firebase.updateUser) {
                    firebase.updateUser(user.id, {
                        unlockedBackgrounds: updatedUser.unlockedBackgrounds,
                        chatBackground: updatedUser.chatBackground
                    }).catch(console.error);
                }
            }

            // Create Transaction Record
            try {
                if (user) {
                    await createTransaction({
                        id: `txn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                        userId: user.id,
                        type: 'background_purchase',
                        status: 'completed',
                        amount: 0, // Assuming backgrounds are free or price not passed here
                        currency: 'PHP',
                        description: `Unlocked background: ${background.name}`,
                        metadata: {
                            backgroundId: background.id,
                            backgroundName: background.name,
                            purchaseType: 'background'
                        },
                        createdAt: new Date().toISOString()
                    });
                }
            } catch (err) {
                console.error('[BackgroundPurchase] Failed to create transaction record:', err);
            }

            const newBg = { id: background.id, name: background.name, css: background.preview, description: background.description };
            localBackgrounds.push(newBg);
            localStorage.setItem('luca_chat_backgrounds', JSON.stringify(localBackgrounds));
            localStorage.setItem('luca_user_background', background.id);
            setChatBackground(background.preview);
            window.dispatchEvent(new Event('background_change'));

            return { success: true, message: `${background.name} unlocked and applied!` };
        } catch (error) {
            console.error('[BackgroundPurchase] Failed:', error);
            return { success: false, message: 'Failed to unlock background.' };
        }
    }, [user, setUser, setChatBackground]);

    const commonStoreProps = useMemo(() => ({
        user: user!,
        config: config || DEFAULT_CONFIG,
        promoCodes,
        isDarkMode,
        showLowTokenPrompt,
        onUserUpdate: setUser,
        onPromoCodesUpdate: (updatedCodes: PromoCode[]) => setPromoCodes(updatedCodes),
        showToast,
        onPurchase: handlePurchaseTokens,
        onPurchaseUnlimited: handlePurchaseUnlimited,
        onPurchasePremium: handlePurchasePremium,
        onPurchaseMax: handlePurchaseMax,
        onPurchaseBackground: handleBackgroundPurchase,
        onPurchaseMastery: async (productId: string, subjectIds: string[], price: number) => {
            try {
                const { recordMasteryPurchase } = await import('./services/masteryStoreService');
                const txnId = `mastery_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                recordMasteryPurchase(productId, subjectIds, txnId);
                try {
                    await createTransaction({
                        id: txnId,
                        userId: user?.id || 'anonymous',
                        type: 'mastery_purchase',
                        status: 'completed',
                        amount: price,
                        currency: 'PHP',
                        description: `Mastery Path: ${productId} (${subjectIds.join(', ')})`,
                        metadata: { productId, subjectIds, purchaseType: 'mastery' },
                        createdAt: new Date().toISOString()
                    });
                } catch (err) { console.error('[MasteryPurchase] Transaction record failed:', err); }
                return { success: true, message: `Unlocked: ${subjectIds.map(s => s.toUpperCase()).join(', ')}!` };
            } catch (error) {
                console.error('[MasteryPurchase] Failed:', error);
                return { success: false, message: 'Purchase failed. Please try again.' };
            }
        },
    }), [user, config, promoCodes, isDarkMode, showLowTokenPrompt, setUser, setPromoCodes, showToast, handlePurchaseTokens, handlePurchaseUnlimited, handlePurchasePremium, handlePurchaseMax, handleBackgroundPurchase]);

    const [isInitialLoading, setIsInitialLoading] = useState(() => {
        // Check if loading screen has already been dismissed in this browser session
        const hasBeenDismissed = sessionStorage.getItem('luca_initial_loading_dismissed');
        return hasBeenDismissed !== 'true';
    });

    // Landing Page State - Controlled by admin config
    const [showLandingPage, setShowLandingPage] = useState(() => {
        // Check landing page config
        const landingConfig = localStorage.getItem('luca_landing_config');
        if (landingConfig) {
            try {
                const config = JSON.parse(landingConfig);
                if (!config.enabled) return false; // Disabled in admin
            } catch (e) { }
        }
        // Show landing page for unauthenticated users who haven't dismissed it
        const storedUser = localStorage.getItem('ledger_user') || localStorage.getItem('ledger_current_user');
        const hasSeenLanding = localStorage.getItem('luca_landing_dismissed');
        return !storedUser && !hasSeenLanding;
    });

    // Guest Limit Modal
    const [showGuestLimitModal, setShowGuestLimitModal] = useState(false);

    // Initial loading and onboarding
    useEffect(() => {
        // Enhanced loading with actual readiness detection
        const handleAppReady = () => {
            console.log('[App] 🚀 App ready, dismissing loading screen');
            setIsInitialLoading(false);
            sessionStorage.setItem('luca_initial_loading_dismissed', 'true');

            // Show onboarding after loading completes with smooth delay
            const hasSeenOnboarding = localStorage.getItem('luca_onboarding_completed');
            if (!hasSeenOnboarding) {
                setTimeout(() => setIsOnboardingOpen(true), 800);
            }
        };

        // Check if app resources are loaded
        if (document.readyState === 'complete') {
            handleAppReady();
        } else {
            window.addEventListener('load', handleAppReady);
        }

        // SAFETY: Force dismiss loading screen after max 5 seconds (prevents gray screen hang)
        const safetyTimeout = setTimeout(() => {
            console.warn('[App] ⚠️ Safety timeout: Force dismissing loading screen if still active');
            setIsInitialLoading(false);
            sessionStorage.setItem('luca_initial_loading_dismissed', 'true');
        }, 5000);

        return () => {
            window.removeEventListener('load', handleAppReady);
            clearTimeout(safetyTimeout);
        };
    }, []);

    // Check for Frozen Streak on Startup
    useEffect(() => {
        // Delay slightly to ensure gamification state is loaded
        const timeout = setTimeout(() => {
            try {
                // We need to access the gamification state directly or via helper
                // Since hooks might not be ready, we can use the service helper if available
                // Or just rely on the gamification widget/context effect.
                // But App.tsx doesn't use GamificationContext directly? 
                // Ah, line 30 imports useGamification from Widget, but that might be specific.
                // Let's use the service loader.
                import('./services/gamificationService').then(({ loadGamificationState }) => {
                    const state = loadGamificationState();
                    if (state.streakStatus === 'frozen') {
                        setIsStreakFrozenModalOpen(true);
                    }
                });
            } catch (e) {
                console.error('[App] Failed to check streak status:', e);
            }
        }, 2000);
        return () => clearTimeout(timeout);
    }, []);

    // Inject dynamic CSS variables for global theme
    useEffect(() => {
        if (!uiConfig) return;

        const root = document.documentElement;

        // Colors
        root.style.setProperty('--color-primary', uiConfig.primaryColor);
        root.style.setProperty('--color-secondary', uiConfig.secondaryColor);
        root.style.setProperty('--color-accent', uiConfig.accentColor);

        // Fonts
        const fontMap: Record<string, string> = {
            'inter': '"Inter", sans-serif',
            'outfit': '"Outfit", sans-serif',
            'space-grotesk': '"Space Grotesk", sans-serif',
            'plus-jakarta': '"Plus Jakarta Sans", sans-serif',
            'satoshi': '"Satoshi", sans-serif',
        };
        root.style.setProperty('--font-family-main', fontMap[uiConfig.fontFamily] || fontMap['outfit']);

        // Border Radius
        const radiusMap: Record<string, string> = {
            'sharp': '0px',
            'rounded': '0.75rem', // 12px
            'pill': '1.5rem', // 24px
        };
        root.style.setProperty('--border-radius-main', radiusMap[uiConfig.borderRadius] || radiusMap['rounded']);

    }, [uiConfig]);

    // Mobile/APK Performance Optimization & Settings Application
    useEffect(() => {
        const initMobileOptimizations = async () => {
            if (isMobileEnvironment() || isNativePlatform()) {
                console.log('[App] Initializing mobile/APK performance optimizations');

                // Load saved APK settings
                const savedSettings = localStorage.getItem('luca_apk_settings');
                if (savedSettings) {
                    try {
                        const settings = JSON.parse(savedSettings);

                        // Apply Performance Settings
                        document.documentElement.style.setProperty(
                            '--animation-duration-multiplier',
                            settings.targetFPS === 30 ? '2' : settings.targetFPS === 60 ? '1' : '0.5'
                        );

                        if (settings.reducedAnimations) {
                            document.documentElement.classList.add('reduce-motion');
                        }

                        if (settings.enableGPURendering) {
                            document.documentElement.classList.add('gpu-accelerated');
                        }

                        // Apply Theme Settings (Auto Theme)
                        if (settings.autoTheme) {
                            const now = new Date();
                            const currentHour = now.getHours();
                            const startHour = parseInt(settings.autoThemeStartTime?.split(':')[0] || '18');
                            const endHour = parseInt(settings.autoThemeEndTime?.split(':')[0] || '6');

                            // Simple check: is it night time?
                            const isNight = currentHour >= startHour || currentHour < endHour;
                            setIsDarkMode(isNight);
                        }
                    } catch (e) {
                        console.error('[App] Failed to apply saved APK settings', e);
                    }
                }

                // Get recommended optimization level based on device conditions
                const optimizationLevel = await getRecommendedOptimizationLevel();

                // Apply battery-aware animation optimization
                if (optimizationLevel === 'high') {
                    optimizeAnimations(true);
                }

                // Add mobile-specific body classes
                document.body.classList.add('mobile-optimized');
                if (isNativePlatform()) {
                    document.body.classList.add('native-app');
                }

                // Ensure viewport is properly configured for mobile
                const viewport = document.querySelector('meta[name="viewport"]');
                if (viewport) {
                    viewport.setAttribute('content',
                        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
                    );
                }
            }
        };

        initMobileOptimizations();

        return () => {
            document.body.classList.remove('mobile-optimized', 'native-app');
        };
    }, []);

    // Listen for fileOpened event from Android native code (File Association)
    useEffect(() => {
        const handleFileOpened = (event: any) => {
            try {
                const { name, data, mimeType } = event.detail;
                console.log('[App] File opened from native:', name, mimeType);

                if (name && data && mimeType) {
                    // Create attachment object
                    const attachment: Attachment = {
                        id: `native-${Date.now()}`,
                        name,
                        data, // Base64 data
                        mimeType,
                        size: estimateBase64Size(data),
                        preview: `data:${mimeType};base64,${data}`
                    };

                    // Open viewer
                    setActivePdf(attachment);
                }
            } catch (error) {
                console.error('[App] Error handling native file open:', error);
            }
        };

        window.addEventListener('fileOpened', handleFileOpened);
        return () => window.removeEventListener('fileOpened', handleFileOpened);
    }, []);

    // Hide landing page when user logs in
    const hasLoggedLandingHide = useRef(false);
    useEffect(() => {
        if (user && !user.isAnonymous) {
            if (!hasLoggedLandingHide.current) {
                console.log('[App] User logged in, hiding landing page');
                hasLoggedLandingHide.current = true;
            }
            setShowLandingPage(false);
            localStorage.setItem('luca_landing_dismissed', 'true');
        }
    }, [user]);

    // PWA Install Prompt Capture
    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPWAPrompt(e);
            // Silently capture - reduce console noise
            // console.log('[PWA] Install prompt captured and ready');
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    // Check and show PWA prompt
    const checkAndShowPWAPrompt = useCallback(() => {
        // Quick check - don't run if prompt is already showing
        if (showPWAPrompt || showPWALoading) {
            return false;
        }

        const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true;

        // Don't show if already installed
        if (isPWAInstalled) {
            return false;
        }

        // Check if permanently installed
        if (localStorage.getItem('pwa_installed') === 'true') {
            return false;
        }

        // Check if user permanently dismissed the prompt
        if (localStorage.getItem('pwa_install_prompt_never_show') === 'true') {
            return false;
        }

        // Check if install is available (has deferred prompt or is iOS)
        const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        const canInstall = !!deferredPWAPrompt || isIOSDevice;

        // Only show if install is possible
        if (!canInstall) {
            return false;
        }

        // Show prompt
        setShowPWAPrompt(true);
        return true;
    }, [deferredPWAPrompt, showPWAPrompt, showPWALoading]);

    // PWA Prompt - Show on page load and periodically
    useEffect(() => {
        // Wait for initial loading to complete
        if (isInitialLoading) return;

        // Wait for onboarding to close
        if (isOnboardingOpen) return;

        // Don't set up timers if already installed
        const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches;
        if (isPWAInstalled || localStorage.getItem('pwa_installed') === 'true') {
            return;
        }

        // Don't show PWA prompt if guided tour is active
        if (!localStorage.getItem('luca_tour_completed')) {
            // First-time user: wait much longer before showing PWA prompt
            const initialTimer = setTimeout(() => {
                checkAndShowPWAPrompt();
            }, 30000); // 30 seconds for first-time users
            return () => clearTimeout(initialTimer);
        }

        // Show on page load (after 5 seconds for returning users)
        const initialTimer = setTimeout(() => {
            checkAndShowPWAPrompt();
        }, 5000);

        // Show every 15 minutes
        const recurringTimer = setInterval(() => {
            checkAndShowPWAPrompt();
        }, 15 * 60 * 1000); // 15 minutes

        return () => {
            clearTimeout(initialTimer);
            clearInterval(recurringTimer);
        };
    }, [isInitialLoading, isOnboardingOpen, checkAndShowPWAPrompt]);



    // Handle PWA loading screen completion
    const handlePWALoadingComplete = useCallback(() => {
        setShowPWALoading(false);

        // If running as PWA now, we could show a success message
        const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches;
        if (isPWAInstalled) {
            showToast('success', 'Luca app installed successfully!');
        }
    }, [showToast]);

    // Quiz State
    const [isQuizActive, setIsQuizActive] = useState(false);
    const [quizData, setQuizData] = useState<any>(null);
    const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false);
    const [quizProgress, setQuizProgress] = useState({ percent: 0, status: '' });
    const [examProgress, setExamProgress] = useState({ percent: 0, status: '' });

    // Generated Materials State
    const [generatedMaterials, setGeneratedMaterials] = useState<GeneratedMaterial[]>([]);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [pendingMaterialPDF, setPendingMaterialPDF] = useState<{
        type: 'practice-problems' | 'study-guide';
        topic: string;
    } | null>(null);
    const [feedbackMessageId, setFeedbackMessageId] = useState<string>('');
    const [feedbackMessageContent, setFeedbackMessageContent] = useState<string>('');
    const [feedbackType, setFeedbackType] = useState<'up' | 'down'>('up');
    const [feedbackTokensCharged, setFeedbackTokensCharged] = useState<number>(0);

    // Token Refresh & Cost Modal State
    const [pendingFile, setPendingFile] = useState<{ file: File, estimatedCost: number } | null>(null);
    const [usageNotifications, setUsageNotifications] = useState<Set<number>>(() => {
        // Persist notifications in sessionStorage to prevent re-showing on page reload
        try {
            const saved = sessionStorage.getItem('luca_usage_notifications');
            if (saved) {
                const arr = JSON.parse(saved);
                return new Set(arr);
            }
        } catch (e) {
            console.error('[App] Failed to load usage notifications:', e);
        }
        return new Set();
    });

    // Usage Alert Modal State
    const [usageAlertModal, setUsageAlertModal] = useState<{
        isOpen: boolean;
        type: 'info' | 'warning' | 'critical';
        usagePercent: number;
    }>({ isOpen: false, type: 'info', usagePercent: 0 });

    // Attachment Cost Modal State
    const [showAttachmentCostModal, setShowAttachmentCostModal] = useState(false);
    const [pendingAttachments, setPendingAttachments] = useState<File[]>([]);
    const [attachmentCostBreakdown, setAttachmentCostBreakdown] = useState<TokenCostBreakdown | null>(null);

    // Token Cost Modal State (for file uploads)
    const [showTokenCostModal, setShowTokenCostModal] = useState(false);

    // Advanced Token Cost Confirmation Modal State
    const [showAdvancedCostModal, setShowAdvancedCostModal] = useState(false);
    const [advancedCostBreakdown, setAdvancedCostBreakdown] = useState<CostBreakdown | null>(null);
    const [pendingOperation, setPendingOperation] = useState<{
        name: string;
        action: () => void;
    } | null>(null);

    // Pre-emptive token warning state removed - messages proceed directly

    // Connection & Offline State
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [connectionSpeed, setConnectionSpeed] = useState<'fast' | 'moderate' | 'slow' | 'offline'>('moderate');

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const chatInputAreaRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isPaused, setIsPaused] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    // Click Outside Listener for Profile Menu
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
                setIsProfileMenuOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Monitor connection quality
    useEffect(() => {
        const unsubscribe = connectionManager.subscribe((metrics) => {
            setIsOnline(metrics.isOnline);

            // Map connection quality to existing speed state
            const speedMap: Record<string, 'fast' | 'moderate' | 'slow' | 'offline'> = {
                'excellent': 'fast',
                'good': 'fast',
                'fair': 'moderate',
                'poor': 'slow',
                'offline': 'offline'
            };
            setConnectionSpeed(speedMap[metrics.quality]);
        });

        return unsubscribe;
    }, []);

    // OPTIMIZATION: Pause expensive operations when app goes to background
    // Reduces battery consumption by up to 75% in background
    useEffect(() => {
        let debounceTimer: NodeJS.Timeout | null = null;
        let lastState: boolean | null = null;

        const handleVisibilityChange = () => {
            // Debounce rapid visibility changes (common on mobile)
            if (debounceTimer) clearTimeout(debounceTimer);

            debounceTimer = setTimeout(() => {
                const isHidden = document.hidden;

                // Skip if state hasn't actually changed
                if (lastState === isHidden) return;
                lastState = isHidden;

                if (isHidden) {
                    // App went to background - pause expensive operations
                    stopAllSync();
                    window.postMessage({ type: 'APP_BACKGROUND' }, '*');
                } else {
                    // App came to foreground - resume operations
                    if (user && !user.isAnonymous) {
                        // Restart user sync
                        startUserSync(user.id, (updatedUser) => {
                            if (updatedUser) {
                                // Merge with local gamification stats to ensure we don't regress
                                try {
                                    const gamStats = getGamificationSummary();
                                    if (gamStats && gamStats.xp.total > (updatedUser.totalXP || 0)) {
                                        console.log('[App] Preserving local gamification stats over Firebase:', gamStats);
                                        updatedUser.totalXP = gamStats.xp.total;
                                        updatedUser.level = gamStats.level.level;
                                        updatedUser.streak = gamStats.streak.current;
                                    }
                                } catch (e) {
                                    console.error('[App] Failed to merge gamification stats in sync:', e);
                                }

                                setUser(updatedUser);
                                localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
                            }
                        });

                        // Restart sessions sync
                        startSessionsSync(user.id, (sessions) => {
                            setAllSessions(sessions);
                        });
                    }

                    // Restart config sync (works for all users including anonymous)
                    startConfigSync((cfg) => {
                        if (cfg) setConfig(cfg);
                    });

                    window.postMessage({ type: 'APP_FOREGROUND' }, '*');
                }
            }, 300); // 300ms debounce to prevent rapid toggling
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, [user]);

    // Initialize: Load Config, Sessions, User, Ads, Density
    useEffect(() => {
        try {
            // Load Global Config (Simulating DB fetch)
            const storedConfig = localStorage.getItem('ledger_config');
            if (storedConfig) {
                const parsedConfig = JSON.parse(storedConfig);

                // If API key is missing from stored config, migrate from env
                if (!parsedConfig.apiKey && process.env.API_KEY) {
                    parsedConfig.apiKey = process.env.API_KEY;
                }

                // Ensure PayPal config is always available
                if (!parsedConfig.paypal && DEFAULT_CONFIG.paypal) {
                    parsedConfig.paypal = DEFAULT_CONFIG.paypal;
                }

                localStorage.setItem('ledger_config', JSON.stringify(parsedConfig));
                setConfig(parsedConfig);
            } else {
                // First time load: migrate from env if available
                const initialConfig = { ...DEFAULT_CONFIG };
                if (process.env.API_KEY) {
                    initialConfig.apiKey = process.env.API_KEY;
                }
                localStorage.setItem('ledger_config', JSON.stringify(initialConfig));
                setConfig(initialConfig);
            }
        } catch (error) {
            console.error('Error loading config:', error);
            setConfig(DEFAULT_CONFIG);
        }

        try {
            // Load Density
            const storedDensity = localStorage.getItem('ledger_ui_density');
            if (storedDensity) {
                setMessageDensity(storedDensity as MessageDensity);
            }

            // Load Dark Mode Preference
            const storedDarkMode = localStorage.getItem('ledger_dark_mode');
            if (storedDarkMode !== null) {
                setIsDarkMode(storedDarkMode === 'true');
            }

            // Welcome modal logic removed - now handled by UnifiedOnboarding
        } catch (error) {
            console.error('Error loading UI preferences:', error);
        }

        // Load Ads from Firebase
        try {
            const unsubscribeAds = listenToAds((loadedAds) => {
                console.log('[App] Ads loaded from Firebase:', loadedAds);
                setAds(loadedAds);
            });

            return () => {
                if (unsubscribeAds) unsubscribeAds();
            };
        } catch (error) {
            console.error('[App] Error loading ads:', error);
        }

        try {
            // Load Generated Materials
            const storedMaterials = localStorage.getItem('ledger_generated_materials');
            if (storedMaterials) {
                setGeneratedMaterials(JSON.parse(storedMaterials));
            }

            // Load Promo Codes - First from localStorage, then sync from Firebase
            const loadPromoCodes = async () => {
                try {
                    // First load from localStorage for immediate availability
                    const storedPromos = localStorage.getItem('ledger_promos');
                    if (storedPromos) {
                        const localPromos = JSON.parse(storedPromos);
                        setPromoCodes(localPromos);
                    }

                    // Then sync from Firebase to get the latest promo codes
                    try {
                        const { getPromoCodes } = await import('./services/firebaseService');
                        const firebasePromos = await getPromoCodes();
                        if (firebasePromos && firebasePromos.length > 0) {
                            // De-duplicate promo codes by code (use Map to keep unique codes)
                            const uniquePromos = Array.from(
                                new Map(firebasePromos.map(p => [p.code, p])).values()
                            );
                            setPromoCodes(uniquePromos);
                            localStorage.setItem('ledger_promos', JSON.stringify(uniquePromos));
                            console.log('[PromoSync] Loaded', uniquePromos.length, 'promo codes from Firebase');
                        }
                    } catch (firebaseError) {
                        console.warn('[PromoSync] Could not sync from Firebase, using local cache:', firebaseError);
                    }
                } catch (error) {
                    console.error('Error loading promo codes:', error);
                }
            };
            loadPromoCodes();

            // Listen for promo code changes
            const handleStorageChange = (e: StorageEvent) => {
                if (e.key === 'ledger_promos') {
                    loadPromoCodes();
                }
            };
            window.addEventListener('storage', handleStorageChange);

            // Load Ads
            const storedAds = localStorage.getItem('ledger_ads');
            if (storedAds) {
                setAds(JSON.parse(storedAds));
            }

            // Load Announcements
            const storedAnnouncements = localStorage.getItem('luca_announcements');
            if (storedAnnouncements) {
                setAnnouncements(JSON.parse(storedAnnouncements));
            }

            // Initialize LUCA's Quest System
            const questUnsubscribe = lucasQuestService.subscribe((quest) => {
                if (quest && quest.status === 'pending') {
                    setActiveQuest(quest);
                    showToast('info', '🎯 New Quest Available! Check your notification.');
                }
            });

            // Start tracking current session
            if (currentSessionId) {
                lucasQuestService.startTracking(currentSessionId);
            }

            // Initialize Gamification System
            try {
                // Update streak on app load
                const streakResult = updateStreak();
                if (streakResult.isNewDay && streakResult.streakExtended) {
                    setTimeout(() => {
                        showToast('success', `🔥 ${streakResult.currentStreak} day streak! Keep it up!`);
                    }, 2000);
                } else if (streakResult.streakLost && streakResult.previousStreak > 0) {
                    setTimeout(() => {
                        showToast('info', `😢 Streak lost! Start a new one today!`);
                    }, 2000);
                }

                // Track new session
                trackSession();

                // Check for achievements and milestones
                const newAchievements = checkAchievements();
                if (newAchievements.length > 0) {
                    setTimeout(() => setAchievementUnlocks(newAchievements), 3000);
                }

                checkMilestones();

                // Gamification system initialized silently
            } catch (gamErr) {
                console.error('[Gamification] Init error:', gamErr);
            }

            // Load Sessions using sessionManager
            const sessionUnsubscribe = sessionManager.subscribe((sessions) => {
                setAllSessions(sessions);
            });

            // Cleanup function for storage listener and session manager
            const cleanup = () => {
                window.removeEventListener('storage', handleStorageChange);
                sessionUnsubscribe();
                questUnsubscribe();
            };

            // Load User - PERSISTENT LOGIN FIX
            const storedUser = localStorage.getItem('ledger_current_user');
            if (storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);

                    // Refresh User Data from users array (get latest premium status)
                    const allUsers = JSON.parse(localStorage.getItem('ledger_users') || '[]');
                    const freshUser = allUsers.find((u: User) => u.id === parsedUser.id);

                    if (freshUser) {
                        console.log('Found fresh user data:', freshUser);

                        const now = Date.now();

                        // Initialize lastResetTime if not set
                        if (!freshUser.lastResetTime) {
                            freshUser.lastResetTime = now;
                        }

                        // Initialize lastTokenRefresh if not set
                        if (!freshUser.lastTokenRefresh) {
                            freshUser.lastTokenRefresh = now;
                        }

                        // Ensure dailyTokenUsage is initialized (legacy, kept for compatibility)
                        if (typeof freshUser.dailyTokenUsage !== 'number') {
                            freshUser.dailyTokenUsage = 0;
                        }

                        // Initialize tokenBalance if not set (NEW TOKEN SYSTEM)
                        if (typeof freshUser.tokenBalance !== 'number') {
                            const tierTokenLimit = freshUser.isMax ? 50000 : freshUser.isPremium ? 15000 : 2000;
                            freshUser.tokenBalance = tierTokenLimit;
                        }

                        // Ensure dailyAttachmentCount is initialized
                        if (typeof freshUser.dailyAttachmentCount !== 'number') {
                            freshUser.dailyAttachmentCount = 0;
                        }

                        // Cleanup expired promo effects
                        const cleanedUser = cleanupExpiredEffects(freshUser);
                        if (cleanedUser) {
                            console.log('Cleaned up expired promo effects');
                            Object.assign(freshUser, cleanedUser);

                            // Update in storage and Firebase
                            const updatedAllUsers = allUsers.map((u: User) => u.id === freshUser.id ? freshUser : u);
                            localStorage.setItem('ledger_users', JSON.stringify(updatedAllUsers));
                            localStorage.setItem('ledger_current_user', JSON.stringify(freshUser));

                            if (firebase.db) {
                                import('firebase/firestore').then(({ doc, updateDoc }) => {
                                    const userRef = doc(firebase.db!, 'users', freshUser.id);
                                    updateDoc(userRef, {
                                        activePromoEffects: freshUser.activePromoEffects || null
                                    }).catch(err => console.error('Failed to update promo effects in Firebase:', err));
                                });
                            }
                        }

                        // ==================== TOKEN SYSTEM v3.2 ====================
                        // REMOVED: Daily token reset (users now rely on hourly replenishment only)
                        // Tokens are no longer reset to tier limit after 24 hours
                        // Users must manage tokens through hourly replenishment or purchasing more

                        // Save current state (no daily reset)
                        const updatedAllUsers = allUsers.map((u: User) => u.id === freshUser.id ? freshUser : u);
                        localStorage.setItem('ledger_users', JSON.stringify(updatedAllUsers));
                        localStorage.setItem('ledger_current_user', JSON.stringify(freshUser));

                        // Update Firebase with current values to ensure sync
                        if (firebase.db) {
                            import('firebase/firestore').then(({ doc, updateDoc }) => {
                                const userRef = doc(firebase.db!, 'users', freshUser.id);
                                updateDoc(userRef, {
                                    dailyTokenUsage: freshUser.dailyTokenUsage || 0,
                                    dailyMessageCount: freshUser.dailyMessageCount || 0,
                                    dailyAttachmentCount: freshUser.dailyAttachmentCount || 0,
                                    lastResetTime: freshUser.lastResetTime,
                                    lastTokenRefresh: freshUser.lastTokenRefresh,
                                    tokenBalance: freshUser.tokenBalance,
                                    // Sync Gamification Stats to Firebase
                                    totalXP: freshUser.totalXP || 0,
                                    level: freshUser.level || 1,
                                    streak: freshUser.streak || 0
                                }).catch(err => console.error('Failed to sync user data to Firebase:', err));
                            });
                        }

                        // Sync with Gamification Stats (Source of Truth for XP/Level)
                        try {
                            const gamStats = getGamificationSummary();
                            if (gamStats) {
                                console.log('[App] Syncing user with gamification stats:', gamStats);
                                freshUser.totalXP = gamStats.xp.total;
                                freshUser.level = gamStats.level.level;
                                // Also sync streak if needed
                                freshUser.streak = gamStats.streak.current;
                            }
                        } catch (e) {
                            console.error('[App] Failed to sync gamification stats:', e);
                        }

                        initializeGamification(freshUser.id);
                        setUser(freshUser);
                        if (freshUser.role === 'admin') setShowAdminPanel(true);
                    } else {
                        // User not found in users array, keep current stored user

                        // Sync with Gamification Stats even for stored user
                        try {
                            const gamStats = getGamificationSummary();
                            if (gamStats) {
                                console.log('[App] Syncing stored user with gamification stats:', gamStats);
                                parsedUser.totalXP = gamStats.xp.total;
                                parsedUser.level = gamStats.level.level;
                                parsedUser.streak = gamStats.streak.current;
                            }
                        } catch (e) {
                            console.error('[App] Failed to sync gamification stats:', e);
                        }

                        initializeGamification(parsedUser.id);
                        setUser(parsedUser);
                        if (parsedUser.role === 'admin') setShowAdminPanel(true);
                    }
                } catch (e) {
                    console.error('Error loading user:', e);
                    // Don't remove user on error - keep them logged in
                    try {
                        const parsedUser = JSON.parse(storedUser);
                        initializeGamification(parsedUser.id);
                        setUser(parsedUser);
                        if (parsedUser.role === 'admin') setShowAdminPanel(true);
                    } catch (parseError) {
                        console.error('Failed to parse stored user:', parseError);
                        localStorage.removeItem('ledger_current_user');
                    }
                }
            }

            if (window.innerWidth < 768) {
                setIsSidebarOpen(false);
            }

            return cleanup;
        } catch (error) {
            console.error('Error in initialization effect:', error);
        }
    }, []);

    // Show welcome greeting on login/refresh (but NOT during onboarding flow)
    useEffect(() => {
        if (user && !user.isAnonymous) {
            // Don't show greeting if onboarding is active or hasn't been completed
            const hasCompletedOnboarding = localStorage.getItem('luca_onboarding_completed') === 'true';
            if (!hasCompletedOnboarding) return;
            if (isOnboardingOpen) return;
            if (isGuidedTourOpen) return;

            const hasSeenGreeting = sessionStorage.getItem(`luca_greeting_${user.id}`);
            if (!hasSeenGreeting) {
                // Longer delay to avoid overlapping with other UI
                const timer = setTimeout(() => {
                    // Double-check no other modals are active
                    if (!isOnboardingOpen && !isGuidedTourOpen) {
                        setShowWelcomeGreeting(true);
                    }
                }, 1500);
                return () => clearTimeout(timer);
            }
        }
    }, [user, isOnboardingOpen, isGuidedTourOpen]);

    // Load active events
    useEffect(() => {
        if (user && !user.isAnonymous) {
            import('./services/eventsService').then(({ loadActiveEvents }) => {
                loadActiveEvents().then(events => {
                    setActiveEvents(events);
                }).catch(e => console.warn('[Events] Failed to load:', e));
            });
        }
    }, [user]);

    // Load Feature Access Config
    useEffect(() => {
        import('./services/featureAccessService').then(({ loadFeatureAccessConfig }) => {
            loadFeatureAccessConfig().then(config => {
                setFeatureAccessConfig(config);
                console.log('[App] Feature access config loaded');
            }).catch(e => console.warn('[FeatureAccess] Failed to load:', e));
        });
    }, []);

    // Token Refresh Handler - NEW: Uses tokenBalance system
    const handleTokenRefresh = useCallback(async (newTokenBalance: number) => {
        if (!user) return;

        const now = Date.now();
        const updatedUser = {
            ...user,
            tokenBalance: newTokenBalance,
            lastTokenRefresh: now
        };

        setUser(updatedUser);
        localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));

        // Update local storage users array
        const allUsers = JSON.parse(localStorage.getItem('ledger_users') || '[]');
        const dbUpdate = allUsers.map((u: User) => u.id === user.id ? updatedUser : u);
        localStorage.setItem('ledger_users', JSON.stringify(dbUpdate));

        // Update Firebase if available
        if (firebase.isInitialized && firebase.db) {
            try {
                const { doc, updateDoc } = await import('firebase/firestore');
                const userRef = doc(firebase.db, 'users', user.id);
                await updateDoc(userRef, {
                    tokenBalance: newTokenBalance,
                    lastTokenRefresh: now
                });
            } catch (err) {
                console.error('Failed to update token refresh in Firebase:', err);
            }
        }

        const refreshRate = user.isPremium ? (config?.premiumRefreshRate || 7500) : (config?.standardRefreshRate || 4500);
        console.log(`✅ Tokens refreshed: +${refreshRate} tokens added`);
    }, [user, firebase, config]);

    // This is now handled in the user loading section above - removed to prevent double initialization

    // Usage Notifications - Show at 50%, 75%, 100%
    useEffect(() => {
        if (!user) return;

        const isMaxTier = user.isMax || user.accountTier === 'max';
        const isPremium = user.isPremium && !isMaxTier;
        const tokenLimit = isMaxTier
            ? (config?.maxTokenLimit || 50000)
            : isPremium
                ? (config?.premiumTokenLimit || 15000)
                : (config?.freeTokenLimit || 2000);

        // FIX: Ensure we're using actual usage, not balance
        // If dailyTokenUsage is greater than limit, it's likely the balance, not usage
        const currentUsage = (user.dailyTokenUsage || 0) > tokenLimit * 2 ? 0 : (user.dailyTokenUsage || 0);
        const usagePercent = tokenLimit > 0 ? (currentUsage / tokenLimit) * 100 : 0;

        console.log('[TokenAlert] Current Usage:', currentUsage, 'Limit:', tokenLimit, 'Percent:', usagePercent.toFixed(2) + '%');

        const showUsageAlert = (threshold: number, type: 'info' | 'warning' | 'critical') => {
            // Only show alert if we actually reached the threshold
            if (!usageNotifications.has(threshold) && usagePercent >= threshold) {
                console.log('[TokenAlert] Showing alert for threshold:', threshold, 'type:', type);
                setUsageAlertModal({ isOpen: true, type, usagePercent });
                setUsageNotifications(prev => {
                    const newSet = new Set(prev).add(threshold);
                    // Persist to sessionStorage
                    try {
                        sessionStorage.setItem('luca_usage_notifications', JSON.stringify(Array.from(newSet)));
                    } catch (e) {
                        console.error('[App] Failed to save usage notifications:', e);
                    }
                    return newSet;
                });
            }
        };

        // Only show alerts if we're actually at or above the threshold
        if (usagePercent >= 100) {
            showUsageAlert(100, 'critical');
        } else if (usagePercent >= 75) {
            showUsageAlert(75, 'warning');
        } else if (usagePercent >= 50) {
            showUsageAlert(50, 'info');
        }

        // Reset notifications when usage drops below 50% (e.g., after daily reset)
        if (usagePercent < 50 && usageNotifications.size > 0) {
            setUsageNotifications(new Set());
            try {
                sessionStorage.removeItem('luca_usage_notifications');
            } catch (e) {
                console.error('[App] Failed to clear usage notifications:', e);
            }
        }
    }, [user?.dailyTokenUsage, user?.isPremium, user?.isMax, user?.accountTier, config?.premiumTokenLimit, config?.freeTokenLimit, config?.maxTokenLimit, usageNotifications]);

    // Filter Sessions when User or AllSessions changes
    useEffect(() => {
        let filtered: ChatSession[] = [];
        if (user) {
            if (user.isAnonymous) {
                // Guests: Show only current session to avoid clutter
                const currentSession = allSessions.find(s => s.id === currentSessionId);
                filtered = currentSession ? [currentSession] : [];
            } else {
                // Logged-in users: Show all their sessions
                filtered = allSessions.filter(s => s.userId === user.id);
            }
        } else {
            // No user (guest): Check for persistent guest session first
            const storedGuestSessionId = sessionManager.getStoredGuestSessionId();
            if (storedGuestSessionId) {
                // Try to find the stored guest session
                const guestSession = allSessions.find(s => s.id === storedGuestSessionId && !s.userId);
                if (guestSession) {
                    filtered = [guestSession];
                } else {
                    // Stored session not found, show all guest sessions
                    filtered = allSessions.filter(s => !s.userId);
                }
            } else {
                // No stored guest session, show all guest sessions
                filtered = allSessions.filter(s => !s.userId);
            }
        }

        setDisplayedSessions(filtered);

        // If current session is not in the filtered list (e.g. after logout), reset to new chat or first available
        if (currentSessionId && !filtered.find(s => s.id === currentSessionId)) {
            if (filtered.length > 0) {
                const recent = filtered.sort((a, b) => (Number(b.lastModified) || 0) - (Number(a.lastModified) || 0))[0];
                setCurrentSessionId(recent.id);
                setMessages(recent.messages);
            } else {
                // For guests, use persistent session; for users, create new
                if (!user) {
                    const guestSession = sessionManager.getOrCreateGuestSession();
                    setCurrentSessionId(guestSession.id);
                    setMessages(guestSession.messages);
                } else {
                    handleNewChat(false);
                }
            }
        } else if (!currentSessionId && filtered.length > 0) {
            const recent = filtered.sort((a, b) => (Number(b.lastModified) || 0) - (Number(a.lastModified) || 0))[0];
            setCurrentSessionId(recent.id);
            setMessages(recent.messages);
        } else if (!currentSessionId && filtered.length === 0) {
            // For guests, use persistent session; for users, create new
            if (!user) {
                const guestSession = sessionManager.getOrCreateGuestSession();
                setCurrentSessionId(guestSession.id);
                setMessages(guestSession.messages);
            } else {
                handleNewChat(false);
            }
        }

    }, [user, allSessions]);

    // Firebase Realtime Sync
    useEffect(() => {
        if (!firebase.isInitialized) return;

        let unsubscribeUser: (() => void) | undefined;
        let unsubscribeSessions: (() => void) | undefined;
        let unsubscribeConfig: (() => void) | undefined;

        if (user) {
            // Start syncing user data
            unsubscribeUser = startUserSync(user.id, (updatedUser) => {
                setUser(updatedUser);
                localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
            });

            // Debounced session sync handler to prevent too many updates
            let lastSessionSync = 0;
            const SESSION_SYNC_DEBOUNCE = 2000; // Only process session sync every 2 seconds

            // Start syncing sessions
            unsubscribeSessions = startSessionsSync(user.id, (firebaseSessions) => {
                // Debounce session syncs to prevent excessive updates
                const now = Date.now();
                if (now - lastSessionSync < SESSION_SYNC_DEBOUNCE) {
                    return; // Skip this update, too soon
                }
                lastSessionSync = now;

                // Merge Firebase sessions with local data to preserve pinnedMessages & generatedMaterials
                const localSessions = sessionManager.getAllSessions();

                const mergedSessions = firebaseSessions.map(fbSession => {
                    const localSession = localSessions.find(ls => ls.id === fbSession.id);

                    // If local session has more recent pinnedMessages or generatedMaterials, preserve them
                    if (localSession) {
                        return {
                            ...fbSession,
                            // Preserve local pinnedMessages if Firebase doesn't have them
                            pinnedMessages: ((fbSession.pinnedMessages?.length ?? 0) > 0
                                ? fbSession.pinnedMessages
                                : localSession.pinnedMessages) || [],
                            // Preserve local generatedMaterials if Firebase doesn't have them
                            generatedMaterials: ((fbSession.generatedMaterials?.length ?? 0) > 0
                                ? fbSession.generatedMaterials
                                : localSession.generatedMaterials) || [],
                            tags: fbSession.tags || localSession.tags || []
                        };
                    }
                    return {
                        ...fbSession,
                        pinnedMessages: fbSession.pinnedMessages || [],
                        generatedMaterials: fbSession.generatedMaterials || [],
                        tags: fbSession.tags || []
                    };
                });

                setAllSessions(mergedSessions);

                // Hand off to sessionManager to handle storage and pruning
                // Do NOT save directly to localStorage here as it bypasses size checks
                sessionManager.importSessions(mergedSessions, false);

                // Trigger a save (which will check size and prune if needed)
                // This ensures we only store the "latest 5" locally even if we synced 100 from Firebase
                setTimeout(() => sessionManager.forceSave(), 0);
            });

            // Load generated materials from Firebase
            getUserGeneratedMaterials(user.id).then(materials => {
                if (materials.length > 0) {
                    setGeneratedMaterials(materials);
                    localStorage.setItem('ledger_generated_materials', JSON.stringify(materials));
                    console.log(`📚 Loaded ${materials.length} generated materials from Firebase`);
                }
            }).catch(err => console.error('Failed to load materials:', err));
        }

        return () => {
            stopAllSync();
            if (unsubscribeUser) unsubscribeUser();
            if (unsubscribeSessions) unsubscribeSessions();
        };
    }, [firebase.isInitialized, user?.id]);

    // Online status tracking with visibility change and before unload
    useEffect(() => {
        if (!user?.id || user.isAnonymous) return;

        // Set user online when app is visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                updateOnlineStatus(user.id, true).catch(console.error);
            } else {
                updateOnlineStatus(user.id, false).catch(console.error);
            }
        };

        // Set user offline when closing/leaving the page
        const handleBeforeUnload = () => {
            // Use navigator.sendBeacon for reliable offline status on page close
            const data = JSON.stringify({ userId: user.id, isOnline: false });
            navigator.sendBeacon?.('/api/offline-status', data);
            // Also call the regular update (may not complete)
            updateOnlineStatus(user.id, false).catch(() => { });
        };

        // Initial online status
        updateOnlineStatus(user.id, true).catch(console.error);

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Set offline when component unmounts
            updateOnlineStatus(user.id, false).catch(() => { });
        };
    }, [user?.id, user?.isAnonymous]);

    // Save Density Preference
    const handleSetDensity = (density: MessageDensity) => {
        setMessageDensity(density);
        localStorage.setItem('ledger_ui_density', density);
    };

    // Toggle Dark Mode
    const toggleDarkMode = () => {
        const newMode = !isDarkMode;
        setIsDarkMode(newMode);
        localStorage.setItem('ledger_dark_mode', newMode.toString());
    };

    // Handle Login/Logout
    const handleLogin = async (loggedInUser: User) => {
        console.log('[App] 🔐 handleLogin called for user:', loggedInUser.username);

        // PREVENT MULTIPLE ACCOUNT SIGN-IN: If a different user is already logged in, log them out first
        if (user && user.id !== loggedInUser.id) {
            console.log(`[App] ⚠️ Different user already logged in (${user.username}). Logging out first.`);
            // Clean up old user's online status
            if (user.id) {
                updateOnlineStatus(user.id, false).catch(console.error);
            }
            // Clear old user state
            setUser(null);
            localStorage.removeItem('ledger_current_user');
            setShowAdminPanel(false);
            // Reset smart recommendation tracking for new user
            smartRecommendationService.resetSession();
            // Clear session state so old user's messages don't leak
            setMessages([]);
        }

        // CRITICAL FIX: Ensure loading screen is dismissed on login
        setIsInitialLoading(false);
        sessionStorage.setItem('luca_initial_loading_dismissed', 'true');

        // Check and replenish tokens based on time elapsed (even if user was offline)
        const replenishedUser = await checkAndReplenishOnLogin(loggedInUser, config);

        // Migrate guest session to user account if it has messages
        const storedGuestSessionId = sessionManager.getStoredGuestSessionId();
        if (storedGuestSessionId) {
            const guestSession = sessionManager.getSession(storedGuestSessionId);
            if (guestSession && guestSession.messages.length > 0) {
                // Migrate the guest session to the new user
                const migratedSession = sessionManager.migrateGuestSessionToUser(replenishedUser.id);
                if (migratedSession) {
                    console.log('[App] Migrated guest session with', migratedSession.messages.length, 'messages to user:', replenishedUser.id);
                    // Keep the current session as the migrated one
                    setCurrentSessionId(migratedSession.id);
                    setMessages(migratedSession.messages);
                }
            } else {
                // Guest session is empty, just clear the guest session ID
                sessionManager.clearGuestSessionId();
            }
        }

        setUser(replenishedUser);
        localStorage.setItem('ledger_current_user', JSON.stringify(replenishedUser));
        if (replenishedUser.role === 'admin') {
            setShowAdminPanel(true);
        }

        // Update online status for social features
        updateOnlineStatus(replenishedUser.id, true).catch(console.error);

        console.log('[App] ✅ Login complete, user state updated');
        setTimeout(() => inputRef.current?.focus(), 100);

        // Trigger guided tour for first-time users (after login/signup)
        // But only if onboarding is already completed (otherwise onboarding will trigger it)
        const hasSeenTour = localStorage.getItem('luca_tour_completed');
        const hasCompletedOnboarding = localStorage.getItem('luca_onboarding_completed') === 'true';
        if (!hasSeenTour && hasCompletedOnboarding) {
            // Small delay to let the UI settle before starting tour
            setTimeout(() => setIsGuidedTourOpen(true), 800);
        }
    };

    const handleLogout = () => {
        // Update online status before clearing user
        if (user?.id) {
            updateOnlineStatus(user.id, false).catch(console.error);
        }

        setUser(null);
        localStorage.removeItem('ledger_current_user');
        setShowAdminPanel(false);
        setIsProfileMenuOpen(false);
        setGuestMessageCount(0);

        // Clear any existing guest session ID so they get a fresh start
        sessionManager.clearGuestSessionId();

        // Create a new guest session
        const guestSession = sessionManager.getOrCreateGuestSession();
        setCurrentSessionId(guestSession.id);
        setMessages([]);
    };

    // Smart scroll - only auto-scroll if user is near bottom
    const isNearBottom = useCallback(() => {
        if (!chatContainerRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        // Consider "near bottom" if within 200px of bottom (generous threshold)
        return scrollHeight - scrollTop - clientHeight < 200;
    }, []);

    // Track if user manually scrolled away during streaming
    const userScrolledAwayRef = useRef(false);

    const scrollToBottom = useCallback((force = false) => {
        if (userScrolledAwayRef.current && !force) return;
        if (force || isNearBottom()) {
            // Use instant scroll during streaming for smoothness, smooth for final messages
            const isStreaming = messages.some(m => m.isStreaming);
            if (isStreaming) {
                // Use rAF + scrollTop for jank-free streaming scroll
                requestAnimationFrame(() => {
                    if (chatContainerRef.current) {
                        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
                    }
                });
            } else {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [isNearBottom, messages]);

    // Auto-scroll on new messages, but respect user scroll position and autoScroll setting
    useEffect(() => {
        if (!appSettings.autoScroll) return;
        const isStreaming = messages.some(m => m.isStreaming);
        if (isStreaming) {
            // During streaming: scroll if user hasn't scrolled away
            if (!userScrolledAwayRef.current) {
                scrollToBottom(false);
            }
        } else {
            // Non-streaming: always scroll for new messages, reset scroll-away flag
            userScrolledAwayRef.current = false;
            scrollToBottom(true);
        }
    }, [messages, scrollToBottom, appSettings.autoScroll]);

    // Detect user scrolling away during streaming
    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container) return;
        const handleScroll = () => {
            const isStreaming = messages.some(m => m.isStreaming);
            if (isStreaming) {
                const { scrollTop, scrollHeight, clientHeight } = container;
                const distFromBottom = scrollHeight - scrollTop - clientHeight;
                // If user scrolled more than 300px from bottom during streaming, stop auto-scroll
                if (distFromBottom > 300) {
                    userScrolledAwayRef.current = true;
                } else if (distFromBottom < 100) {
                    userScrolledAwayRef.current = false;
                }
            }
        };
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, [messages]);

    // Auto-generate PDF when material generation completes
    useEffect(() => {
        if (!pendingMaterialPDF || messages.length === 0) return;

        // Check if the latest AI message is complete (not streaming)
        const latestMessage = messages[messages.length - 1];
        if (latestMessage && latestMessage.role === Role.MODEL && !latestMessage.isStreaming && !latestMessage.isError) {
            // Generate PDF from the AI response
            const generatePDF = async () => {
                try {
                    showToast('info', 'Creating PDF...');

                    const pdfBlob = await generateMaterialPDF({
                        title: pendingMaterialPDF.type === 'practice-problems' ? 'Practice Problems' : 'Study Guide',
                        content: latestMessage.content,
                        topic: pendingMaterialPDF.topic,
                        type: pendingMaterialPDF.type
                    });

                    downloadMaterialPDF(pdfBlob, pendingMaterialPDF.topic, pendingMaterialPDF.type);

                    showToast('success', `PDF downloaded successfully!`);
                    setPendingMaterialPDF(null);
                } catch (error) {
                    console.error('PDF generation error:', error);
                    showToast('error', 'Failed to generate PDF. You can still copy the content.');
                    setPendingMaterialPDF(null);
                }
            };

            generatePDF();
        }
    }, [messages, pendingMaterialPDF, showToast]);

    // Force scroll when loading starts (user sent message)
    useEffect(() => {
        if (isLoading && messages.length > 0) {
            scrollToBottom(true);
        }
    }, [isLoading, scrollToBottom]);

    useEffect(() => {
        inputRef.current?.focus();
    }, [currentSessionId]);

    // Debounced session saver to reduce localStorage writes - MUST BE DEFINED FIRST
    const debouncedSaveSession = useCallback(
        debounce((sessionData: ChatSession) => {
            saveChatSession(sessionData).catch(err => console.error("Failed to save session to Firebase:", err));
        }, 2000),
        []
    );

    // Handle Redeem Promo Code - returns result for ProfileDropdown component
    const handleRedeemCode = useCallback(async (codeInput: string): Promise<{ success: boolean; message?: string; error?: string }> => {
        if (!user || !codeInput.trim()) {
            return { success: false, error: 'Please enter a code' };
        }

        try {
            const code = codeInput.trim().toUpperCase();

            // Import services
            const { redeemPromoCode, validatePromoCode } = await import('./services/promoCodeService');
            const { savePromoCodes, getPromoCodes } = await import('./services/firebaseService');

            // First check local state
            let promo = promoCodes.find(p => p.code.toUpperCase() === code);
            let currentPromoCodes = promoCodes;

            // If not found locally, fetch fresh from Firebase (for gift codes from other users)
            if (!promo) {
                console.log('[RedeemCode] Code not found locally, fetching from Firebase...');
                try {
                    const freshCodes = await getPromoCodes();
                    promo = freshCodes.find(p => p.code.toUpperCase() === code);
                    if (promo) {
                        // Update local state with fresh codes
                        setPromoCodes(freshCodes);
                        currentPromoCodes = freshCodes;
                        console.log('[RedeemCode] Found code in Firebase:', code);
                    }
                } catch (fetchError) {
                    console.warn('[RedeemCode] Failed to fetch from Firebase:', fetchError);
                }
            }

            if (!promo) {
                return { success: false, error: 'Invalid promo code' };
            }

            // Validate first
            const validation = validatePromoCode(promo, user.id);
            if (!validation.valid) {
                return { success: false, error: validation.reason || 'Invalid promo code' };
            }

            // Redeem the code
            const result = await redeemPromoCode(
                promo,
                user,
                currentPromoCodes,
                async (updatedCodes) => {
                    // Save updated promo codes to Firebase
                    await savePromoCodes(updatedCodes);
                    setPromoCodes(updatedCodes);
                }
            );

            if (result.success) {
                // Update local user state
                if (result.updatedUser) {
                    setUser(result.updatedUser);
                    // Persist to localStorage with correct key
                    localStorage.setItem('ledger_current_user', JSON.stringify(result.updatedUser));

                    // Also update in ledger_users array
                    const storedUsers = localStorage.getItem('ledger_users');
                    if (storedUsers) {
                        const users = JSON.parse(storedUsers);
                        const updatedUsers = users.map((u: User) =>
                            u.id === result.updatedUser!.id ? result.updatedUser : u
                        );
                        localStorage.setItem('ledger_users', JSON.stringify(updatedUsers));
                    }

                    // Sync to Firebase
                    if (firebase.updateUser) {
                        await firebase.updateUser(result.updatedUser.id, result.updatedUser);
                    }
                }

                return { success: true, message: result.message || '✅ Code redeemed successfully!' };
            } else {
                return { success: false, error: result.error || 'Failed to redeem code' };
            }
        } catch (error) {
            console.error('Error redeeming code:', error);
            return { success: false, error: 'An error occurred. Please try again.' };
        }
    }, [user, promoCodes, firebase]);

    const handleNewChat = useCallback((shouldUpdate = true) => {
        let newSession: ChatSession;

        // For guests, check if we should reuse existing session or create truly new one
        if (!user) {
            // If the current guest session is empty, reuse it instead of creating another
            const storedGuestSessionId = sessionManager.getStoredGuestSessionId();
            if (storedGuestSessionId) {
                const existingSession = sessionManager.getSession(storedGuestSessionId);
                if (existingSession && existingSession.messages.length === 0) {
                    // Reuse the existing empty guest session
                    newSession = existingSession;
                    console.log('[App] Reusing existing empty guest session:', existingSession.id);
                } else {
                    // Guest has messages, create a fresh guest session
                    newSession = sessionManager.getOrCreateGuestSession();
                }
            } else {
                // No stored guest session, create one
                newSession = sessionManager.getOrCreateGuestSession();
            }
        } else {
            // For logged-in users, always create a new session
            newSession = sessionManager.createSession(user.id);
        }

        setCurrentSessionId(newSession.id);
        setMessages([]);
        setIsMobileMenuOpen(false);
        setActivePdf(null);
        if (shouldUpdate) inputRef.current?.focus();
    }, [user?.id]);

    const handleSelectSession = useCallback((id: string) => {
        // Use sessionManager's Map for O(1) lookup instead of array search
        const session = sessionManager.getSession(id);
        if (session) {
            setCurrentSessionId(id);
            setMessages(session.messages);
            sessionManager.setCurrentSession(id);
            setIsMobileMenuOpen(false);
            setActivePdf(null);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, []);

    const handleDeleteSession = useCallback(async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();

        // Use sessionManager to delete
        sessionManager.deleteSession(id);

        // Delete from Firebase (queued if offline)
        if (connectionManager.isOnline() && firebase.db) {
            try {
                const { doc, deleteDoc } = await import('firebase/firestore');
                const sessionRef = doc(firebase.db, 'chatSessions', id);
                await deleteDoc(sessionRef);
                console.log(`✅ Session ${id} deleted from Firebase`);
            } catch (error) {
                console.error('Failed to delete session from Firebase:', error);
            }
        } else {
            connectionManager.queueRequest({
                type: 'session',
                data: { id, _deleted: true },
                priority: 2
            });
        }

        if (currentSessionId === id) {
            setCurrentSessionId(null);
        }
    }, [currentSessionId, firebase.db]);

    const handleRenameSession = useCallback((id: string, newTitle: string) => {
        // Use sessionManager to rename
        sessionManager.renameSession(id, newTitle);

        // Update Firebase if online
        const session = sessionManager.getSession(id);
        if (session) {
            if (connectionManager.isOnline() && firebase.db) {
                debouncedSaveSession(session);
            } else {
                connectionManager.queueRequest({
                    type: 'session',
                    data: session,
                    priority: 3
                });
            }
        }
    }, [firebase.db, debouncedSaveSession]);

    const handlePinSession = useCallback((id: string, isPinned: boolean) => {
        // Use sessionManager to pin/unpin
        sessionManager.pinSession(id, isPinned);

        // Update Firebase if online
        const session = sessionManager.getSession(id);
        if (session) {
            if (connectionManager.isOnline() && firebase.db) {
                debouncedSaveSession(session);
            } else {
                connectionManager.queueRequest({
                    type: 'session',
                    data: session,
                    priority: 3
                });
            }
        }
    }, [firebase.db, debouncedSaveSession]);

    const updateCurrentSession = useCallback((newMessages: Message[]) => {
        setMessages(newMessages);
        if (!currentSessionId) return;

        // Use sessionManager to update session
        const existingSession = sessionManager.getSession(currentSessionId);
        let title = existingSession?.title || 'New Chat';

        if ((!existingSession || existingSession.messages.length === 0) && newMessages.length > 0) {
            title = newMessages[0].content.slice(0, 30) + '...';
        }

        const updatedSession: ChatSession = {
            id: currentSessionId,
            userId: user?.id || '',
            title: title,
            messages: newMessages,
            createdAt: existingSession?.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            lastModified: Date.now(),
            pinned: existingSession?.pinned || false,
            // Preserve important session data
            pinnedMessages: existingSession?.pinnedMessages || [],
            generatedMaterials: existingSession?.generatedMaterials || [],
            tags: existingSession?.tags || [],
            lastSyncedAt: existingSession?.lastSyncedAt,
            syncStatus: existingSession?.syncStatus,
            version: existingSession?.version
        };

        // For logged-in users: save to localStorage AND sync to Firebase
        if (user && !user.isAnonymous) {
            // Update using sessionManager (handles localStorage, backup, and Firebase sync via connection manager)
            sessionManager.updateSession(currentSessionId, updatedSession);

            // Sync to Firebase if online, otherwise queue
            if (connectionManager.isOnline()) {
                debouncedSaveSession(updatedSession);
            } else {
                connectionManager.queueRequest({
                    type: 'session',
                    data: updatedSession,
                    priority: 4
                });
            }
        } else {
            // For guests: save to localStorage only (no Firebase sync)
            // This allows the session to persist across page reloads
            sessionManager.updateSession(currentSessionId, updatedSession);
            console.log('[App] Guest session updated locally:', currentSessionId);
        }
    }, [currentSessionId, user?.id, debouncedSaveSession]);

    const handleOpenQuiz = useCallback((quiz: any) => {
        console.log('[App] Opening interactive quiz:', quiz.title);
        setQuizData(quiz);
        setIsQuizActive(true);
    }, []);

    const processFiles = useCallback((files: File[]) => {
        // Guest restriction - Enforce same as handleFileSelect
        if (!user || user.isAnonymous) {
            showToast('warning', 'Please create an account to upload files');
            setShowGuestLimitModal(true);
            return;
        }

        const validFiles = files.filter(f => f.type.startsWith('image/') || f.type === 'application/pdf' ||
            f.name.toLowerCase().endsWith('.ppt') || f.name.toLowerCase().endsWith('.pptx'));

        if (files.length > 0 && validFiles.length === 0) {
            alert('Only images, PDFs, and PowerPoint files are supported.');
            return;
        }

        // Limit to 1 file at a time to prevent memory issues
        if (validFiles.length > 1) {
            alert('Please attach one file at a time.');
            return;
        }

        if (validFiles.length === 0) return;

        const file = validFiles[0];

        // Track mastery for drag and drop too
        if (!localStorage.getItem('luca_mastery_document_uploaded')) {
            localStorage.setItem('luca_mastery_document_uploaded', 'true');
        }

        // Check if it's a PowerPoint file
        const isPPT = file.type === 'application/vnd.ms-powerpoint' ||
            file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
            file.name.toLowerCase().endsWith('.ppt') ||
            file.name.toLowerCase().endsWith('.pptx');

        // Estimate cost (Using same formula as handleFileSelect)
        // Old: file.size / 3 (approx 350k tokens/MB)
        // New: file.size / 1000 (approx 1k tokens/MB)
        // PPT files get 50% extra token cost
        let estimatedCost = Math.ceil(file.size / 1000);
        if (isPPT) {
            estimatedCost = Math.ceil(estimatedCost * 1.5); // 50% extra for PowerPoint
            showToast('info', '📊 PowerPoint files cost 50% more tokens for analysis');
        }

        setPendingFile({ file, estimatedCost });
        setShowTokenCostModal(true);

    }, [user, showToast]);

    const [guestTokenUsage, setGuestTokenUsage] = useState(0);

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        const items = e.clipboardData.items;
        const files: File[] = [];

        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                const file = items[i].getAsFile();
                if (file) files.push(file);
            }
        }

        if (files.length > 0) {
            e.preventDefault();
            processFiles(files);
        }
    }, [processFiles]);

    const processMessage = async (text: string, currentAttachments: Attachment[], isRetry: boolean = false, estimatedTokenCost?: number) => {
        // Strict Guest Limits: 1 Message OR 1000 Tokens
        if (!user && !isRetry) {
            if (guestMessageCount >= 1 || guestTokenUsage >= 1000) {
                setAuthMode('register');
                setIsAuthModalOpen(true);
                return;
            }
        }

        if (user && !user.isPremium) {
            // Check if user has unlimited tokens promo effect
            const hasUnlimited = hasUnlimitedTokens(user);

            if (!hasUnlimited) {
                // REMOVED: Attachment limit for standard users - they now pay tokens instead
                // Attachments are still charged via the token cost system in advancedTokenCostSystem.ts
                // NEW TOKEN SYSTEM: Check tokenBalance instead of dailyTokenUsage
                const tierTokenLimit = (config?.freeTokenLimit || 2000);
                const currentBalance = user.tokenBalance ?? tierTokenLimit;
                if (currentBalance <= 0) {
                    setIsUpgradeModalOpen(true);
                    return;
                }
            }
        } else if (user && user.isPremium) {
            // Check premium file limit (0 = unlimited)
            const premiumFileLimit = config?.premiumFileLimit || 0;
            if (premiumFileLimit > 0 && currentAttachments.length > 0 && ((user.dailyAttachmentCount || 0) + currentAttachments.length) > premiumFileLimit) {
                // Premium user exceeded their file limit (if not unlimited)
                alert(`You've reached your premium file limit of ${premiumFileLimit} files per day.`);
                return;
            }
        }

        // Abort previous generation if active
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        // Create new controller
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setIsPaused(false); // Reset pause state

        if (isLoading && !isRetry) {
            setIsLoading(false);
        }

        setIsLoading(true);

        if (!user && !isRetry) {
            setGuestMessageCount(prev => prev + 1);
        }

        let currentMessages = [...messages];

        // Create user message with attachments
        if (!isRetry) {
            const messageId = Date.now().toString();
            const userMessage: Message = {
                id: messageId,
                role: Role.USER,
                content: text,
                timestamp: new Date().toISOString(),
                attachments: currentAttachments.length > 0 ? currentAttachments : undefined
            };
            currentMessages = [...currentMessages, userMessage];
            updateCurrentSession(currentMessages);

            if (currentAttachments.length > 0 && currentSessionId) {
                trackAttachmentMessage(currentSessionId, messageId, currentAttachments.length);
            }

            // Track mastery
            if (!localStorage.getItem('luca_mastery_chat_sent')) {
                localStorage.setItem('luca_mastery_chat_sent', 'true');
            }
        }

        const apiHistory = currentMessages.filter(m => !m.isError);

        // Select model based on user type - use config models from admin dashboard
        // All models are validated to prevent 404s from deprecated/preview models
        let selectedModel: string;
        let allowEscalation = true;

        // Guests always use the configured free model
        if (!user || user.isAnonymous) {
            selectedModel = validateModel(config?.freeModel);
            allowEscalation = false;
        } else if (user?.isPremium && isPremiumModelEnabled) {
            selectedModel = validateModel(config?.premiumModel);
            allowEscalation = true;
        } else {
            // Regular user or premium with Pro toggle OFF
            selectedModel = validateModel(config?.freeModel);
            allowEscalation = !user?.isPremium; // Allow escalation for non-premium users
        }

        console.log(`[Model Selection] Using: ${selectedModel} (validated)`);

        try {
            const apiAttachments = currentAttachments
                .filter(att => att.mimeType && att.data)
                .map(att => ({
                    mimeType: att.mimeType!,
                    data: att.data!
                }));

            // Create placeholder message for stream
            const placeholderId = (Date.now() + 1).toString();
            const placeholderMsg: Message = {
                id: placeholderId,
                role: Role.MODEL,
                content: '',
                timestamp: new Date().toISOString(),
                isStreaming: true,
                persona: aiMode,
                depth: aiDepth
            };

            // Update UI with placeholder
            updateCurrentSession([...currentMessages, placeholderMsg]);

            const stream = sendMessageToGeminiStream(
                apiHistory,
                text,
                apiAttachments,
                selectedModel,
                user?.isPremium || false,
                allowEscalation,
                {
                    mode: aiMode,
                    depth: aiDepth,
                    autoBudget: autoBudgetEnabled,
                    budgetPerRequest: budgetPerRequest
                },
                userCountry,
                controller.signal
            );

            let fullText = '';
            let fullThought = '';
            let modelUsed = selectedModel;
            let tokensMultiplier = 1;
            let actualTokensCharged = 0; // Capture real tokens from centralized system

            // Throttle streaming UI updates — tighter interval for smoother text appearance
            let lastUIUpdate = 0;
            const UI_UPDATE_INTERVAL = 40; // Update UI every 40ms (~25fps) for smooth streaming

            for await (const chunk of stream) {
                // Improved Pause Logic: Wait while paused, but check for abort
                while (isPaused && !controller.signal.aborted) {
                    await new Promise(r => setTimeout(r, 100));
                }

                // Check if stream was aborted (stopped)
                if (controller.signal.aborted) {
                    console.log('Stream aborted by user');
                    break;
                }

                fullText = chunk.text;
                if (chunk.thought) fullThought = chunk.thought;
                if (chunk.modelUsed) modelUsed = chunk.modelUsed;
                if (chunk.tokensMultiplier) tokensMultiplier = chunk.tokensMultiplier;
                if (chunk.tokensCharged) actualTokensCharged = chunk.tokensCharged; // Capture actual tokens

                // Throttled UI update - only update every UI_UPDATE_INTERVAL ms
                // When streamResponses is disabled, skip intermediate UI updates so response appears all at once
                const now = Date.now();
                if (appSettings.streamResponses && now - lastUIUpdate >= UI_UPDATE_INTERVAL) {
                    lastUIUpdate = now;

                    // Update messages state directly for smooth UI, skip session save during streaming
                    setMessages([...currentMessages, {
                        ...placeholderMsg,
                        content: fullText,
                        thoughtProcess: fullThought,
                        modelUsed: modelUsed,
                        tokensCharged: actualTokensCharged,
                        isStreaming: true
                    }]);
                }
            }

            // Handle aborted stream
            if (controller.signal.aborted) {
                // Mark the message as stopped/partial
                const stoppedMessage: Message = {
                    ...placeholderMsg,
                    content: fullText || '*(Response stopped by user)*',
                    thoughtProcess: fullThought,
                    modelUsed: modelUsed,
                    tokensCharged: actualTokensCharged,
                    isStreaming: false
                };
                updateCurrentSession([...currentMessages, stoppedMessage]);
                setIsLoading(false);
                abortControllerRef.current = null;
                return; // Exit early
            }

            // Safety net: if stream finished but fullText is empty, ensure we show something
            if (!fullText || fullText.trim().length === 0) {
                console.warn('[App] Stream completed but fullText is empty. Showing fallback.');
                fullText = "⚠️ I wasn't able to generate a response. This can happen due to a temporary issue. Please try again.";
            }

            const response = { text: fullText, thought: fullThought, modelUsed, tokensMultiplier };

            // Log model usage for debugging
            if (response.modelUsed && response.tokensMultiplier) {
                console.log(`AI Response - Model: ${response.modelUsed}, Token Multiplier: ${response.tokensMultiplier}x`);
            }

            // Extract referenced attachments from AI response
            const referencedAttachments = extractReferencedAttachments(fullText, currentMessages);

            const finalContent = fullText;

            // Check for Quiz Data
            if (response.text.includes('<QUIZ>')) {
                const quizMatch = response.text.match(/<QUIZ>([\s\S]*?)<\/QUIZ>/);
                if (quizMatch) {
                    try {
                        let jsonStr = quizMatch[1];

                        // Remove markdown code blocks if present (```json ... ```)
                        jsonStr = jsonStr.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();

                        const data = JSON.parse(jsonStr);
                        console.log('[Quiz] Successfully parsed quiz from chat response:', data.title);

                        // Save as Generated Material
                        if (currentSessionId && user) {
                            const newMaterial: GeneratedMaterial = {
                                id: Date.now().toString(),
                                type: data.type || 'quiz',
                                title: data.title || 'Generated Quiz',
                                content: data, // Store the raw quiz data
                                createdAt: new Date().toISOString(),
                                tags: ['quiz', 'ai-generated'],
                                userId: user.id,
                                sessionId: currentSessionId
                            };

                            // Save to Firebase (1 argument only)
                            saveGeneratedMaterial(newMaterial);

                            // Update local session state to reflect new material
                            const currentSession = sessionManager.getSession(currentSessionId);
                            if (currentSession) {
                                const updatedMaterials = [...(currentSession.generatedMaterials || []), newMaterial];
                                sessionManager.updateSession(currentSessionId, {
                                    ...currentSession,
                                    generatedMaterials: updatedMaterials
                                });
                            }
                        }

                        // Auto-open quiz for better UX
                        setQuizData(data);
                        setIsQuizActive(true);
                    } catch (e) {
                        console.error("Failed to parse quiz from chat", e);
                        console.error("Quiz content:", quizMatch[1]);
                    }
                }
            }

            // ==================== ADMIN DASHBOARD TOKEN CONFIG SYSTEM ====================
            // Use admin-configured token costs for ALL calculations
            const tokenConfig = loadTokenCostConfig();
            console.log('[TokenCalculation] Using admin dashboard token config');
            console.log('[TokenCalculation] Config Snapshot:', {
                chatMessageBase: tokenConfig.baseCosts?.chatMessage,
                personaCosts: tokenConfig.personaCosts,
                depthCosts: tokenConfig.depthCosts,
                complexityMultipliers: tokenConfig.complexityMultipliers,
                modelMultipliers: tokenConfig.modelMultipliers
            });

            // ==================== ADVANCED COMPLEXITY ANALYSIS ====================
            // Use the super accurate complexity analyzer
            const complexityAnalysis = advancedAnalyzeComplexity(
                text,
                currentAttachments,
                messages.length
            );

            console.log('[ComplexityAnalysis] Advanced Analysis:', {
                level: complexityAnalysis.level,
                score: complexityAnalysis.score.toFixed(2),
                confidence: (complexityAnalysis.confidence * 100).toFixed(0) + '%',
                reasoning: complexityAnalysis.reasoning,
                requiresComputation: complexityAnalysis.requiresComputation,
                requiresMultiStep: complexityAnalysis.requiresMultiStep,
                domainSpecialty: complexityAnalysis.domainSpecialty,
                topFactors: complexityAnalysis.factors
                    .filter(f => f.detected && Math.abs(f.impact) > 0.5)
                    .map(f => `${f.name} (${f.impact > 0 ? '+' : ''}${f.impact.toFixed(1)})`)
            });

            // Calculate ACTUAL tokens using admin dashboard configuration
            const responseLength = response.text.length;
            const inputLength = text.length;

            // Map persona to admin config format
            const personaKey = aiMode === 'standard' ? 'standard' :
                aiMode === 'educator' ? 'tutor' : 'professional';

            // Map depth to admin config format
            const depthKey = aiDepth === 'concise' ? 'concise' :
                aiDepth === 'balanced' ? 'balanced' :
                    aiDepth === 'detailed' ? 'detailed' : 'comprehensive';

            // Use the advanced complexity level
            const complexityKey = complexityAnalysis.level;

            // Calculate using admin config
            const costResult = calculateTokenCost({
                operationType: 'chatMessage',
                inputLength,
                estimatedOutputLength: responseLength,
                complexity: complexityKey,
                messageContent: text,
                attachmentCount: currentAttachments.length
            }, tokenConfig);

            // Apply persona cost
            const personaCost = tokenConfig.personaCosts?.[personaKey] || 0;

            // Apply depth cost
            const depthCost = tokenConfig.depthCosts?.[depthKey] || 0;

            // Apply model multiplier
            const modelKey = selectedModel.includes('flash') ? 'flash' :
                selectedModel.includes('pro') ? 'pro' : 'experimental';
            const modelMultiplier = tokenConfig.modelMultipliers?.[modelKey] || 1.0;

            // Final calculation with all admin config values
            const finalCost = Math.ceil((costResult.totalCost + personaCost + depthCost) * modelMultiplier);
            const estimatedTokens = finalCost;

            console.log(`[TokenCalculation] ========== CALCULATION BREAKDOWN ==========`);
            console.log(`[TokenCalculation] Input: ${inputLength} chars | Output: ${responseLength} chars`);
            console.log(`[TokenCalculation] Persona: ${personaKey} (+${personaCost} tokens)`);
            console.log(`[TokenCalculation] Depth: ${depthKey} (+${depthCost} tokens)`);
            console.log(`[TokenCalculation] Complexity: ${complexityKey} (×${tokenConfig.complexityMultipliers?.[complexityKey] || 1})`);
            console.log(`[TokenCalculation] Model: ${modelKey} (×${modelMultiplier})`);
            console.log(`[TokenCalculation] Attachments: ${currentAttachments.length}`);
            console.log(`[TokenCalculation] ---`);
            console.log(`[TokenCalculation] Cost Result from calculateTokenCost():`, costResult);
            console.log(`[TokenCalculation] Formula: (${costResult.totalCost} + ${personaCost} + ${depthCost}) × ${modelMultiplier} = ${finalCost}`);
            console.log(`[TokenCalculation] FINAL CHARGE: ${estimatedTokens} tokens`);
            console.log(`[TokenCalculation] =========================================`);

            // CHECK FOR UNLIMITED TOKENS - Don't charge if user has unlimited
            const userHasUnlimited = user ? hasUnlimitedTokens(user) : false;
            const tokensToCharge = userHasUnlimited ? 0 : estimatedTokens;

            // NOW create the message with the ACTUAL tokens charged
            const botMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: Role.MODEL,
                content: finalContent,
                thought: response.thought,
                timestamp: new Date().toISOString(),
                referencedAttachments: referencedAttachments.length > 0 ? referencedAttachments : undefined,
                tokensCharged: tokensToCharge, // Use 0 if unlimited, otherwise actual calculated tokens
                modelUsed: response.modelUsed || selectedModel,
                budgetAdjusted: autoBudgetEnabled && budgetPerRequest ? budgetPerRequest : undefined,
                persona: aiMode,
                depth: aiDepth
            };

            const finalMessages = [...currentMessages, botMessage];
            updateCurrentSession(finalMessages);

            if (user) {
                // NEW TOKEN SYSTEM: Subtract from tokenBalance instead of adding to dailyTokenUsage
                const tierTokenLimit = user.isMax ? 50000 : user.isPremium ? 15000 : 2000;
                const currentBalance = user.tokenBalance ?? tierTokenLimit;
                const newTokenBalance = Math.max(0, currentBalance - tokensToCharge);
                const newAttachments = (user.dailyAttachmentCount || 0) + currentAttachments.length;

                const updatedUser = {
                    ...user,
                    dailyMessageCount: (user.dailyMessageCount || 0) + 1,
                    tokenBalance: newTokenBalance,
                    dailyAttachmentCount: newAttachments,
                    lastActive: Date.now(),
                    // Preserve lastTokenRefresh and lastResetTime
                    lastTokenRefresh: user.lastTokenRefresh || Date.now(),
                    lastResetTime: user.lastResetTime || new Date().toISOString()
                };

                if (userHasUnlimited) {
                    console.log(`💎 UNLIMITED: No token charge (would have been ${estimatedTokens})`);
                } else {
                    console.log(`💰 Token consumption: -${tokensToCharge} tokens (Balance: ${newTokenBalance})`);
                }

                // Trigger token consumption animation ONLY if tokens were charged
                if (tokensToCharge > 0) {
                    setTokenAnimationData({ tokens: tokensToCharge, visible: true });
                }

                // SMART REFUND CHECK - Analyze response quality and issue refunds for issues
                if (tokensToCharge > 0 && finalContent) {
                    const qualityCheck = analyzeResponseQuality(finalContent);

                    if (qualityCheck.qualityScore < 90) {
                        console.log(`[SmartRefund] Quality issues detected: ${qualityCheck.issues.join(', ')}`);
                        console.log(`[SmartRefund] Quality Score: ${qualityCheck.qualityScore}%`);

                        // Process smart refund asynchronously
                        processSmartRefund(updatedUser, finalContent, tokensToCharge, text)
                            .then(({ user: refundedUser, refunded, amount, reason }) => {
                                if (refunded && amount > 0) {
                                    console.log(`[SmartRefund] ✅ Refunded ${amount} tokens: ${reason}`);
                                    setUser(refundedUser);
                                    showToast('info', `🔄 ${amount} tokens refunded: ${reason}`);
                                }
                            })
                            .catch(err => console.error('[SmartRefund] Error:', err));
                    }
                }

                // Check if tokens are running low (50% or more used) - show purchase prompt
                // ONLY if user doesn't have unlimited tokens
                if (!userHasUnlimited) {
                    const tokenLimit = user.isMax || user.accountTier === 'max'
                        ? (config?.maxTokenLimit || 50000)
                        : user.isPremium
                            ? (config?.premiumTokenLimit || 15000)
                            : (config?.freeTokenLimit || 2000);
                    const usagePercent = ((tokenLimit - newTokenBalance) / tokenLimit) * 100;
                    if (usagePercent >= 75) {
                        // Show prompt after a delay to not interrupt
                        setTimeout(() => {
                            setShowLowTokenPrompt(true);
                            setIsTokenPurchaseOpen(true);
                        }, 2000);
                    }
                }

                // Update state
                setUser(updatedUser);

                // Update localStorage - both current user and users array
                localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
                const allUsers = JSON.parse(localStorage.getItem('ledger_users') || '[]');
                const dbUpdate = allUsers.map((u: User) => u.id === user.id ? updatedUser : u);
                localStorage.setItem('ledger_users', JSON.stringify(dbUpdate));

                console.log('✅ User data saved to localStorage:', {
                    tokenBalance: newTokenBalance,
                    lastTokenRefresh: updatedUser.lastTokenRefresh,
                    lastResetTime: updatedUser.lastResetTime
                });

                // Update Firebase with all relevant fields
                if (firebase.db) {
                    import('firebase/firestore').then(({ doc, updateDoc }) => {
                        const userRef = doc(firebase.db!, 'users', user.id);
                        updateDoc(userRef, {
                            tokenBalance: newTokenBalance,
                            dailyAttachmentCount: newAttachments,
                            dailyMessageCount: updatedUser.dailyMessageCount,
                            lastActive: Date.now(),
                            lastTokenRefresh: updatedUser.lastTokenRefresh,
                            lastResetTime: updatedUser.lastResetTime
                        }).catch(err => console.error('Failed to update Firebase:', err));
                    });
                }

                // Log token usage to history
                const historyKey = `token_history_${user.id}`;
                const history = JSON.parse(localStorage.getItem(historyKey) || '[]');
                history.push({
                    timestamp: Date.now(),
                    tokens: estimatedTokens,
                    operation: 'Chat Message'
                });
                // Keep only last 100 entries
                if (history.length > 100) history.shift();
                localStorage.setItem(historyKey, JSON.stringify(history));
            } else {
                // Trigger animation for guest users too
                setTokenAnimationData({ tokens: estimatedTokens, visible: true });
                setGuestTokenUsage(prev => prev + estimatedTokens);
            }

            setTimeout(() => inputRef.current?.focus(), 100);

            // ==================== GAMIFICATION TRACKING ====================
            try {
                // Track question asked
                trackQuestion();

                // MASTERY PATH: Award mastery points if conversation is relevant to current topic
                try {
                    const { processConversationForMastery, checkMasteryMilestones } = await import('./services/masteryPathService');
                    const masteryResult = processConversationForMastery(text, finalContent);
                    if (masteryResult && masteryResult.awarded) {
                        showToast('info', `+${masteryResult.pointsAwarded} Mastery Points for "${masteryResult.topicTitle}"`);
                        if (masteryResult.thresholdReached) {
                            setTimeout(() => {
                                showToast('success', `Mastery threshold reached! You can now take the exam for "${masteryResult.topicTitle}"`);
                            }, 1500);
                        }
                        // Check milestones after awarding points
                        const milestones = checkMasteryMilestones();
                        if (milestones.newMilestones.length > 0) {
                            for (const m of milestones.newMilestones) {
                                setTimeout(() => {
                                    showToast('success', `Milestone: ${m.title} — +${m.reward.tokens} tokens, +${m.reward.xp} XP`);
                                }, 2500);
                            }
                        }
                    }
                } catch (masteryErr) {
                    console.error('[MasteryPath] Error processing mastery:', masteryErr);
                }

                // Award XP for message sent (3 XP base)
                const xpResult = awardXP(3, 'message_sent');
                if (xpResult.xpGained > 0) {
                    setXpGainAnimation({ amount: xpResult.xpGained, visible: true });
                    setTimeout(() => setXpGainAnimation({ amount: 0, visible: false }), 2500);
                }

                // Check for level up
                if (xpResult.leveledUp && xpResult.newLevel) {
                    showToast('success', `🎉 Level Up! You're now ${xpResult.newLevel.title}!`);
                    if (xpResult.levelUpReward && xpResult.levelUpReward.tokens > 0) {
                        showToast('success', `🎁 Level up bonus: +${xpResult.levelUpReward.tokens} tokens!`);
                    }
                }

                // Check for new achievements
                const newAchievements = checkAchievements();
                if (newAchievements.length > 0) {
                    setAchievementUnlocks(newAchievements);
                }

                // Check milestones
                checkMilestones();

            } catch (gamificationError) {
                console.error('[Gamification] Tracking error:', gamificationError);
            }

            // ==================== SMART RECOMMENDATION SYSTEM ====================
            try {
                // Record the interaction signal
                smartRecommendationService.recordMessage(text, finalContent);

                // Run analysis to check if we should show a recommendation
                const tokenLimit = user?.isMax ? (config?.maxTokenLimit || 50000)
                    : user?.isPremium ? (config?.premiumTokenLimit || 15000)
                    : (config?.freeTokenLimit || 2000);
                const userHasUnlimitedCheck = user ? hasUnlimitedTokens(user) : false;

                const recommendation = smartRecommendationService.analyze({
                    user: user ? {
                        tokenBalance: user.tokenBalance,
                        isPremium: user.isPremium,
                        isMax: user.isMax,
                        accountType: user.accountType,
                        totalQuestions: user.totalQuestions,
                        activePromoEffects: user.activePromoEffects
                    } : null,
                    currentMode: aiMode,
                    currentDepth: aiDepth,
                    tokenLimit,
                    hasUnlimitedTokens: userHasUnlimitedCheck,
                    lastQuery: text,
                    lastResponse: finalContent
                });

                if (recommendation) {
                    // Show after a short delay so it doesn't compete with the response
                    setTimeout(() => setActiveRecommendation(recommendation), 2500);
                }
            } catch (recError) {
                console.warn('[SmartRecommendation] Analysis failed (non-critical):', recError);
            }

        } catch (error) {
            console.error('Error in processMessage:', error);

            // Determine if we should refund tokens
            const refundDecision = shouldRefund(error);

            let errorContent = "I encountered an error. Please check your connection.";

            // Handle refund if applicable
            if (user && refundDecision.shouldRefund) {
                // Use pre-estimated token cost if available, otherwise calculate based on input
                const tokensToRefund = estimatedTokenCost || Math.max(10, Math.floor(text.length / 10));

                try {
                    const refundedUser = await refundTokens(
                        user,
                        tokensToRefund,
                        refundDecision.reason,
                        text
                    );
                    setUser(refundedUser);

                    errorContent = `⚠️ ${refundDecision.reason}. Your request could not be completed.\n\n✅ **${tokensToRefund} tokens have been automatically refunded** to your account.\n\nPlease try again.`;
                } catch (refundError) {
                    console.error('Failed to process refund:', refundError);
                    errorContent = `⚠️ ${refundDecision.reason}. Your request could not be completed. Please try again.`;
                }
            } else if (!user) {
                errorContent = "Connection error. Please try again.";
            } else {
                errorContent = `⚠️ ${refundDecision.reason}. Please try again.`;
            }

            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: Role.MODEL,
                content: errorContent,
                timestamp: new Date().toISOString(),
                isError: true
            };
            updateCurrentSession([...currentMessages, errorMessage]);
        } finally {
            setIsLoading(false);
            abortControllerRef.current = null;

            // Check for LUCA's Quest trigger after successful message
            if (user && currentSessionId && !activeQuest) {
                try {
                    // Load quest config from admin or use defaults
                    const questConfigStr = localStorage.getItem('luca_quest_config');
                    const adminQuestConfig = localStorage.getItem('luca_admin_quest_config');
                    const questConfig = adminQuestConfig
                        ? JSON.parse(adminQuestConfig)
                        : questConfigStr
                            ? JSON.parse(questConfigStr)
                            : {
                                enabled: true,
                                triggerProbability: 80,
                                minConversationTime: 3, // 3 minutes (reduced for better engagement)
                                questCooldown: 5, // 5 minutes cooldown
                                minItems: 5,
                                maxItems: 20
                            };

                    // Check if quest system is enabled (default to true)
                    if (questConfig.enabled !== false) {
                        // Check tier access (default to allowing all tiers)
                        const userTier = user.isMax || user.accountTier === 'max' ? 'max'
                            : user.isPremium ? 'premium' : 'standard';
                        const hasAccess = questConfig.tierAccess?.[userTier] !== false;

                        // Get current session for analysis
                        const currentSession = sessionManager.getSession(currentSessionId);

                        // Quest triggers if:
                        // 1. User has access
                        // 2. Can trigger quest (cooldown passed, time requirement met)
                        // 3. Session has enough messages (at least 4 back-and-forth)
                        // 4. Random probability check passes
                        if (hasAccess && currentSession && currentSession.messages.length >= 4) {
                            // Start tracking if not already
                            lucasQuestService.startTracking(currentSessionId);

                            if (lucasQuestService.canTriggerQuest(currentSessionId)) {
                                // Random trigger based on probability (default 80%)
                                const probability = questConfig.triggerProbability ?? 80;
                                const shouldTrigger = Math.random() * 100 < probability;

                                if (shouldTrigger) {
                                    console.log('[LucasQuest] Triggering quest for session:', currentSessionId);
                                    console.log('[LucasQuest] Messages in session:', currentSession.messages.length);

                                    const quest = lucasQuestService.triggerQuest(
                                        currentSessionId,
                                        currentSession.messages,
                                        questConfig
                                    );

                                    if (quest) {
                                        console.log('[LucasQuest] Quest generated:', quest.id);
                                        setActiveQuest(quest);
                                        showToast('success', '🎯 Luca\'s Quest is ready! Test your knowledge!');
                                    }
                                }
                            } else {
                                // Log why quest didn't trigger for debugging
                                const timeUntilNext = lucasQuestService.getTimeUntilNextQuest(currentSessionId);
                                if (timeUntilNext > 0) {
                                    console.log(`[LucasQuest] Cooldown active: ${Math.ceil(timeUntilNext / 1000 / 60)} minutes remaining`);
                                }
                            }
                        }
                    }
                } catch (questError) {
                    console.error('[LucasQuest] Error checking quest trigger:', questError);
                }
            }
        }
    };

    // Ad Watch Handler for Contextual Upsells
    const startAdWatch = () => {
        setIsLowBalanceModalOpen(false);
        // In a real app, this would trigger the ad network SDK
        showToast('info', '🎬 Loading sponsored video...');

        setTimeout(() => {
            if (user) {
                const reward = 50;
                // Use the same tier limit fallback logic as seen elsewhere
                const tokenLimit = user?.isMax || user?.accountTier === 'max'
                    ? (config?.maxTokenLimit || 50000)
                    : user?.isPremium
                        ? (config?.premiumTokenLimit || 15000)
                        : (config?.freeTokenLimit || 2000);
                const currentBalance = user.tokenBalance ?? tokenLimit;
                const newBalance = currentBalance + reward;

                setUser(prev => prev ? { ...prev, tokenBalance: newBalance } : prev);

                // Update specific storage keys if they exist
                const savedUser = localStorage.getItem('ledger_current_user');
                if (savedUser) {
                    const userData = JSON.parse(savedUser);
                    localStorage.setItem('ledger_current_user', JSON.stringify({
                        ...userData,
                        tokenBalance: newBalance
                    }));
                }

                showToast('success', `Thank you for watching! +${reward} tokens added.`);
            }
        }, 2500);
    };

    // File Upload Handlers with Token Cost Modal
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Guest restriction
        if (!user || user.isAnonymous) {
            showToast('warning', 'Please create an account to upload files');
            setShowGuestLimitModal(true);
            return;
        }

        const file = event.target.files?.[0];
        if (!file) return;

        // Track mastery
        if (!localStorage.getItem('luca_mastery_document_uploaded')) {
            localStorage.setItem('luca_mastery_document_uploaded', 'true');
        }

        // Import to check for PPT files
        const isPPT = file.type === 'application/vnd.ms-powerpoint' ||
            file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
            file.name.toLowerCase().endsWith('.ppt') ||
            file.name.toLowerCase().endsWith('.pptx');

        // OPTIMIZATION: Drastically reduced upload cost
        // Old: file.size / 3 (approx 350k tokens/MB)
        // New: file.size / 1000 (approx 1k tokens/MB)
        // PPT files get 50% extra token cost
        let estimatedCost = Math.ceil(file.size / 1000);
        if (isPPT) {
            estimatedCost = Math.ceil(estimatedCost * 1.5); // 50% extra for PowerPoint
            showToast('info', '📊 PowerPoint files cost 50% more tokens for analysis');
        }

        setPendingFile({ file, estimatedCost });
        setShowTokenCostModal(true);
    };

    const handleTokenCostConfirm = async () => {
        if (!pendingFile) return;

        // Check for token balance
        if (user && !hasUnlimitedTokens(user)) {
            const tokenConfig = loadTokenCostConfig();
            if (tokenConfig.attachmentSizeCosts.enabled && tokenConfig.attachmentSizeCosts.chargeOnAttach) {
                const attachmentCost = calculateAttachmentCost(pendingFile.file.size, tokenConfig);
                const currentBalance = user.tokenBalance ?? ((user.isMax || user.accountTier === 'max') ? 50000 : user.isPremium ? 15000 : 2000);

                if (currentBalance < attachmentCost) {
                    setLowBalanceNeeded(attachmentCost);
                    setIsLowBalanceModalOpen(true);
                    setShowTokenCostModal(false);
                    return;
                }
            }
        }

        try {
            const file = pendingFile.file;
            const validation = validateFileSize(file);

            if (!validation.valid) {
                alert(validation.error);
                setShowTokenCostModal(false);
                setPendingFile(null);
                return;
            }

            let base64: string;

            // Check if it's a PowerPoint file
            const isPPT = file.type === 'application/vnd.ms-powerpoint' ||
                file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
                file.name.toLowerCase().endsWith('.ppt') ||
                file.name.toLowerCase().endsWith('.pptx');

            // Use enhanced processing based on file type
            if (file.type.startsWith('image/')) {
                console.log('[App] Compressing image with intelligent optimization...');
                base64 = await compressImageSmart(file);
            } else if (file.type === 'application/pdf') {
                console.log('[App] Processing PDF with progressive loading...');
                const pdfResult = await processPDF(file, { extractText: validation.requiresProgressive });
                base64 = pdfResult.data;
            } else if (isPPT) {
                // PowerPoint files - premium processing
                console.log('[App] Processing PowerPoint presentation...');
                base64 = await readFileWithTimeout(file);
                showToast('success', '📊 PowerPoint loaded - ready for AI analysis');
            } else {
                // Fallback for other file types
                base64 = await readFileWithTimeout(file);
            }

            // Determine the MIME type (handle PPT extension-based detection)
            let mimeType = file.type;
            if (!mimeType && file.name.toLowerCase().endsWith('.pptx')) {
                mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
            } else if (!mimeType && file.name.toLowerCase().endsWith('.ppt')) {
                mimeType = 'application/vnd.ms-powerpoint';
            }

            const newAttachment: Attachment = {
                name: file.name,
                mimeType: mimeType || file.type,
                data: base64.split(',')[1],
                preview: base64
            };

            // If no current session or current session has no messages, create new session
            const currentSession = displayedSessions.find(s => s.id === currentSessionId);
            if (!currentSession || currentSession.messages.length === 0) {
                handleNewChat();
                showToast('info', 'New session created for your file');
            }

            setAttachments(prev => [...prev, newAttachment]);

            // Charge user for attachment based on file size
            const tokenConfig = loadTokenCostConfig();
            if (tokenConfig.attachmentSizeCosts.enabled && tokenConfig.attachmentSizeCosts.chargeOnAttach) {
                const attachmentCost = calculateAttachmentCost(file.size, tokenConfig);
                if (attachmentCost > 0 && user && !user.isAnonymous) {
                    // Fix: Use tier limit as fallback instead of 0 for undefined tokenBalance
                    const tokenLimit = user?.isMax || user?.accountTier === 'max'
                        ? (config?.maxTokenLimit || 50000)
                        : user?.isPremium
                            ? (config?.premiumTokenLimit || 15000)
                            : (config?.freeTokenLimit || 2000);
                    const currentBalance = user.tokenBalance ?? tokenLimit;
                    const newBalance = currentBalance - attachmentCost;

                    // Update user token balance
                    setUser(prev => prev ? { ...prev, tokenBalance: newBalance } : prev);

                    // Save to localStorage
                    const savedUser = localStorage.getItem('ledger_current_user');
                    if (savedUser) {
                        const userData = JSON.parse(savedUser);
                        localStorage.setItem('ledger_current_user', JSON.stringify({
                            ...userData,
                            tokenBalance: newBalance
                        }));
                    }

                    // Update in Firebase
                    if (firebase.updateUser) {
                        await firebase.updateUser(user.id, { tokenBalance: newBalance });
                    }

                    console.log(`[App] 📎 Attachment charge: -${attachmentCost} tokens (File: ${(file.size / (1024 * 1024)).toFixed(2)} MB, Balance: ${newBalance})`);
                    showToast('info', `📎 ${file.name} attached (-${attachmentCost} tokens)`);
                } else {
                    showToast('success', `${file.name} attached successfully`);
                }
            } else {
                showToast('success', `${file.name} attached successfully`);
            }

            console.log(`[App] Attachment added: ${file.name}`);

            // Reset file input to allow re-selecting files
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('[App] Failed to process file:', error);
            alert(`Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`);

            // Reset file input on error too
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }

        setShowTokenCostModal(false);
        setPendingFile(null);
    };

    const handleTokenCostCancel = () => {
        setShowTokenCostModal(false);
        setPendingFile(null);
        // Reset file input to allow re-selecting the same file
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSendMessage = () => {
        if ((!inputValue.trim() && attachments.length === 0)) return;

        // Guest user restrictions
        if (!user || user.isAnonymous) {
            const guestMessageCount = user?.guestMessageCount || 0;
            if (guestMessageCount >= 1) {
                setShowGuestLimitModal(true);
                return;
            }
            // Increment guest message count
            if (user) {
                setUser({ ...user, guestMessageCount: guestMessageCount + 1 });
            }
        }

        const text = inputValue.trim();
        const currentAtt = [...attachments];

        // -------------------------------------------------------------------------
        // 🧠 INTENT DETECTION: Intercept Quiz/Exam requests to trigger Native Flow
        // -------------------------------------------------------------------------

        // 1. MATCH QUIZ: "Generate quiz on [topic]"
        const quizMatch = text.match(/^(?:generate|create|make|give|start)\s+(?:a\s+)?(?:quick\s+)?(?:quiz|test|assessment|practice questions)(?:\s+(?:on|about|for)\s+(.+))?$/i);

        if (quizMatch) {
            const topic = quizMatch[1] ? quizMatch[1].trim() : 'General Review';
            setInputValue('');
            setAttachments([]);

            console.log('[Intent detected] Triggering Quiz for:', topic);

            // Use default reasonable config for chat-triggered quizzes
            handleGenerateQuiz({
                topic: topic,
                itemCount: 5, // Default for quick chat quizzes
                difficulty: 'medium',
                type: 'multiple-choice',
                focus: 'mixed'
            });
            return;
        }

        // 2. MATCH MOCK EXAM: "Generate mock exam on [subject]"
        const examMatch = text.match(/^(?:generate|create|make|give|start)\s+(?:a\s+)?(?:mock\s+)?(?:exam|examination|board exam)(?:\s+(?:on|about|for)\s+(.+))?$/i);

        if (examMatch) {
            const subject = examMatch[1] ? examMatch[1].trim() : 'General CPA Review';
            setInputValue('');
            setAttachments([]);

            console.log('[Intent detected] Triggering Mock Exam for:', subject);

            // Use default reasonable config for chat-triggered exams
            handleGenerateMockExam({
                subject: subject,
                itemCount: 20, // Shorter than full exam for quick chat interaction
                difficulty: 'hard',
                focus: 'mixed',
                questionFormat: 'mixed_with_essays',
                essayCount: 1,
                essayConfig: {
                    includeRubric: true,
                    includeKeyPoints: true,
                    minWordCount: 100,
                    gradingScale: '1.0-5.0'
                },
                // Missing properties defaults
                timeLimitMinutes: 60,
                includeTheories: true,
                includeComputations: true,
                simulationMode: 'practice',

                showAnswerKey: true, // For study mode exams
                shuffleQuestions: true,
                passingScore: 75
            });
            return;
        }
        // -------------------------------------------------------------------------

        // Get threshold from admin config (default 600)
        const warningThreshold = config?.tokenWarningThreshold || 600;

        // Use the new pre-emptive token estimation system (v5.0 - matches actual consumption)
        const model = user?.isPremium && isPremiumModelEnabled ? 'gemini-2.0-pro' : 'gemini-2.5-flash-lite';
        // Calculate token limit based on user tier
        const isMaxTier = user?.isMax || user?.accountTier === 'max';
        const isPremiumTier = user?.isPremium && !isMaxTier;
        const tokenLimit = isMaxTier
            ? (config?.maxTokenLimit || 50000)
            : isPremiumTier
                ? (config?.premiumTokenLimit || 15000)
                : (config?.freeTokenLimit || 2000);
        // NEW TOKEN SYSTEM: Use tokenBalance for calculation, fallback to tokenLimit for new users
        const currentBalance = user?.tokenBalance ?? tokenLimit;
        // Fix: Don't calculate usage as negative, just use the balance directly
        const currentUsage = Math.max(0, tokenLimit - currentBalance); // Ensure non-negative

        // Get token estimate using ADMIN DASHBOARD CONFIG
        const adminTokenConfig = loadTokenCostConfig();

        // Map persona and depth
        const personaKey = aiMode === 'standard' ? 'standard' :
            aiMode === 'educator' ? 'tutor' : 'professional';
        const depthKey = aiDepth === 'concise' ? 'concise' :
            aiDepth === 'balanced' ? 'balanced' :
                aiDepth === 'detailed' ? 'detailed' : 'comprehensive';

        // Use advanced complexity analyzer
        const complexityAnalysis = advancedAnalyzeComplexity(text, currentAtt, messages.length);
        const complexityKey = complexityAnalysis.level;

        console.log('[PreEmptiveComplexity]', complexityAnalysis.reasoning);

        // Calculate using admin config
        const inputLength = text.length;
        const estimatedOutputLength = inputLength * 15; // Estimate output is 15x input

        const preEstimateCost = calculateTokenCost({
            operationType: 'chatMessage',
            inputLength,
            estimatedOutputLength,
            complexity: complexityKey,
            messageContent: text,
            attachmentCount: currentAtt.length
        }, adminTokenConfig);

        const personaCost = adminTokenConfig.personaCosts?.[personaKey] || 0;
        const depthCost = adminTokenConfig.depthCosts?.[depthKey] || 0;
        const modelKey = model.includes('flash') ? 'flash' :
            model.includes('pro') ? 'pro' : 'experimental';
        const modelMultiplier = adminTokenConfig.modelMultipliers?.[modelKey] || 1.0;

        const totalEstimatedTokens = Math.ceil((preEstimateCost.totalCost + personaCost + depthCost) * modelMultiplier);

        const estimate = {
            totalEstimatedTokens,
            inputTokens: inputLength,
            estimatedOutputTokens: estimatedOutputLength,
            exceedsThreshold: totalEstimatedTokens > warningThreshold,
            willExceedLimit: (currentUsage + totalEstimatedTokens) > tokenLimit
        };

        // GUEST TOKEN LIMIT CHECK (Added per request)
        if ((!user || user.isAnonymous) && totalEstimatedTokens > 185) {
            console.log(`[GuestLimit] Request blocked. Cost: ${totalEstimatedTokens} > 185 limit`);
            showToast('warning', '⚠️ This request is too complex for guest users. Please sign in to continue.');
            setAuthMode('register');
            setIsAuthModalOpen(true);
            return;
        }

        // Debug logging with admin config info
        console.log('[PreEmptiveToken] Using ADMIN CONFIG - Estimated cost:', totalEstimatedTokens, 'tokens');
        console.log('[PreEmptiveToken] Persona:', personaKey, `(+${personaCost})`, '| Depth:', depthKey, `(+${depthCost})`, '| Model:', modelKey, `(×${modelMultiplier})`);
        console.log('[PreEmptiveToken] Complexity:', complexityKey, `(×${adminTokenConfig.complexityMultipliers?.[complexityKey] || 1})`);
        console.log('[PreEmptiveToken] Warning threshold:', warningThreshold);
        console.log('[PreEmptiveToken] Will exceed limit:', estimate.willExceedLimit);

        // Check if auto-budget is exceeded
        // Process message directly (pre-emptive warning removed)
        // Log estimate for debugging if high cost
        if (estimate.totalEstimatedTokens > warningThreshold) {
            console.log('📊 [TokenEstimate] High cost message:', {
                cost: estimate.totalEstimatedTokens,
                threshold: warningThreshold,
                willExceedLimit: estimate.willExceedLimit
            });
        }

        setInputValue('');
        setAttachments([]);

        // Check for low balance BEFORE sending
        if (!hasUnlimitedTokens(user) && estimate.willExceedLimit && currentBalance < totalEstimatedTokens) {
            console.log('[TokenCheck] Insufficient tokens for message:', currentBalance, '<', totalEstimatedTokens);
            setLowBalanceNeeded(totalEstimatedTokens);
            setIsLowBalanceModalOpen(true);
            return;
        }

        // Small delay before processing to let UI update (input clear, scroll)
        setTimeout(() => {
            processMessage(text, currentAtt, false, estimate.totalEstimatedTokens);
        }, 50);
    };

    const confirmSendMessage = () => {
        if (pendingMessage) {
            processMessage(pendingMessage.text, pendingMessage.attachments, false);
            setPendingMessage(null);
        } else if (pendingAction) {
            pendingAction();
            setPendingAction(null);
        }
        setIsCostConfirmationOpen(false);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    // Attachment Cost Modal Handlers
    const handleAttachmentCostConfirm = async () => {
        try {
            if (pendingMessage) {
                setInputValue('');
                setAttachments([]);
                await processMessage(pendingMessage.text, pendingMessage.attachments, false);
                setPendingMessage(null);
            }
        } catch (error) {
            console.error('Error processing message from attachment modal:', error);
            showToast('error', 'Failed to send message. Please try again.');
        } finally {
            setShowAttachmentCostModal(false);
            setAttachmentCostBreakdown(null);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const handleAttachmentCostCancel = () => {
        // Restore the input and attachments
        if (pendingMessage) {
            setInputValue(pendingMessage.text);
            setAttachments(pendingMessage.attachments);
        }
        setShowAttachmentCostModal(false);
        setAttachmentCostBreakdown(null);
        setPendingMessage(null);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    // Advanced Cost Modal Handlers
    const handleAdvancedCostConfirm = () => {
        if (pendingOperation) {
            pendingOperation.action();
            setPendingOperation(null);
        }
        setShowAdvancedCostModal(false);
        setAdvancedCostBreakdown(null);
    };

    const handleAdvancedCostCancel = () => {
        setPendingOperation(null);
        setShowAdvancedCostModal(false);
        setAdvancedCostBreakdown(null);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    // Auto Budget Handlers
    const handleToggleAutoBudget = useCallback((enabled: boolean) => {
        setAutoBudgetEnabled(enabled);
        localStorage.setItem('luca_auto_budget_enabled', String(enabled));
        showToast('info', enabled ? '💰 Auto-budget enabled' : 'Auto-budget disabled');
    }, [showToast]);

    const handleSetBudgetPerRequest = useCallback((budget: number) => {
        setBudgetPerRequest(budget);
        localStorage.setItem('luca_budget_per_request', String(budget));
        showToast('info', `Budget set to ${budget.toLocaleString()} tokens/request`);
    }, [showToast]);

    // Drag and Drop Handlers
    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set dragging to false if leaving the chat input area
        if (e.currentTarget === chatInputAreaRef.current) {
            setIsDragging(false);
        }
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            processFiles(files);
        }
    }, [processFiles]);

    const handleRetry = () => {
        const messagesWithoutError = messages.filter(m => !m.isError);
        const lastUserMessage = [...messagesWithoutError].reverse().find(m => m.role === Role.USER);

        if (lastUserMessage) {
            const lastUserIndex = messagesWithoutError.lastIndexOf(lastUserMessage);
            const historyForRetry = messagesWithoutError.slice(0, lastUserIndex + 1);
            setMessages(historyForRetry);
            processMessage(lastUserMessage.content, lastUserMessage.attachments || [], true);
        }
    };

    const handleRegenerate = () => {
        const lastMsg = messages[messages.length - 1];
        if (lastMsg.role === Role.MODEL && !lastMsg.isError) {
            const lastUserMsg = [...messages].reverse().find(m => m.role === Role.USER);
            if (lastUserMsg) {
                const lastUserIndex = messages.lastIndexOf(lastUserMsg);
                const historyReset = messages.slice(0, lastUserIndex + 1);
                setMessages(historyReset);
                processMessage(lastUserMsg.content, lastUserMsg.attachments || [], true);
                showToast('info', 'Regeneration successful');
            }
        }
    };

    const confirmRefresh = () => {
        setIsRefreshModalOpen(false);
        if (isLoading) return;

        const standardsLabel = selectedStandards.length > 0
            ? selectedStandards.map(id => STANDARDS_OPTIONS.find(o => o.id === id)?.label).join(', ')
            : "General Accounting Standards";

        const refreshMessage: Message = {
            id: Date.now().toString(),
            role: Role.MODEL,
            content: `**System Protocol**: Context refreshed. Prioritizing updates for: **${standardsLabel}**.`,
            timestamp: new Date().toISOString(),
            isError: false,
        };
        updateCurrentSession([...messages, refreshMessage]);
        setSelectedStandards([]);
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    // ==================== SMART RECOMMENDATION ACTION HANDLER ====================
    const handleRecommendationAction = useCallback((action: string) => {
        setActiveRecommendation(null);

        switch (action) {
            case 'set_mode_educator':
                setAiMode('educator');
                showToast('success', '🎓 Switched to Educator mode — answers will be more step-by-step!');
                break;
            case 'set_mode_lawyer':
                setAiMode('lawyer');
                showToast('success', '⚖️ Switched to Lawyer mode — IRAC analysis enabled!');
                break;
            case 'set_depth_concise':
                setAiDepth('concise');
                showToast('info', '⚡ Switched to Concise mode — shorter, direct answers.');
                break;
            case 'set_depth_balanced':
                setAiDepth('balanced');
                showToast('info', '📖 Switched to Balanced mode — standard detail level.');
                break;
            case 'open_token_purchase':
                setTokenPurchaseInitialTab('tokens');
                setIsTokenPurchaseOpen(true);
                break;
            case 'open_upgrade_premium':
                setTokenPurchaseInitialTab('premium');
                setIsTokenPurchaseOpen(true);
                break;
            case 'open_mastery_store':
                setTokenPurchaseInitialTab('mastery');
                setIsTokenPurchaseOpen(true);
                break;
            case 'open_mock_exam_config':
                setIsMockExamConfigOpen(true);
                break;
            case 'enable_premium_model':
                setIsPremiumModelEnabled(true);
                showToast('success', '🧠 Premium model enabled for this session.');
                break;
            case 'open_flashcard_generator':
                setIsGenerateMaterialsOpen(true);
                break;
            case 'start_computation_quiz':
                setQuizInitialTopic('Computations');
                setIsQuizConfigOpen(true);
                break;
            case 'open_standards_browser':
                // Trigger standards browser if available
                try {
                    const event = new CustomEvent('open-standards-browser');
                    window.dispatchEvent(event);
                } catch (_) {}
                break;
            case 'pin_current_session':
                if (currentSessionId) {
                    const session = sessionManager.getSession(currentSessionId);
                    if (session) {
                        sessionManager.updateSession(currentSessionId, { ...session, pinned: true } as any);
                        showToast('success', '📌 Session pinned!');
                    }
                }
                break;
            default: {
                const topicNames: Record<string, string> = {
                    'TAX': 'Taxation', 'AUD': 'Auditing', 'FAR': 'Financial Accounting',
                    'AFAR': 'Advanced Accounting', 'MAS': 'Management Advisory Services', 'RFBT': 'Business Law'
                };
                // Handle dynamic actions like start_quiz_TAX
                if (action.startsWith('start_quiz_')) {
                    const topic = action.replace('start_quiz_', '');
                    setQuizInitialTopic(topicNames[topic] || topic);
                    setIsQuizConfigOpen(true);
                }
                // Handle ask_review_TOPIC — inject a review message into chat
                else if (action.startsWith('ask_review_')) {
                    const topic = decodeURIComponent(action.replace('ask_review_', ''));
                    const reviewQuery = `Please review and explain the key concepts I got wrong about ${topic}. Break them down step by step so I can understand my mistakes.`;
                    setInputValue(reviewQuery);
                    setTimeout(() => inputRef.current?.focus(), 100);
                }
                // Handle generate_study_guide_TOPIC
                else if (action.startsWith('generate_study_guide_')) {
                    const topicKey = action.replace('generate_study_guide_', '');
                    const topicName = topicNames[topicKey] || topicKey;
                    setIsStudyGuideConfigOpen(true);
                    setMaterialGenerationTopic(topicName);
                }
                break;
            }
        }
    }, [showToast, setAiMode, setAiDepth, setTokenPurchaseInitialTab, setIsTokenPurchaseOpen, setIsMockExamConfigOpen, setIsPremiumModelEnabled, setQuizInitialTopic, setIsQuizConfigOpen, setIsGenerateMaterialsOpen, currentSessionId, setInputValue, setIsStudyGuideConfigOpen, setMaterialGenerationTopic]);

    const handleRecommendationDismiss = useCallback(() => {
        if (activeRecommendation) {
            smartRecommendationService.dismissRecommendation(activeRecommendation.id);
        }
        setActiveRecommendation(null);
    }, [activeRecommendation]);

    const handleFeedback = async (messageId: string, type: 'up' | 'down'): Promise<{ rewarded: boolean; amount: number } | void> => {
        const feedbackValue = type === 'up' ? 'positive' : 'negative';
        const message = messages.find(m => m.id === messageId);
        if (!message) return;

        // If clicking the same feedback type that's already set, just toggle off (no reward)
        if (message.feedback === feedbackValue) {
            const updatedMessages = messages.map(msg =>
                msg.id === messageId ? { ...msg, feedback: undefined } : msg
            );
            updateCurrentSession(updatedMessages);
            return;
        }

        // Open detailed feedback modal
        setFeedbackMessageId(messageId);
        setFeedbackMessageContent(message.content);
        setFeedbackType(type);
        setFeedbackTokensCharged(message.tokensUsed || 0);
        setShowFeedbackModal(true);
    };

    // Handle detailed feedback submission from modal
    const handleDetailedFeedbackSubmit = async (feedbackData: FeedbackData): Promise<{ refundAmount: number }> => {
        const messageId = feedbackData.messageId;
        const message = messages.find(m => m.id === messageId);
        if (!message) return { refundAmount: 0 };

        // Update message with feedback
        const feedbackValue = feedbackData.type === 'up' ? 'positive' : 'negative';
        const updatedMessages = messages.map(msg =>
            msg.id === messageId ? { ...msg, feedback: feedbackValue } : msg
        );
        updateCurrentSession(updatedMessages);

        // Record feedback signal for smart recommendations
        try {
            smartRecommendationService.recordFeedback(feedbackValue as 'positive' | 'negative' | 'neutral');
        } catch (_) { /* non-critical */ }

        // Find the user query (previous message)
        const messageIndex = messages.findIndex(m => m.id === messageId);
        const previousUserMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;

        // Calculate enhanced token refund based on detailed feedback
        const feedbackReward = localKnowledgeService.calculateFeedbackReward({
            query: previousUserMessage?.content || '',
            response: message.content,
            rating: feedbackData.type === 'up' ? 'positive' : 'negative',
            category: feedbackData.category,
            specificIssues: feedbackData.specificIssues,
            suggestedImprovement: feedbackData.suggestedImprovement,
            qualityRating: feedbackData.qualityRating,
            tokensCharged: message.tokensCharged || message.tokenCost || 0,
            timestamp: Date.now()
        });

        // Refund tokens to user
        if (feedbackReward.refundAmount > 0 && user) {
            const newBalance = (user.tokenBalance || 0) + feedbackReward.refundAmount;
            setUser({ ...user, tokenBalance: newBalance });

            // Save to localStorage
            try {
                const userData = {
                    tokenBalance: newBalance,
                    lastTokenRefresh: user.lastTokenRefresh,
                    lastResetTime: user.lastResetTime
                };
                localStorage.setItem('userData', JSON.stringify(userData));
                console.log(`✅ User data saved to localStorage:`, userData);
            } catch (error) {
                console.error('Failed to save user data:', error);
            }

            console.log(`[FeedbackReward] Refunded ${feedbackReward.refundAmount} tokens (${feedbackReward.refundPercentage}%)`);
        }

        // Log detailed feedback to Firebase
        if (firebase.isInitialized) {
            const feedbackItem: FeedbackItem = {
                id: `${messageId}_${Date.now()}`,
                userId: user?.id || 'guest',
                query: previousUserMessage?.content.substring(0, 500) || '',
                response: message.content.substring(0, 500),
                rating: feedbackData.type,
                timestamp: Date.now(),
                modelUsed: message.modelUsed || config?.freeModel
            };
            logFeedback(feedbackItem).catch(err => console.error('Failed to log feedback:', err));

            // Process detailed feedback for AI learning system
            const detailedFeedbackText = `Category: ${feedbackData.category}\n` +
                (feedbackData.specificIssues?.length ? `Issues: ${feedbackData.specificIssues.join(', ')}\n` : '') +
                `Details: ${feedbackData.details}\n` +
                (feedbackData.suggestedImprovement ? `Suggestion: ${feedbackData.suggestedImprovement}` : '');

            processAILearningFeedback(
                messageId,
                previousUserMessage?.content || '',
                message.content,
                feedbackData.type === 'up' ? 'positive' : 'negative',
                detailedFeedbackText,
                {
                    userId: user?.id,
                    domain: 'GENERAL',
                    complexity: 'moderate',
                    persona: aiMode,
                    depth: aiDepth,
                    modelUsed: message.modelUsed || config?.freeModel,
                    tokensUsed: message.tokenCost || 0
                }
            ).catch(err => console.error('Failed to process AI learning feedback:', err));

            // Learn from detailed feedback in local knowledge service
            localKnowledgeService.learnFromInteraction({
                query: previousUserMessage?.content || '',
                response: message.content,
                rating: feedbackData.type === 'up' ? 'positive' : 'negative',
                category: feedbackData.category,
                specificIssues: feedbackData.specificIssues,
                suggestedImprovement: feedbackData.suggestedImprovement,
                qualityRating: feedbackData.qualityRating,
                tokensCharged: message.tokensCharged || message.tokenCost || 0,
                timestamp: Date.now()
            });
        }

        // Close modal
        setShowFeedbackModal(false);

        // Show success toast with refund amount
        if (feedbackReward.refundAmount > 0) {
            showToast('success', `🎁 +${feedbackReward.refundAmount} tokens refunded (${feedbackReward.refundPercentage}%)! Thanks for your detailed feedback!`);
        } else {
            showToast('success', feedbackData.type === 'up' ? '👍 Thanks for your feedback!' : '👎 Feedback recorded');
        }

        // Return reward info
        return {
            refundAmount: feedbackReward.refundAmount
        };
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && appSettings.sendOnEnter) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    // Generated Materials Management
    const saveMaterial = useCallback((material: GeneratedMaterial) => {
        const updated = [...generatedMaterials, material];
        setGeneratedMaterials(updated);
        localStorage.setItem('ledger_generated_materials', JSON.stringify(updated));
        console.log(`✅ Saved ${material.type}: ${material.title}`);

        // Also save to current session using sessionManager
        if (currentSessionId) {
            const currentSession = sessionManager.getSession(currentSessionId);
            if (currentSession) {
                const sessionMaterials = currentSession.generatedMaterials || [];
                const updatedSession: ChatSession = {
                    ...currentSession,
                    generatedMaterials: [...sessionMaterials, material],
                    lastModified: Date.now()
                };

                // Update through sessionManager (handles localStorage and sync)
                sessionManager.updateSession(currentSessionId, updatedSession);

                // Update local state to reflect changes
                const updatedSessions = displayedSessions.map(s =>
                    s.id === currentSessionId ? updatedSession : s
                );
                setDisplayedSessions(updatedSessions);

                // Update modal if open
                if (selectedSessionForModal?.id === currentSessionId) {
                    setSelectedSessionForModal(updatedSession);
                }

                // Force immediate save to ensure persistence
                sessionManager.forceSave();

                // Sync session to Firebase (which now includes generatedMaterials)
                if (user?.id && firebase.isInitialized && connectionManager.isOnline()) {
                    debouncedSaveSession(updatedSession);
                }

                console.log(`📎 Material linked to session: ${currentSessionId}`);
            }
        }

        // Save to Firebase if user is logged in
        if (user?.id && firebase.isInitialized) {
            saveGeneratedMaterial(material).catch(err =>
                console.error('Failed to save material to Firebase:', err)
            );
        }
    }, [generatedMaterials, user, firebase.isInitialized, currentSessionId, displayedSessions, selectedSessionForModal, debouncedSaveSession]);

    const loadMaterial = useCallback((materialId: string) => {
        // Try to find in global state first, then in the currently open session modal
        let material = generatedMaterials.find(m => m.id === materialId);

        if (!material && selectedSessionForModal) {
            material = selectedSessionForModal.generatedMaterials?.find(m => m.id === materialId);
        }

        if (!material) {
            console.warn(`[loadMaterial] Could not find material with ID: ${materialId}`);
            return;
        }

        // Update last accessed time
        const updated = generatedMaterials.map(m =>
            m.id === materialId ? { ...m, lastAccessed: Date.now() } : m
        );

        // Only update global state if we found it there
        if (generatedMaterials.find(m => m.id === materialId)) {
            setGeneratedMaterials(updated);
            localStorage.setItem('ledger_generated_materials', JSON.stringify(updated));
        }

        // Load the material based on type
        if (material.type === 'quiz' || material.type === 'flashcards') {
            setQuizData(material.data);
            setIsQuizActive(true);

            // Close session modal if open to show the quiz
            setSessionContentModalOpen(false);
        } else if (material.type === 'exam') {
            setExamData(material.data);
            setIsExamActive(true);
            setSessionContentModalOpen(false);
        }

        console.log(`📂 Loaded ${material.type}: ${material.title}`);
    }, [generatedMaterials, selectedSessionForModal]);

    const deleteMaterial = useCallback((materialId: string) => {
        const updated = generatedMaterials.filter(m => m.id !== materialId);
        setGeneratedMaterials(updated);
        localStorage.setItem('ledger_generated_materials', JSON.stringify(updated));

        // Also remove from current session using sessionManager
        if (currentSessionId) {
            const currentSession = sessionManager.getSession(currentSessionId);
            if (currentSession && currentSession.generatedMaterials) {
                const updatedSession: ChatSession = {
                    ...currentSession,
                    generatedMaterials: currentSession.generatedMaterials.filter(m => m.id !== materialId),
                    lastModified: Date.now()
                };

                // Update through sessionManager
                sessionManager.updateSession(currentSessionId, updatedSession);

                // Update local state
                const updatedSessions = displayedSessions.map(s =>
                    s.id === currentSessionId ? updatedSession : s
                );
                setDisplayedSessions(updatedSessions);

                if (selectedSessionForModal?.id === currentSessionId) {
                    setSelectedSessionForModal(updatedSession);
                }

                // Force immediate save
                sessionManager.forceSave();

                // Sync session to Firebase
                if (user?.id && firebase.isInitialized && connectionManager.isOnline()) {
                    debouncedSaveSession(updatedSession);
                }
            }
        }

        console.log(`🗑️ Deleted material: ${materialId}`);
    }, [generatedMaterials, currentSessionId, displayedSessions, selectedSessionForModal, user, firebase.isInitialized, debouncedSaveSession]);

    // LUCA's Quest Handlers
    const handleAcceptQuest = useCallback(async () => {
        if (!activeQuest || isGeneratingQuest) return;

        setIsGeneratingQuest(true);
        showToast('info', '🎯 Generating quest questions...');

        try {
            // Generate the prompt for questions
            const prompt = lucasQuestService.generateQuestPrompt(activeQuest);

            // Use Gemini to generate questions
            const model = config?.freeModel || 'gemini-2.5-flash-lite';
            const response = await sendMessageToGemini(
                [], // No history needed
                prompt,
                [],
                model,
                false, // Not premium
                false  // No escalation
            );

            // Parse JSON response
            let questions: any[] = [];
            try {
                // Extract JSON from response
                const jsonMatch = response.text.match(/\{[\s\S]*"questions"[\s\S]*\}/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    questions = parsed.questions || [];
                }
            } catch (parseError) {
                console.error('[LucasQuest] Failed to parse questions:', parseError);
                // Generate simple fallback questions
                questions = Array.from({ length: Math.min(activeQuest.itemCount, 10) }, (_, i) => ({
                    id: i + 1,
                    type: 'multipleChoice',
                    question: `Question ${i + 1} about ${activeQuest.topics.mainTopic}`,
                    options: { A: 'Option A', B: 'Option B', C: 'Option C', D: 'Option D' },
                    correctAnswer: 'A',
                    explanation: 'This is the correct answer.'
                }));
            }

            if (questions.length === 0) {
                throw new Error('No questions generated');
            }

            setQuestQuestions(questions);
            setIsQuestInterfaceOpen(true);
            lucasQuestService.acceptQuest(activeQuest.sessionId);
            showToast('success', `🎯 Quest started with ${questions.length} questions!`);
        } catch (error) {
            console.error('[LucasQuest] Failed to generate questions:', error);
            showToast('error', 'Failed to generate quest questions. Try again later.');
            setActiveQuest(null);
        } finally {
            setIsGeneratingQuest(false);
        }
    }, [activeQuest, isGeneratingQuest, config?.freeModel, showToast]);

    const handleDeclineQuest = useCallback(() => {
        if (!activeQuest) return;
        lucasQuestService.cancelQuest(activeQuest.sessionId);
        setActiveQuest(null);
        showToast('info', 'Quest declined');
    }, [activeQuest, showToast]);

    const handleCompleteQuest = useCallback((result: { questId: string; score: number; totalItems: number; difficulty: string; tokensRewarded: number; bonusTokens: number; perfectBonus: boolean; streakBonus: number }) => {
        if (!user) return;

        try {
            // Update user tokens (add tokens as reward - reduce usage)
            const updatedUser = {
                ...user,
                dailyTokenUsage: Math.max(0, (user.dailyTokenUsage || 0) - result.tokensRewarded)
            };
            setUser(updatedUser);
            localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));

            // Update Firebase
            if (firebase.db && firebase.isInitialized) {
                import('firebase/firestore').then(({ doc, updateDoc }) => {
                    const userRef = doc(firebase.db!, 'users', user.id);
                    updateDoc(userRef, {
                        dailyTokenUsage: updatedUser.dailyTokenUsage
                    }).catch(err => console.error('Failed to update Firebase:', err));
                });
            }

            // Show reward message
            const rewardMsg = `🏆 Quest Complete! Earned ${result.tokensRewarded} tokens! ${result.perfectBonus ? '🌟 Perfect score bonus!' : ''}`;
            setQuestRewardMessage(rewardMsg);
            showToast('success', rewardMsg);

            // Reset quest state
            setIsQuestInterfaceOpen(false);
            setActiveQuest(null);
            setQuestQuestions([]);

            // Clear reward message after 5 seconds
            setTimeout(() => setQuestRewardMessage(''), 5000);
        } catch (error) {
            console.error('Error completing quest:', error);
            showToast('error', 'Failed to complete quest');
        }
    }, [user, showToast, firebase.db, firebase.isInitialized]);

    // Pin/Unpin message handler - FIXED: proper dependencies for correct closure values
    const handleTogglePin = useCallback((messageId: string) => {
        // Guest restriction - check user state properly
        if (!user) {
            showToast('warning', 'Please create an account to pin messages');
            setShowGuestLimitModal(true);
            return;
        }

        // Allow non-anonymous users to pin (isAnonymous check was too restrictive)
        if (user.isAnonymous === true) {
            showToast('warning', 'Please log in or create an account to pin messages');
            setShowGuestLimitModal(true);
            return;
        }

        if (!currentSessionId) {
            console.warn('[Pin] No current session');
            return;
        }

        // Get session from sessionManager (source of truth)
        const currentSession = sessionManager.getSession(currentSessionId);
        if (!currentSession) {
            console.warn('[Pin] Session not found:', currentSessionId);
            return;
        }

        const pinnedMessages = (currentSession.pinnedMessages || []) as string[];
        const isPinned = pinnedMessages.includes(messageId);

        const updatedPinnedMessages = isPinned
            ? pinnedMessages.filter(id => id !== messageId)
            : [...pinnedMessages, messageId];

        const updatedSession: ChatSession = {
            ...currentSession,
            pinnedMessages: updatedPinnedMessages,
            lastModified: Date.now()
        };

        // Update through sessionManager (handles localStorage and sync)
        sessionManager.updateSession(currentSessionId, updatedSession);

        // Update local state to reflect changes
        setDisplayedSessions(prev => prev.map(s => s.id === currentSessionId ? updatedSession : s));
        setAllSessions(prev => prev.map(s => s.id === currentSessionId ? updatedSession : s));

        // Update selectedSessionForModal if it's the current session
        if (selectedSessionForModal?.id === currentSessionId) {
            setSelectedSessionForModal(updatedSession);
        }

        // Force immediate save to ensure persistence
        sessionManager.forceSave();

        // Show feedback to user
        showToast('success', isPinned ? 'Message unpinned' : 'Message pinned');

        setTimeout(() => {
            const element = document.getElementById(`message-${messageId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);
    }, [user, currentSessionId, selectedSessionForModal?.id, showToast]);

    // Handle jump to message (for search and pinned messages)
    const handleJumpToMessage = useCallback((messageId: string) => {
        const element = document.getElementById(`message-${messageId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Highlight briefly
            element.style.backgroundColor = isDarkMode ? 'rgba(251, 191, 36, 0.2)' : 'rgba(251, 191, 36, 0.3)';
            setTimeout(() => {
                element.style.backgroundColor = '';
            }, 2000);
        }
    }, [isDarkMode]);

    // Handle export message to document
    const handleExportMessage = useCallback((message: Message) => {
        setExportTargetMessage(message);
        setIsExportModalOpen(true);
    }, []);

    // Handle compiled export (from header or sidebar)
    const handleCompiledExport = useCallback(() => {
        setExportTargetMessage(undefined); // No target = compiled mode
        setIsExportModalOpen(true);
    }, []);

    const handleAddAnnotation = () => {
        if (!activePdf || !annotationText.trim()) return;

        const newAnnotation: PdfAnnotation = {
            id: Date.now().toString(),
            text: annotationText,
            page: pdfPage,
            x: 0,
            y: 0,
            timestamp: new Date().toISOString()
        };

        const updatedMessages = messages.map(msg => {
            if (!msg.attachments) return msg;
            const attIndex = msg.attachments.findIndex(a => a.name === activePdf.name);
            if (attIndex === -1) return msg;

            const updatedAtts = [...msg.attachments];
            updatedAtts[attIndex] = {
                ...updatedAtts[attIndex],
                annotations: [...(updatedAtts[attIndex].annotations || []), newAnnotation]
            };

            return { ...msg, attachments: updatedAtts };
        });

        setActivePdf(prev => prev ? ({ ...prev, annotations: [...(prev.annotations || []), newAnnotation] }) : null);
        updateCurrentSession(updatedMessages);
        setAnnotationText('');
        setIsAddingNote(false);
    };


    const handleGenerateQuiz = async (config: QuizConfig) => {
        // Check for token balance
        if (user && !hasUnlimitedTokens(user)) {
            const quizEstimate = estimateQuizTokens(config.itemCount, config.difficulty, user);
            const estimatedTokens = quizEstimate.total;
            const currentBalance = user.tokenBalance ?? ((user.isMax || user.accountTier === 'max') ? 50000 : user.isPremium ? 15000 : 2000);

            if (currentBalance < estimatedTokens) {
                setLowBalanceNeeded(estimatedTokens);
                setIsLowBalanceModalOpen(true);
                return;
            }
        }

        setIsQuizConfigOpen(false);
        setIsGeneratingQuiz(true);
        setIsAiMenuOpen(false);
        setQuizProgress({ percent: 0, status: 'Initializing quiz generation...' });

        // Add a system message to show activity
        const systemMsg: Message = {
            id: Date.now().toString(),
            role: Role.MODEL,
            content: `Generating a ${config.difficulty} ${config.type} quiz for: **${config.topic}**...`,
            thoughtProcess: '',
            timestamp: new Date().toISOString(),
            isStreaming: true
        };

        // Initial update to add the system message
        const newMessages = [...messages, systemMsg];
        updateCurrentSession(newMessages);

        // Simulate progress updates with more granularity
        const progressSteps = [
            { percent: 5, status: 'Initializing quiz parameters...' },
            { percent: 15, status: 'Analyzing topic depth...' },
            { percent: 30, status: 'Drafting conceptual questions...' },
            { percent: 45, status: 'Formulating computational problems...' },
            { percent: 60, status: 'Generating plausible distractors...' },
            { percent: 75, status: 'Validating answer keys...' },
            { percent: 85, status: 'Refining explanations...' },
            { percent: 95, status: 'Finalizing quiz structure...' }
        ];

        let currentStep = 0;
        const progressInterval = setInterval(() => {
            if (currentStep < progressSteps.length) {
                setQuizProgress(progressSteps[currentStep]);
                currentStep++;
            }
        }, 1500); // Slower updates for realism (1.5s per step * 8 steps = ~12s)

        try {
            // Validate model before calling API
            const quizModel = getValidatedModelForUser(user?.isPremium || false, config?.freeModel, config?.premiumModel);

            // Extract attached file content if available
            let attachedFileContent = '';
            let contextMessages = '';

            // Check for attachments in recent messages
            const recentMessages = messages.slice(-10); // Last 10 messages
            for (const msg of recentMessages) {
                if (msg.attachments && msg.attachments.length > 0) {
                    for (const attachment of msg.attachments) {
                        if (attachment.extractedText) {
                            attachedFileContent += `\n--- ${attachment.name} ---\n${attachment.extractedText}\n`;
                        }
                    }
                }
                // Also capture message content for context
                if (msg.role === Role.USER || msg.role === Role.MODEL) {
                    contextMessages += `${msg.role === Role.USER ? 'User' : 'AI'}: ${msg.content.substring(0, 500)}\n`;
                }
            }

            console.log('[Quiz] Generating with attached content:', attachedFileContent.length > 0 ? `${attachedFileContent.length} chars` : 'None');
            console.log('[Quiz] Context messages:', contextMessages.length > 0 ? `${contextMessages.length} chars` : 'None');

            const data = await generateQuiz(
                config.topic,
                config.itemCount,
                config.difficulty,
                config.type,
                config.focus,
                quizModel,
                (thought: string) => {
                    // Use setMessages for high-frequency updates to avoid re-rendering entire app or triggering heavy saves
                    setMessages(prev => prev.map(m =>
                        m.id === systemMsg.id ? { ...m, thoughtProcess: thought } : m
                    ));
                },
                undefined, // user (not needed, already handled)
                (updates: Partial<User>) => { /* updates handled internally */ },
                attachedFileContent.length > 0 ? attachedFileContent : undefined,
                contextMessages.length > 0 ? contextMessages : undefined
            );

            clearInterval(progressInterval);
            setQuizProgress({ percent: 100, status: 'Quiz ready!' });

            // Track mastery
            if (!localStorage.getItem('luca_mastery_quiz_created')) {
                localStorage.setItem('luca_mastery_quiz_created', 'true');
            }

            // Update token usage after quiz generation - Using centralized estimation
            if (user && firebase.isInitialized) {
                // Use centralized token estimation service
                const quizEstimate = estimateQuizTokens(config.itemCount, config.difficulty, user);
                const estimatedTokens = quizEstimate.total;

                // NEW TOKEN SYSTEM: Subtract from tokenBalance
                const tierTokenLimit = user.isMax ? 50000 : user.isPremium ? 15000 : 2000;
                const currentBalance = user.tokenBalance ?? tierTokenLimit;
                const newTokenBalance = Math.max(0, currentBalance - estimatedTokens);
                console.log(`[Quiz TokenEstimation] ${quizEstimate.breakdown.map(b => `${b.label}: ${b.value}`).join(', ')} = ${estimatedTokens} total`);
                const updatedUser: User = {
                    ...user,
                    tokenBalance: newTokenBalance,
                    lastActive: Date.now(),
                    // Preserve lastTokenRefresh and lastResetTime
                    lastTokenRefresh: user.lastTokenRefresh || Date.now(),
                    lastResetTime: user.lastResetTime || Date.now()
                };

                setUser(updatedUser);
                localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));

                // Update local storage users array
                const allUsers = JSON.parse(localStorage.getItem('ledger_users') || '[]');
                const dbUpdate = allUsers.map((u: User) => u.id === user.id ? updatedUser : u);
                localStorage.setItem('ledger_users', JSON.stringify(dbUpdate));

                if (firebase.db) {
                    const { doc, updateDoc } = await import('firebase/firestore');
                    const userRef = doc(firebase.db, 'users', user.id);
                    await updateDoc(userRef, {
                        tokenBalance: newTokenBalance,
                        lastActive: Date.now(),
                        lastTokenRefresh: updatedUser.lastTokenRefresh,
                        lastResetTime: updatedUser.lastResetTime
                    }).catch(err => console.error('Failed to update Firebase:', err));
                }

                if (quizEstimate.total === 0) {
                    console.log(`💎 Quiz generated - UNLIMITED (no charge)`);
                } else {
                    console.log(`Quiz generated - Tokens used: ${estimatedTokens}, Balance: ${newTokenBalance}`);
                }

                // Trigger token consumption animation
                setTokenAnimationData({ tokens: estimatedTokens, visible: true });

                // Log to history
                const historyKey = `token_history_${user.id}`;
                const history = JSON.parse(localStorage.getItem(historyKey) || '[]');
                history.push({
                    timestamp: Date.now(),
                    tokens: estimatedTokens,
                    operation: `Quiz: ${config.topic}`
                });
                if (history.length > 100) history.shift();
                localStorage.setItem(historyKey, JSON.stringify(history));
            }

            setQuizData(data);
            setIsQuizActive(true);

            // Auto-save the generated quiz FIRST (before session update)
            // This ensures the material is added to session before any overwriting
            const material: GeneratedMaterial = {
                id: Date.now().toString(),
                userId: user?.id || '',
                type: 'quiz',
                title: data.title,
                content: data,
                data: data,
                createdAt: new Date().toISOString(),
                topic: config.topic
            };
            saveMaterial(material);

            // Final update with the result using functional update to avoid stale state
            // NOTE: saveMaterial already handles session.generatedMaterials update
            setMessages(prev => {
                const updated = prev.map(m =>
                    m.id === systemMsg.id ? {
                        ...m,
                        content: `**Quiz Generated:** ${data.title}\n\n*Click "Open Quiz" to start.*`,
                        isStreaming: false,
                        quiz: { ...data, type: 'quiz' } // Store quiz data for reopening
                    } : m
                );

                // Update session with messages only - materials already handled by saveMaterial
                setTimeout(() => {
                    const existingSession = sessionManager.getSession(currentSessionId || '');
                    if (existingSession) {
                        const updatedSession: ChatSession = {
                            ...existingSession,
                            messages: updated,
                            updatedAt: new Date().toISOString(),
                            lastModified: Date.now()
                        };
                        sessionManager.updateSession(currentSessionId || '', updatedSession);
                        if (user && !user.isAnonymous && connectionManager.isOnline()) {
                            debouncedSaveSession(updatedSession);
                        }
                    }
                }, 0);

                return updated;
            });
        } catch (e) {
            clearInterval(progressInterval);
            console.error(e);
            const errorMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: Role.MODEL,
                content: "Sorry, I failed to generate a quiz. Please try again.",
                timestamp: new Date().toISOString(),
                isError: true
            };
            updateCurrentSession([...newMessages, errorMsg]);
        } finally {
            setIsGeneratingQuiz(false);
            setQuizProgress({ percent: 0, status: '' });
        }
    };

    const handleGenerateMockExam = async (config: MockExamConfig) => {
        // Check for token balance
        if (user && !hasUnlimitedTokens(user)) {
            const examEstimate = estimateExamTokens(config.itemCount, config.difficulty, user);
            const estimatedTokens = examEstimate.total;
            const currentBalance = user.tokenBalance ?? ((user.isMax || user.accountTier === 'max') ? 50000 : user.isPremium ? 15000 : 2000);

            if (currentBalance < estimatedTokens) {
                setLowBalanceNeeded(estimatedTokens);
                setIsLowBalanceModalOpen(true);
                return;
            }
        }

        setIsMockExamConfigOpen(false);
        setIsGeneratingQuiz(true);
        setIsAiMenuOpen(false);
        setExamProgress({ percent: 0, status: 'Initializing exam generation...' });

        const systemMsg: Message = {
            id: Date.now().toString(),
            role: Role.MODEL,
            content: `Generating a ${config.difficulty} Mock Board Exam for: **${config.subject}**...`,
            thoughtProcess: '',
            timestamp: new Date().toISOString(),
            isStreaming: true
        };

        const newMessages = [...messages, systemMsg];
        updateCurrentSession(newMessages);

        // Simulate progress updates with high granularity for exams
        const progressSteps = [
            { percent: 5, status: 'Initializing secure exam environment...' },
            { percent: 10, status: 'Analyzing subject syllabus...' },
            { percent: 20, status: 'Reviewing CPA standards & regulations...' },
            { percent: 30, status: 'Drafting theory questions...' },
            { percent: 40, status: 'Formulating computational problems...' },
            { percent: 50, status: 'Creating scenario-based items...' },
            { percent: 60, status: 'Generating complex distractors...' },
            { percent: 70, status: 'Validating solutions against standards...' },
            { percent: 80, status: 'Writing comprehensive explanations...' },
            { percent: 90, status: 'Structuring exam format...' },
            { percent: 95, status: 'Final quality check...' },
            { percent: 98, status: 'Preparing exam interface...' }
        ];

        let currentStep = 0;
        const progressInterval = setInterval(() => {
            if (currentStep < progressSteps.length) {
                setExamProgress(progressSteps[currentStep]);
                currentStep++;
            }
        }, 2000); // Faster progress updates (reduced from 3s for quicker perceived generation)

        try {
            // Validate model before calling API
            const examModel = getValidatedModelForUser(user?.isPremium || false, config?.freeModel, config?.premiumModel);

            const data = await generateMockExam(
                config.subject,
                config.itemCount,
                config.difficulty,
                config.focus,
                examModel,
                (thought: string) => {
                    setMessages(prev => prev.map(m =>
                        m.id === systemMsg.id ? { ...m, thoughtProcess: thought } : m
                    ));
                },
                user || undefined,
                (updates: Partial<User>) => setUser(prev => prev ? { ...prev, ...updates } : prev),
                // Essay options
                {
                    questionFormat: config.questionFormat,
                    essayCount: config.essayCount,
                    essayConfig: config.essayConfig
                }
            );

            clearInterval(progressInterval);
            setExamProgress({ percent: 100, status: 'Exam ready!' });

            // Update token usage after mock exam generation - Using centralized estimation
            if (user && firebase.isInitialized) {
                // Use centralized token estimation service
                const examEstimate = estimateExamTokens(config.itemCount, config.difficulty, user);
                const estimatedTokens = examEstimate.total;

                // NEW TOKEN SYSTEM: Subtract from tokenBalance
                const tierTokenLimit = user.isMax ? 50000 : user.isPremium ? 15000 : 2000;
                const currentBalance = user.tokenBalance ?? tierTokenLimit;
                const newTokenBalance = Math.max(0, currentBalance - estimatedTokens);
                console.log(`[Exam TokenEstimation] ${examEstimate.breakdown.map(b => `${b.label}: ${b.value}`).join(', ')} = ${estimatedTokens} total`);
                const updatedUser: User = {
                    ...user,
                    tokenBalance: newTokenBalance,
                    lastActive: Date.now(),
                    // Preserve lastTokenRefresh and lastResetTime
                    lastTokenRefresh: user.lastTokenRefresh || Date.now(),
                    lastResetTime: user.lastResetTime || Date.now()
                };

                setUser(updatedUser);
                localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));

                // Update local storage users array
                const allUsers = JSON.parse(localStorage.getItem('ledger_users') || '[]');
                const dbUpdate = allUsers.map((u: User) => u.id === user.id ? updatedUser : u);
                localStorage.setItem('ledger_users', JSON.stringify(dbUpdate));

                if (firebase.db) {
                    const { doc, updateDoc } = await import('firebase/firestore');
                    const userRef = doc(firebase.db, 'users', user.id);
                    await updateDoc(userRef, {
                        tokenBalance: newTokenBalance,
                        lastActive: Date.now(),
                        lastTokenRefresh: updatedUser.lastTokenRefresh,
                        lastResetTime: updatedUser.lastResetTime
                    }).catch(err => console.error('Failed to update Firebase:', err));
                }

                if (examEstimate.total === 0) {
                    console.log(`💎 Exam generated - UNLIMITED (no charge)`);
                } else {
                    console.log(`Exam generated - Tokens used: ${estimatedTokens}, Balance: ${newTokenBalance}`);
                }

                console.log(`Mock exam generated - Tokens used: ${estimatedTokens}, Balance: ${newTokenBalance}`);

                // Trigger token consumption animation
                setTokenAnimationData({ tokens: estimatedTokens, visible: true });

                // Log to history
                const historyKey = `token_history_${user.id}`;
                const history = JSON.parse(localStorage.getItem(historyKey) || '[]');
                history.push({
                    timestamp: Date.now(),
                    tokens: estimatedTokens,
                    operation: `Mock Exam: ${config.subject}`
                });
                if (history.length > 100) history.shift();
                localStorage.setItem(historyKey, JSON.stringify(history));
            }

            setExamData(data);
            setIsExamActive(true);

            // Auto-save the generated exam FIRST (before session update)
            // This ensures the material is added to session before any overwriting
            const material: GeneratedMaterial = {
                id: Date.now().toString(),
                userId: user?.id || '',
                type: 'exam',
                title: data.title,
                content: data,
                data: data,
                createdAt: new Date().toISOString(),
                topic: config.subject
            };
            saveMaterial(material);

            // Final update using functional update to avoid stale closures
            // NOTE: saveMaterial already handles session.generatedMaterials update
            setMessages(prev => {
                const finalMessages = prev.map(m =>
                    m.id === systemMsg.id ? {
                        ...m,
                        content: `**Mock Exam Generated:** ${data.title}\n\n*Click "Open Exam" to start.*`,
                        isStreaming: false,
                        quiz: { ...data, type: 'exam' } // Store exam data for reopening
                    } : m
                );

                // Update session with messages only - materials already handled by saveMaterial
                setTimeout(() => {
                    const existingSession = sessionManager.getSession(currentSessionId || '');
                    if (existingSession) {
                        const updatedSession: ChatSession = {
                            ...existingSession,
                            messages: finalMessages,
                            updatedAt: new Date().toISOString(),
                            lastModified: Date.now()
                        };
                        sessionManager.updateSession(currentSessionId || '', updatedSession);
                        if (user && !user.isAnonymous && connectionManager.isOnline()) {
                            debouncedSaveSession(updatedSession);
                        }
                    }
                }, 0);

                return finalMessages;
            });
        } catch (e) {
            clearInterval(progressInterval);
            console.error(e);
            const errorMsg: Message = {
                id: (Date.now() + 1).toString(),
                role: Role.MODEL,
                content: "Sorry, I failed to generate the mock exam. Please try again.",
                timestamp: new Date().toISOString(),
                isError: true
            };
            updateCurrentSession([...newMessages, errorMsg]);
        } finally {
            setIsGeneratingQuiz(false);
            setExamProgress({ percent: 0, status: '' });
        }
    };

    // State for post-quiz/exam review offer
    const [showReviewOffer, setShowReviewOffer] = useState(false);
    const [reviewData, setReviewData] = useState<{
        incorrectAnswers: any[];
        totalQuestions: number;
        score: number;
        quizType: 'quiz' | 'exam';
        quizTitle?: string;
    } | null>(null);

    const handleExamSubmit = async (answers: Record<string, string>, essayScores?: Record<string, any>) => {
        // Calculate score for MC questions
        let mcScore = 0;
        const incorrectAnswers: any[] = [];

        examData.questions.forEach((q: any) => {
            if (q.type === 'essay') {
                // Essay grading handled separately via essayScores
                return;
            }
            const correct = q.options?.find((o: any) => o.isCorrect)?.id;
            if (answers[q.id] === correct) {
                mcScore++;
            } else if (answers[q.id]) {
                // Track incorrect MC answers
                const userOption = q.options?.find((o: any) => o.id === answers[q.id]);
                const correctOption = q.options?.find((o: any) => o.isCorrect);
                incorrectAnswers.push({
                    questionId: q.id,
                    questionText: q.text,
                    topic: q.topic || 'General',
                    userAnswer: userOption?.text,
                    correctAnswer: correctOption?.text || 'N/A',
                    explanation: correctOption?.explanation || '',
                    type: q.type || 'multiple_choice'
                });
            }
        });

        // Count essay questions separately
        const essayQuestions = examData.questions.filter((q: any) => q.type === 'essay');
        const mcQuestions = examData.questions.filter((q: any) => q.type !== 'essay');

        const total = mcQuestions.length;
        const percentage = total > 0 ? Math.round((mcScore / total) * 100) : 0;

        let feedback = "";
        if (percentage >= 75) feedback = "Excellent! You passed the board exam simulation.";
        else if (percentage >= 60) feedback = "Good effort, but you need more practice to pass.";
        else feedback = "Keep studying! Focus on your weak areas.";

        let resultContent = `**Mock Exam Results**\n\n`;
        resultContent += `**Multiple Choice Score:** ${mcScore}/${total} (${percentage}%)\n\n`;

        if (essayQuestions.length > 0) {
            resultContent += `**Essay Questions:** ${essayQuestions.length} (graded separately)\n\n`;
        }

        resultContent += feedback;

        const resultMsg: Message = {
            id: Date.now().toString(),
            role: Role.MODEL,
            content: resultContent,
            timestamp: new Date().toISOString()
        };
        updateCurrentSession([...messages, resultMsg]);
        setIsExamActive(false);

        // Show review offer if there are incorrect answers
        if (incorrectAnswers.length > 0) {
            setReviewData({
                incorrectAnswers,
                totalQuestions: total,
                score: mcScore,
                quizType: 'exam',
                quizTitle: examData.title
            });
            setShowReviewOffer(true);
        }

        // ==================== GAMIFICATION TRACKING ====================
        try {
            // Track quiz/exam completion
            trackQuizComplete(percentage >= 75, percentage);

            // Award XP based on score
            // Base XP: 50 for exam
            // Bonus: +2 per correct answer
            const baseXP = 50;
            const scoreBonus = mcScore * 2;
            const totalXP = baseXP + scoreBonus;

            const xpResult = awardXP(totalXP, 'quiz_complete');
            if (xpResult.xpGained > 0) {
                setXpGainAnimation({ amount: xpResult.xpGained, visible: true });
                setTimeout(() => setXpGainAnimation({ amount: 0, visible: false }), 2500);
            }

            // Check for level up
            if (xpResult.leveledUp && xpResult.newLevel) {
                // Show Level Up Modal
                setLevelUpData({
                    oldLevel: (user?.level || 1),
                    newLevel: xpResult.newLevel,
                    rewards: {
                        tokens: xpResult.levelUpReward?.tokens || 0,
                        unlocks: xpResult.newLevel.perks || []
                    }
                });

                showToast('success', `🎉 Level Up! You're now ${xpResult.newLevel.title}!`);
                if (xpResult.levelUpReward && xpResult.levelUpReward.tokens > 0) {
                    showToast('success', `🎁 Level up bonus: +${xpResult.levelUpReward.tokens} tokens!`);
                }
            }

            // Update local user state with new XP and Level
            if (user) {
                setUser(prev => prev ? ({
                    ...prev,
                    totalXP: xpResult.newTotalXP,
                    level: xpResult.newLevel ? xpResult.newLevel.level : prev.level
                }) : null);
            }

            // Save Mock Exam Result
            if (user) {
                saveMockExamResult(user.id, {
                    id: crypto.randomUUID(),
                    subject: examData.title || 'Mock Exam',
                    score: mcScore,
                    totalQuestions: total,
                    percentage: percentage,
                    date: Date.now(),
                    timeTaken: 0 // Tracking not yet implemented in App.tsx
                }).catch(e => console.error('Failed to save mock exam result:', e));
            }

            // MASTERY PATH: Award mock exam mastery bonus
            try {
                const { awardQuizMasteryBonus, checkMasteryMilestones } = await import('./services/masteryPathService');
                const diff = (examData.difficulty || 'hard') as 'easy' | 'medium' | 'hard' | 'expert';
                const mockBonusResult = awardQuizMasteryBonus(percentage, total, diff, true);
                if (mockBonusResult.awarded) {
                    showToast('info', `+${mockBonusResult.pointsAwarded} Mastery Points from mock exam`);
                    if (mockBonusResult.thresholdReached) {
                        setTimeout(() => showToast('success', 'Mastery threshold reached! You can now take the topic exam.'), 1500);
                    }
                    const milestones = checkMasteryMilestones();
                    for (const m of milestones.newMilestones) {
                        setTimeout(() => showToast('success', `Milestone: ${m.title} — +${m.reward.tokens} tokens, +${m.reward.xp} XP`), 2500);
                    }
                }
            } catch (e) { console.error('[MasteryPath] Mock exam bonus error:', e); }

            // Check for new achievements
            const newAchievements = checkAchievements();
            if (newAchievements.length > 0) {
                setAchievementUnlocks(newAchievements);
            }

            // Check milestones
            checkMilestones();

        } catch (gamificationError) {
            console.error('[Gamification] Tracking error:', gamificationError);
        }
    };

    // Handle starting a review session after quiz/exam
    const handleStartReview = (topics: string[], incorrectAnswers: any[]) => {
        setShowReviewOffer(false);

        // Generate a review prompt
        const topicSummary = topics.map(topic => {
            const topicAnswers = incorrectAnswers.filter(a => a.topic === topic);
            return `### ${topic}\n${topicAnswers.map((a, i) =>
                `**Q${i + 1}:** ${a.questionText}\n**Correct:** ${a.correctAnswer}\n**Why:** ${a.explanation}`
            ).join('\n\n')}`;
        }).join('\n\n');

        const reviewPrompt = `I just completed a quiz/exam and got some questions wrong. Please help me review these topics:

${topicSummary}

Please:
1. Identify common themes in my mistakes
2. Explain the key concepts I need to understand
3. Provide clear explanations with examples
4. Give me tips to remember these concepts for the board exam`;

        // Set the input and send it
        setInputValue(reviewPrompt);
        setTimeout(() => {
            const fakeEvent = { key: 'Enter', preventDefault: () => { } } as React.KeyboardEvent;
            // Trigger send
            processMessage(reviewPrompt, [], false);
            setInputValue('');
        }, 100);

        setReviewData(null);
    };

    if (showAdminPanel) {
        return (
            <React.Suspense fallback={
                <div className="flex ios-h-screen items-center justify-center bg-slate-100">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-slate-600 font-medium">Loading Admin Dashboard...</p>
                    </div>
                </div>
            }>
                <AdminDashboard
                    sessions={allSessions}
                    onLogout={handleLogout}
                    onPromoCodesUpdate={(updatedPromoCodes) => {
                        setPromoCodes(updatedPromoCodes);
                        localStorage.setItem('ledger_promos', JSON.stringify(updatedPromoCodes));
                        // Trigger storage event manually for same tab
                        window.dispatchEvent(new StorageEvent('storage', {
                            key: 'ledger_promos',
                            newValue: JSON.stringify(updatedPromoCodes),
                            url: window.location.href,
                            storageArea: localStorage
                        }));
                    }}
                />
            </React.Suspense>
        );
    }



    // Show maintenance screen for non-admin users when maintenance mode is enabled
    if (config?.maintenanceMode && user?.role !== 'admin') {
        return (
            <React.Suspense fallback={<LoadingScreen isDarkMode={false} />}>
                <MaintenanceScreen
                    maintenanceETA={config?.maintenanceETA}
                    maintenanceMessage={config?.maintenanceMessage}
                />
            </React.Suspense>
        );
    }

    // Show landing page for unauthenticated visitors
    if (showLandingPage && !user && !isInitialLoading) {
        const handleGetStarted = () => {
            // Dismiss landing page and show the main app (guest mode)
            setShowLandingPage(false);
            localStorage.setItem('luca_landing_dismissed', 'true');
        };

        const handleLogin = () => {
            // Dismiss landing page and open auth modal
            setShowLandingPage(false);
            localStorage.setItem('luca_landing_dismissed', 'true');
            setAuthMode('login');
            setIsAuthModalOpen(true);
        };

        return (
            <React.Suspense fallback={<LoadingScreen isDarkMode={false} />}>
                <LandingPageV2
                    onGetStarted={handleGetStarted}
                    onLogin={handleLogin}
                />
            </React.Suspense>
        );
    }













    return (
        <div className={`
            fixed top-0 left-0 w-full ios-h-screen flex flex-col md:flex-row overflow-hidden font-sans
            ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}
        `.trim().replace(/\s+/g, ' ')}>

            <FirebaseMigration onComplete={() => { }} />

            {/* Force Update Checker - DISABLED due to infinite reload loop */}
            {/* <ForceUpdateChecker /> */}

            {/* Token Consumption Animation */}
            <TokenConsumptionAnimation
                tokensConsumed={tokenAnimationData.tokens}
                isVisible={tokenAnimationData.visible}
                onComplete={() => setTokenAnimationData({ tokens: 0, visible: false })}
            />

            {/* Smart Recommendation Popup */}
            {activeRecommendation && (
                <SmartRecommendationPopup
                    recommendation={activeRecommendation}
                    onAction={handleRecommendationAction}
                    onDismiss={handleRecommendationDismiss}
                    isDarkMode={isDarkMode}
                />
            )}

            {/* WelcomeModal removed - now integrated into UnifiedOnboarding */}

            {user && (
                <ExtendPremiumModal
                    isOpen={isExtendPremiumOpen}
                    onClose={() => setIsExtendPremiumOpen(false)}
                    user={user}
                    config={config}
                    onExtend={(months, cost) => {
                        // Automatic Update Workflow
                        if (user) {
                            const now = new Date();
                            let currentExpiry = user.premiumExpiryDate ? new Date(user.premiumExpiryDate) : now;
                            if (currentExpiry < now) currentExpiry = now;

                            const newExpiry = new Date(currentExpiry);
                            newExpiry.setMonth(newExpiry.getMonth() + months);

                            const updatedUser = {
                                ...user,
                                isPremium: true,
                                premiumExpiryDate: newExpiry.toISOString()
                            };

                            setUser(updatedUser);
                            localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));

                            // Update in "DB" local cache
                            const allUsers = JSON.parse(localStorage.getItem('ledger_users') || '[]');
                            const dbUpdate = allUsers.map((u: User) => u.id === user.id ? updatedUser : u);
                            localStorage.setItem('ledger_users', JSON.stringify(dbUpdate));
                        }
                        setIsExtendPremiumOpen(false);
                    }}
                />
            )}

            {/* Customer Support Modal */}
            {user && (
                <CustomerSupportModal
                    isOpen={isSupportOpen}
                    onClose={() => setIsSupportOpen(false)}
                    user={user}
                    config={config}
                    isDarkMode={isDarkMode}
                    showToast={showToast}
                />
            )}

            {/* Feedback Modal - Detailed feedback collection with token refunds */}
            <FeedbackModal
                isOpen={showFeedbackModal}
                onClose={() => setShowFeedbackModal(false)}
                messageId={feedbackMessageId}
                messageContent={feedbackMessageContent}
                feedbackType={feedbackType}
                tokensCharged={feedbackTokensCharged}
                isDarkMode={isDarkMode}
                onSubmit={handleDetailedFeedbackSubmit}
            />

            {/* Token Purchase Modal - Unified payment system for tokens, unlimited, and premium */}
            {user && (
                <TokenPurchaseModal
                    {...commonStoreProps}
                    isOpen={isTokenPurchaseOpen}
                    onClose={() => { setIsTokenPurchaseOpen(false); setShowLowTokenPrompt(false); setTokenPurchaseInitialTab('tokens'); }}
                    initialTab={tokenPurchaseInitialTab}
                />
            )}

            <AuthModal
                isOpen={isAuthModalOpen}
                onClose={() => { setIsAuthModalOpen(false); setTimeout(() => inputRef.current?.focus(), 100); }}
                onLogin={(u) => { handleLogin(u); setIsAuthModalOpen(false); }}
                onShowToast={showToast}
                initialMode={authMode}
            />

            <UpgradeModal
                isOpen={isUpgradeModalOpen}
                onClose={() => { setIsUpgradeModalOpen(false); setTimeout(() => inputRef.current?.focus(), 100); }}
                config={config}
                promoCodes={promoCodes}
                user={user}
                showToast={showToast}
                onPromoCodesUpdate={(updatedCodes) => {
                    setPromoCodes(updatedCodes);
                    localStorage.setItem('ledger_promos', JSON.stringify(updatedCodes));
                }}
                onUpgradeUser={(upgradedUser) => {
                    setUser(upgradedUser);
                    // Update in localStorage
                    const storedUsers = localStorage.getItem('ledger_users');
                    if (storedUsers) {
                        const users = JSON.parse(storedUsers);
                        const updatedUsers = users.map((u: User) =>
                            u.id === upgradedUser.id ? upgradedUser : u
                        );
                        localStorage.setItem('ledger_users', JSON.stringify(updatedUsers));
                    }
                    localStorage.setItem('ledger_current_user', JSON.stringify(upgradedUser));
                }}
            />

            <SocialModal
                isOpen={isSocialModalOpen}
                onClose={() => setIsSocialModalOpen(false)}
                user={user}
                isDarkMode={isDarkMode}
                config={config}
                onOpenDirectMessage={handleOpenDirectMessage}
                initialTab={socialInitialTab}
            />

            <UsageModal
                isOpen={isUsageModalOpen}
                onClose={() => setIsUsageModalOpen(false)}
                user={user}
                config={config}
                isDarkMode={isDarkMode}
                onUpgrade={() => setIsTokenPurchaseOpen(true)}
            />

            {/* Post Quiz/Exam Review Offer */}
            {reviewData && (
                <PostQuizReviewOffer
                    isOpen={showReviewOffer}
                    onClose={() => { setShowReviewOffer(false); setReviewData(null); }}
                    onStartReview={handleStartReview}
                    incorrectAnswers={reviewData.incorrectAnswers}
                    totalQuestions={reviewData.totalQuestions}
                    score={reviewData.score}
                    quizType={reviewData.quizType}
                    quizTitle={reviewData.quizTitle}
                    isDarkMode={isDarkMode}
                />
            )}

            {isCostConfirmationOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 border border-slate-200">
                        <div className="flex items-center gap-3 mb-4 text-amber-600">
                            <div className="p-2 bg-amber-100 rounded-lg">
                                <Activity size={24} />
                            </div>
                            <h3 className="text-lg font-bold text-slate-800">Complex Request Detected</h3>
                        </div>

                        <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                            This request requires advanced reasoning and will use a more powerful AI model.
                        </p>

                        <div className="bg-slate-50 rounded-lg p-4 mb-6 space-y-3 border border-slate-200">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-slate-500 uppercase">Estimated Cost</span>
                                <span className="text-sm font-bold text-slate-900">~{estimatedCost.tokens} Tokens</span>
                            </div>
                            <div className="w-full bg-slate-200 h-1.5 rounded-full overflow-hidden">
                                <div className="bg-amber-500 h-full rounded-full" style={{ width: `${Math.min((estimatedCost.tokens / estimatedCost.limit) * 100, 100)}%` }}></div>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-medium text-slate-500 uppercase">Your Daily Limit</span>
                                <span className="text-sm font-bold text-slate-900">{estimatedCost.limit.toLocaleString()} Tokens</span>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsCostConfirmationOpen(false)}
                                className="flex-1 py-2.5 text-slate-600 font-bold hover:bg-slate-100 rounded-xl transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmSendMessage}
                                className="flex-1 py-2.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 shadow-lg shadow-slate-900/20 transition-colors"
                            >
                                Proceed
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isRefreshModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95">
                        <h3 className="text-lg font-bold text-slate-800 mb-4">Refresh Session Context</h3>
                        <p className="text-sm text-slate-500 mb-4">Select accounting standards to prioritize in the next response:</p>
                        <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                            {STANDARDS_OPTIONS.map(opt => (
                                <div key={opt.id} className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => {
                                    if (selectedStandards.includes(opt.id)) setSelectedStandards(s => s.filter(x => x !== opt.id));
                                    else setSelectedStandards(s => [...s, opt.id]);
                                }}>
                                    <div className={`mt-0.5 w-5 h-5 rounded border flex items-center justify-center ${selectedStandards.includes(opt.id) ? 'bg-amber-400 border-amber-400 text-slate-900' : 'border-slate-300 bg-white'}`}>
                                        {selectedStandards.includes(opt.id) && <CheckSquare size={14} />}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-slate-800">{opt.label}</p>
                                        <p className="text-xs text-slate-500">{opt.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setIsRefreshModalOpen(false)} className="flex-1 py-2 text-slate-600 font-bold hover:bg-slate-100 rounded-lg">Cancel</button>
                            <button onClick={confirmRefresh} className="flex-1 py-2 bg-slate-900 text-white font-bold rounded-lg hover:bg-slate-800">Confirm & Refresh</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Ad Positions */}
            <React.Suspense fallback={null}>
                <AdDisplay position="popup" ads={ads} />
                <AdDisplay position="toast" ads={ads} />
                <AdDisplay position="header" ads={ads} />

                {/* Announcements Banner */}
                {announcements.length > 0 && (
                    <div className="fixed top-0 left-0 right-0 z-40 px-4 pt-4">
                        <AnnouncementDisplay
                            announcements={announcements}
                            userType={user?.isPremium ? 'premium' : user?.isAnonymous ? 'guest' : 'free'}
                        />
                    </div>
                )}

                {/* LUCA's Quest Notification */}
                {activeQuest && !isQuestInterfaceOpen && (
                    <LucasQuestNotification
                        quest={activeQuest}
                        onAccept={handleAcceptQuest}
                        onDecline={handleDeclineQuest}
                        isDarkMode={isDarkMode}
                    />
                )}

                {/* Gamification Modal */}
                <GamificationModal
                    isOpen={isGamificationModalOpen}
                    onClose={() => setIsGamificationModalOpen(false)}
                    isDarkMode={isDarkMode}
                />

                {/* Level Up Celebration Modal */}
                {levelUpData && (
                    <LevelUpModal
                        isOpen={!!levelUpData}
                        onClose={() => setLevelUpData(null)}
                        oldLevel={levelUpData.oldLevel}
                        newLevel={levelUpData.newLevel}
                        rewards={levelUpData.rewards}
                        isDarkMode={isDarkMode}
                    />
                )}

                {/* Achievement Celebration */}
                {achievementUnlocks.length > 0 && (
                    <AchievementCelebration
                        unlocks={achievementUnlocks}
                        onClose={() => setAchievementUnlocks([])}
                        isDarkMode={isDarkMode}
                    />
                )}
            </React.Suspense>

            {/* XP Gain Animation - Bottom Left Position */}
            {xpGainAnimation.visible && (
                <div className="fixed bottom-24 left-4 z-[200] animate-in slide-in-from-left fade-in duration-300">
                    <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-4 py-2 rounded-xl shadow-2xl flex items-center gap-2 font-bold">
                        <Star className="animate-pulse" size={20} />
                        <span>+{xpGainAnimation.amount} XP</span>
                    </div>
                </div>
            )}

            {/* LUCA's Quest Interface */}
            <React.Suspense fallback={null}>
                {isQuestInterfaceOpen && activeQuest && user && questQuestions.length > 0 && (
                    <LucasQuestInterface
                        quest={activeQuest}
                        questions={questQuestions}
                        user={user}
                        onComplete={handleCompleteQuest}
                        onClose={() => {
                            setIsQuestInterfaceOpen(false);
                            setActiveQuest(null);
                            setQuestQuestions([]);
                        }}
                        isDarkMode={isDarkMode}
                    />
                )}
            </React.Suspense>

            <Sidebar
                sessions={displayedSessions}
                currentSessionId={currentSessionId}
                onSelectSession={handleSelectSession}
                onNewChat={handleNewChat}
                onDeleteSession={(id) => handleDeleteSession({} as React.MouseEvent, id)}
                onRenameSession={handleRenameSession}
                onPinSession={handleTogglePin}
                isOpen={isSidebarOpen}
                isMobileOpen={isMobileMenuOpen}
                onCloseMobile={() => setIsMobileMenuOpen(false)}
                isPremium={user?.isPremium || false}
                config={config}
                onOpenUpgradeModal={() => { setTokenPurchaseInitialTab('premium'); setIsTokenPurchaseOpen(true); }}
                onOpenExtendModal={() => setIsExtendPremiumOpen(true)}
                onOpenTokenPurchaseModal={() => { setTokenPurchaseInitialTab('tokens'); setIsTokenPurchaseOpen(true); }}
                ads={ads}
                user={user}
                onUserUpdate={setUser}
                onOpenSessionFiles={currentSessionId && user && !user.isAnonymous ? () => {
                    const session = sessionManager.getSession(currentSessionId || '');
                    if (session) {
                        setSelectedSessionForModal(session);
                        setSessionContentModalOpen(true);
                    }
                } : undefined}
                onLogout={handleLogout}
                onOpenSocial={() => setIsSocialModalOpen(true)}
                showToast={showToast}
                onOpenMasteryPath={() => setIsMasteryPathOpen(true)}
            />

            <div className="flex-1 flex flex-col h-full relative transition-all duration-300 ease-in-out min-w-0">

                <Header
                    user={user}
                    config={config}
                    promoCodes={promoCodes}
                    isDarkMode={isDarkMode}
                    messageDensity={messageDensity}
                    autoBudgetEnabled={autoBudgetEnabled}
                    budgetPerRequest={budgetPerRequest}
                    messages={messages}
                    isSidebarOpen={isSidebarOpen}
                    isProfileMenuOpen={isProfileMenuOpen}
                    onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
                    onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
                    onOpenRefreshModal={() => setIsRefreshModalOpen(true)}
                    onOpenUsageModal={() => setIsUsageModalOpen(true)}
                    onOpenAuthModal={() => { setAuthMode('login'); setIsAuthModalOpen(true); }}
                    onOpenExtendPremium={() => setIsExtendPremiumOpen(true)}
                    onOpenTokenPurchase={() => setIsTokenPurchaseOpen(true)}
                    onToggleDarkMode={toggleDarkMode}
                    onSetDensity={handleSetDensity}
                    onLogout={handleLogout}
                    onRedeemCode={handleRedeemCode}
                    onOpenSupport={() => setIsSupportOpen(true)}
                    onToggleAutoBudget={handleToggleAutoBudget}
                    onSetBudgetPerRequest={handleSetBudgetPerRequest}
                    onUserUpdate={setUser}
                    onProfileMenuToggle={setIsProfileMenuOpen}
                    onJumpToMessage={handleJumpToMessage}
                    onExportAll={handleCompiledExport}
                    onOpenSocial={() => setIsSocialModalOpen(true)}
                    onOpenNotifications={() => setIsNotificationsModalOpen(true)}
                    onOpenSessionFiles={currentSessionId && user && !user.isAnonymous ? () => {
                        const session = sessionManager.getSession(currentSessionId || '');
                        if (session) {
                            setSelectedSessionForModal(session);
                            setSessionContentModalOpen(true);
                        }
                    } : undefined}
                    onOpenQuizConfig={() => setIsQuizConfigOpen(true)}
                    onOpenMockExamConfig={() => setIsMockExamConfigOpen(true)}

                    onOpenGamification={() => {
                        const state = loadGamificationState();
                        if (state.streakStatus === 'frozen') {
                            setIsStreakFrozenModalOpen(true);
                        } else {
                            setIsGamificationModalOpen(true);
                        }
                    }}
                    onOpenDashboard={() => setIsUserDashboardOpen(true)}
                    onOpenReferral={() => setIsReferralModalOpen(true)}
                    showToast={showToast}
                />

                <main className="flex-1 flex overflow-hidden relative">
                    {/* Chat Area - Click to collapse sidebar/menus */}
                    <div
                        className={`flex-1 flex flex-col overflow-hidden !bg-fixed
                            ${!chatBackground
                                ? isDarkMode
                                    ? 'bg-[radial-gradient(#334155_1.5px,transparent_1.5px)] [background-size:24px_24px]'
                                    : 'bg-[radial-gradient(#cbd5e1_1.5px,transparent_1.5px)] [background-size:24px_24px]'
                                : ''
                            }
                        `}
                        style={{
                            ...(chatBackground ? { background: chatBackground } : {}),
                        }}
                        onClick={() => {
                            // Auto-collapse sidebar and menus when clicking chat area
                            if (isMobileMenuOpen) setIsMobileMenuOpen(false);
                            if (isProfileMenuOpen) setIsProfileMenuOpen(false);
                            if (isAiMenuOpen) setIsAiMenuOpen(false);
                            if (isAIPersonaOpen) setIsAIPersonaOpen(false);
                        }}
                    >
                        <div
                            ref={chatContainerRef}
                            className={`
                                flex-1 flex flex-col min-h-0 p-1.5 sm:p-2 md:p-3 relative
                            `}
                            style={{
                                background: 'transparent'
                            }}
                        >
                            <div className={`w-full flex-1 flex flex-col items-center min-h-0 overflow-y-auto ${appSettings.fontSize === 'small' ? 'text-xs' : appSettings.fontSize === 'large' ? 'text-lg' : 'text-base'}`}>
                                {messages.length === 0 && (
                                    <div className="mt-4 sm:mt-6 md:mt-10 lg:mt-14 text-center space-y-3 sm:space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700 px-2 pb-4 w-full max-w-2xl mx-auto">
                                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl shadow-xl flex items-center justify-center mx-auto mb-2 sm:mb-3 overflow-hidden ring-2 ring-amber-400/30">
                                            <img src="/robot-luca.png" alt="Luca - Your Personal Reviewer" className="w-full h-full object-contain" />
                                        </div>
                                        <h2 className={`text-lg sm:text-2xl md:text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                            {user ? `Welcome back, ${user.firstName || user.username || 'Reviewer'}!` : "Hello, I'm Luca!"}
                                        </h2>
                                        <p className={`text-xs sm:text-sm md:text-base ${isDarkMode ? 'text-slate-400' : 'text-slate-500'} max-w-md mx-auto leading-relaxed px-4`}>
                                            {user
                                                ? 'Ready to continue your review? Pick a topic below or type your own question.'
                                                : 'Your dedicated CPA Board Exam Reviewer. I can analyze problems, explain standards (PFRS/PAS), and audit your reasoning.'
                                            }
                                        </p>

                                        {/* Suggestion Cards - show 6 random prompts */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto mt-3 sm:mt-5 text-left px-2">
                                            {SUGGESTED_PROMPTS.sort(() => 0.5 - Math.random()).slice(0, 6).map((prompt, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => { setInputValue(prompt); inputRef.current?.focus(); }}
                                                    className={`
                                                        w-full p-2.5 sm:p-3 rounded-xl text-xs sm:text-sm text-left
                                                        border transition-all duration-200 ease-out
                                                        hover:shadow-md hover:-translate-y-0.5
                                                        active:scale-[0.98] touch-manipulation
                                                        animate-in fade-in slide-in-from-bottom-2
                                                        ${isDarkMode
                                                            ? 'bg-slate-800/60 border-slate-700/80 text-slate-300 hover:border-amber-400/60 hover:bg-slate-800/90'
                                                            : 'bg-white/70 border-slate-200 text-slate-600 hover:border-amber-400 hover:bg-white'
                                                        }
                                                    `}
                                                    style={{
                                                        animationDelay: `${i * 70}ms`,
                                                        animationFillMode: 'both'
                                                    }}
                                                >
                                                    {prompt}
                                                </button>
                                            ))}
                                        </div>

                                        {/* Guest CTA */}
                                        {!user && (
                                            <p className={`text-[10px] sm:text-xs mt-3 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                                <button
                                                    onClick={() => setIsAuthModalOpen(true)}
                                                    className={`underline underline-offset-2 hover:no-underline ${isDarkMode ? 'text-amber-400/80 hover:text-amber-300' : 'text-amber-600 hover:text-amber-700'} font-medium`}
                                                >
                                                    Sign in
                                                </button>
                                                {' '}to save your progress and unlock more features
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Use optimized rendering for large sessions */}
                                {messages.length > 15 ? (
                                    <VirtualizedMessageList
                                        messages={messages}
                                        density={messageDensity}
                                        isDarkMode={isDarkMode}
                                        onFeedback={handleFeedback}
                                        onRegenerate={handleRegenerate}
                                        onPreview={(att) => setActivePdf(att)}
                                        pinnedMessageIds={(displayedSessions.find(s => s.id === currentSessionId)?.pinnedMessages || []) as string[]}
                                        onTogglePin={handleTogglePin}
                                        onAIPersonaClick={() => setIsAIPersonaOpen(!isAIPersonaOpen)}
                                        onExport={handleExportMessage}
                                        userName={user?.username}
                                        onOpenQuiz={handleOpenQuiz}
                                        showTimestamps={appSettings.showTimestamps}
                                        showAvatars={appSettings.showAvatars}
                                    />
                                ) : (
                                    /* Use VirtualizedMessageList for better performance with large sessions */
                                    <VirtualizedMessageList
                                        messages={messages}
                                        density={messageDensity}
                                        isDarkMode={isDarkMode}
                                        user={user ?? undefined}
                                        config={config}
                                        pinnedMessageIds={(displayedSessions.find(s => s.id === currentSessionId)?.pinnedMessages || []) as string[]}
                                        rewardedMessageIds={new Set(messages.filter(m => hasMessageBeenRewarded(m.id)).map(m => m.id))}
                                        onFeedback={handleFeedback}
                                        onRegenerate={handleRegenerate}
                                        onPreview={(att) => setActivePdf(att)}
                                        onTogglePin={handleTogglePin}
                                        onAIPersonaClick={() => setIsAIPersonaOpen(!isAIPersonaOpen)}
                                        onExport={handleExportMessage}
                                        userName={user?.username}
                                        onOpenQuiz={handleOpenQuiz}
                                        showTimestamps={appSettings.showTimestamps}
                                        showAvatars={appSettings.showAvatars}
                                    />
                                )}
                                <div ref={messagesEndRef} />
                            </div>
                        </div>

                        {/* AI Persona Selector - Premium Design */}
                        <PersonaSelector
                            isOpen={isAIPersonaOpen}
                            onClose={() => setIsAIPersonaOpen(false)}
                            currentPersona={aiMode === 'standard' ? 'general' : aiMode === 'educator' ? 'educator' : 'lawyer'}
                            currentDepth={aiDepth === 'concise' ? 'concise' : aiDepth === 'balanced' || aiDepth === 'detailed' ? 'standard' : 'comprehensive'}
                            config={config}
                            onSelect={(persona, depth) => {
                                // Map modal persona IDs to unified state types
                                const modeMap: Record<string, 'standard' | 'educator' | 'lawyer'> = {
                                    'general': 'standard',
                                    'educator': 'educator',
                                    'lawyer': 'lawyer'
                                };
                                const depthMap: Record<string, 'concise' | 'balanced' | 'detailed' | 'comprehensive'> = {
                                    'concise': 'concise',
                                    'standard': 'balanced',
                                    'comprehensive': 'comprehensive',
                                    'expert': 'comprehensive'
                                };
                                setAiMode(modeMap[persona] || 'standard');
                                setAiDepth(depthMap[depth] || 'balanced');
                            }}
                            userTier={user?.accountTier === 'max' ? 'max' : user?.isPremium ? 'premium' : 'free'}
                            isDarkMode={isDarkMode}
                        />

                        {/* Input Area - Ultra Compact, Transparent */}
                        <div
                            ref={chatInputAreaRef}
                            className="px-2 sm:px-2.5 pt-1.5 pb-2.5 sm:pt-2 sm:pb-3 ios-bottom-safe ios-chat-input flex-shrink-0 relative"
                            style={{
                                paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))',
                                minHeight: 'fit-content',
                                background: 'transparent'
                            }}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                            {/* Drag and Drop Overlay */}
                            {isDragging && (
                                <div className="absolute inset-0 z-50 bg-indigo-500/20 backdrop-blur-sm border-4 border-dashed border-indigo-400 rounded-2xl flex items-center justify-center">
                                    <div className="text-center">
                                        <Paperclip size={48} className="mx-auto mb-2 text-indigo-600" />
                                    </div>
                                </div>
                            )}
                            <div className="w-full max-w-2xl mx-auto relative">
                                {attachments.length > 0 && (
                                    <div className="flex gap-1.5 sm:gap-2 mb-2 overflow-x-auto pb-1 scrollbar-thin">
                                        {attachments.map((att, idx) => (
                                            <div key={idx} className="relative group flex-shrink-0">
                                                {att.mimeType?.startsWith('image/') ? (
                                                    <img src={att.preview} alt={att.name} className={`h-12 w-12 sm:h-14 sm:w-14 object-cover rounded-lg border ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`} />
                                                ) : (
                                                    <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-lg border flex flex-col items-center justify-center text-[7px] p-0.5 text-center ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                                                        <FileText size={16} className={isDarkMode ? 'mb-0.5 text-slate-400' : 'mb-0.5 text-slate-400'} />
                                                        <span className="truncate w-full">{att.name}</span>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow-md opacity-0 group-hover:opacity-100 sm:group-hover:opacity-100 touch-manipulation transition-opacity active:opacity-100"
                                                    aria-label={`Remove ${att.name}`}
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className={`relative ${isDarkMode ? 'bg-slate-800/95 border-slate-700/50' : 'bg-white/95 border-slate-200'} rounded-xl border backdrop-blur-sm focus-within:ring-1 sm:focus-within:ring-2 focus-within:ring-amber-400 focus-within:border-transparent transition-all shadow-lg`}>

                                    {/* AI Tools Menu */}
                                    {isAiMenuOpen && (
                                        <div ref={aiMenuRef} className="absolute bottom-full left-0 mb-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 p-2 z-50 animate-in fade-in slide-in-from-bottom-2">
                                            <div className="space-y-1">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">Persona</p>
                                                <div className="grid grid-cols-3 gap-1 p-1 bg-slate-100 rounded-lg">
                                                    <button
                                                        onClick={() => setAiMode('standard')}
                                                        className={`text-xs py-1.5 rounded-md font-bold flex items-center justify-center gap-1 transition-all ${aiMode === 'standard' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`}
                                                        title="Directly solves the problem with step-by-step computations."
                                                    >
                                                        <Sparkles size={12} /> Solver
                                                    </button>
                                                    <button
                                                        onClick={() => setAiMode('educator')}
                                                        className={`text-xs py-1.5 rounded-md font-bold flex items-center justify-center gap-1 transition-all ${aiMode === 'educator' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-500 hover:text-slate-700'}`}
                                                        title="Adaptive teaching: ELI5 for strugglers, expert-level for fast learners"
                                                    >
                                                        <GraduationCap size={12} /> Tutor
                                                    </button>
                                                    <button
                                                        onClick={() => setAiMode('lawyer')}
                                                        className={`text-[11px] py-1.5 rounded-md font-bold flex items-center justify-center gap-1 transition-all ${aiMode === 'lawyer' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-500 hover:text-slate-700'}`}
                                                        title="Expert analysis with full citations, case law, and standards references"
                                                    >
                                                        <Crown size={12} /> Expert
                                                    </button>
                                                </div>

                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1 mt-2">Response Detail Level</p>
                                                <div className="grid grid-cols-4 gap-1 p-1 bg-slate-100 rounded-lg">
                                                    <button
                                                        onClick={() => setAiDepth('concise')}
                                                        className={`text-[9px] py-1.5 rounded-md font-bold flex flex-col items-center justify-center gap-0.5 transition-all ${aiDepth === 'concise' ? 'bg-white shadow-sm text-green-600' : 'text-slate-500 hover:text-slate-700'}`}
                                                        title="Brief, to-the-point answers only"
                                                    >
                                                        <Zap size={11} /> Brief
                                                    </button>
                                                    <button
                                                        onClick={() => setAiDepth('balanced')}
                                                        className={`text-[9px] py-1.5 rounded-md font-bold flex flex-col items-center justify-center gap-0.5 transition-all ${aiDepth === 'balanced' ? 'bg-white shadow-sm text-blue-600' : 'text-slate-500 hover:text-slate-700'}`}
                                                        title="Balanced explanation with key details"
                                                    >
                                                        <BookOpen size={11} /> Balanced
                                                    </button>
                                                    <button
                                                        onClick={() => setAiDepth('detailed')}
                                                        className={`text-[9px] py-1.5 rounded-md font-bold flex flex-col items-center justify-center gap-0.5 transition-all ${aiDepth === 'detailed' ? 'bg-white shadow-sm text-purple-600' : 'text-slate-500 hover:text-slate-700'}`}
                                                        title="Thorough explanation with examples"
                                                    >
                                                        <Brain size={11} /> Detailed
                                                    </button>
                                                    <button
                                                        onClick={() => setAiDepth('comprehensive')}
                                                        className={`text-[9px] py-1.5 rounded-md font-bold flex flex-col items-center justify-center gap-0.5 transition-all ${aiDepth === 'comprehensive' ? 'bg-white shadow-sm text-amber-600' : 'text-slate-500 hover:text-slate-700'}`}
                                                        title="Complete analysis with citations & nuances"
                                                    >
                                                        <FileText size={11} /> Full
                                                    </button>
                                                </div>

                                                <div className="h-px bg-slate-100 my-2"></div>

                                                <button
                                                    onClick={() => { setInputValue("Generate a comprehensive practice problem for [Topic]"); inputRef.current?.focus(); setIsAiMenuOpen(false); }}
                                                    className="w-full text-left px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                                                    title="Generates a unique problem for the current topic."
                                                >
                                                    <FileQuestion size={14} className="text-amber-500" /> Practice Problem
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!user || user.isAnonymous) {
                                                            showToast('warning', 'Please create an account to generate exams');
                                                            setShowGuestLimitModal(true);
                                                            setIsAiMenuOpen(false);
                                                            return;
                                                        }
                                                        setIsMockExamConfigOpen(true);
                                                        setIsAiMenuOpen(false);
                                                    }}
                                                    className="w-full text-left px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                                                    title="Full Mock Board Exam Simulation"
                                                >
                                                    <AlertTriangle size={14} className="text-red-500" /> Full Mock Board Exam
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!user || user.isAnonymous) {
                                                            showToast('warning', 'Please create an account to generate quizzes');
                                                            setShowGuestLimitModal(true);
                                                            setIsAiMenuOpen(false);
                                                            return;
                                                        }
                                                        setIsQuizConfigOpen(true);
                                                        setIsAiMenuOpen(false);
                                                    }}
                                                    className="w-full text-left px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                                                    title="Generates an interactive quiz card session."
                                                >
                                                    <BrainCircuit size={14} className="text-pink-500" /> Interactive Quiz
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!user || user.isAnonymous) {
                                                            showToast('warning', 'Please create an account to join group study');
                                                            setShowGuestLimitModal(true);
                                                            setIsAiMenuOpen(false);
                                                            return;
                                                        }
                                                        setIsGroupStudyModalOpen(true);
                                                        setIsAiMenuOpen(false);
                                                    }}
                                                    className="w-full text-left px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                                                    title="Join or create a group study session with friends."
                                                >
                                                    <Users size={14} className="text-emerald-500" /> Group Study Session
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!user || user.isAnonymous) {
                                                            showToast('warning', 'Please create an account to access Study Mode');
                                                            setShowGuestLimitModal(true);
                                                            setIsAiMenuOpen(false);
                                                            return;
                                                        }
                                                        setIsStudyModeIntroOpen(true);
                                                        setIsAiMenuOpen(false);
                                                    }}
                                                    className="w-full text-left px-2 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 rounded-lg flex items-center gap-2"
                                                    title="Access flashcards, error journal, moot court, and more."
                                                >
                                                    <Brain size={14} className="text-purple-500" /> Study Mode
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <textarea
                                        ref={inputRef}
                                        value={inputValue}
                                        onChange={(e) => {
                                            setInputValue(e.target.value);
                                            // Auto-resize textarea
                                            if (inputRef.current) {
                                                inputRef.current.style.height = 'auto';
                                                inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
                                            }
                                        }}
                                        onKeyDown={handleKeyDown}
                                        onPaste={handlePaste}
                                        placeholder={aiMode === 'educator' ? "Ask for guidance..." : "Ask Luca about PFRS, Taxation..."}
                                        className={`w-full bg-transparent border-none p-2.5 sm:p-3 pl-11 sm:pl-12 pr-14 sm:pr-16 min-h-[44px] sm:min-h-[48px] max-h-[120px] resize-none focus:ring-0 text-sm ${isDarkMode ? 'text-white placeholder:text-slate-500' : 'text-slate-800 placeholder:text-slate-400'} touch-manipulation overflow-y-auto leading-tight`}
                                        style={{ height: 'auto' }}
                                        rows={1}
                                    />

                                    {/* Plus Button with Drop-up Menu */}
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 sm:left-3">
                                        {/* Drop-up Menu */}
                                        {isPlusMenuOpen && (
                                            <div className={`absolute bottom-full left-0 mb-2 w-56 rounded-xl shadow-xl border overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-200 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'
                                                }`}>
                                                <button
                                                    onClick={() => {
                                                        fileInputRef.current?.click();
                                                        setIsPlusMenuOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isDarkMode
                                                        ? 'hover:bg-slate-700 text-slate-200'
                                                        : 'hover:bg-slate-50 text-slate-700'
                                                        }`}
                                                >
                                                    <Image size={18} className="text-indigo-500" />
                                                    <span>Attach photos/files</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setTokenPurchaseInitialTab('tokens');
                                                        setIsTokenPurchaseOpen(true);
                                                        setIsPlusMenuOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isDarkMode
                                                        ? 'hover:bg-slate-700 text-slate-200'
                                                        : 'hover:bg-slate-50 text-slate-700'
                                                        }`}
                                                >
                                                    <Coins size={18} className="text-amber-500" />
                                                    <span>Get Tokens</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!user || user.isAnonymous) {
                                                            showToast('warning', 'Please create an account to generate materials');
                                                            setShowGuestLimitModal(true);
                                                            setIsPlusMenuOpen(false);
                                                            return;
                                                        }
                                                        setIsGenerateMaterialsOpen(true);
                                                        setIsPlusMenuOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isDarkMode
                                                        ? 'hover:bg-slate-700 text-slate-200'
                                                        : 'hover:bg-slate-50 text-slate-700'
                                                        }`}
                                                >
                                                    <Sparkles size={18} className="text-amber-500" />
                                                    <span>Generate materials</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!user || user.isAnonymous) {
                                                            showToast('warning', 'Please create an account to view materials');
                                                            setShowGuestLimitModal(true);
                                                            setIsPlusMenuOpen(false);
                                                            return;
                                                        }
                                                        setIsReadablesLibraryOpen(true);
                                                        setIsPlusMenuOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isDarkMode
                                                        ? 'hover:bg-slate-700 text-slate-200'
                                                        : 'hover:bg-slate-50 text-slate-700'
                                                        }`}
                                                >
                                                    <FolderOpen size={18} className="text-amber-500" />
                                                    <span>Generated Materials</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setIsAIPersonaOpen(true);
                                                        setIsPlusMenuOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isDarkMode
                                                        ? 'hover:bg-slate-700 text-slate-200'
                                                        : 'hover:bg-slate-50 text-slate-700'
                                                        }`}
                                                >
                                                    <Settings size={18} className="text-purple-500" />
                                                    <span>Change persona</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!user || user.isAnonymous) {
                                                            showToast('warning', 'Please create an account to join group study');
                                                            setShowGuestLimitModal(true);
                                                            setIsPlusMenuOpen(false);
                                                            return;
                                                        }
                                                        setIsGroupStudyModalOpen(true);
                                                        setIsPlusMenuOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isDarkMode
                                                        ? 'hover:bg-slate-700 text-slate-200'
                                                        : 'hover:bg-slate-50 text-slate-700'
                                                        }`}
                                                >
                                                    <Users size={18} className="text-emerald-500" />
                                                    <span>Group Study Session</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (!user || user.isAnonymous) {
                                                            showToast('warning', 'Please create an account to access Study Mode');
                                                            setShowGuestLimitModal(true);
                                                            setIsPlusMenuOpen(false);
                                                            return;
                                                        }
                                                        setIsStudyModeIntroOpen(true);
                                                        setIsPlusMenuOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isDarkMode
                                                        ? 'hover:bg-slate-700 text-slate-200'
                                                        : 'hover:bg-slate-50 text-slate-700'
                                                        }`}
                                                >
                                                    <Brain size={18} className="text-purple-500" />
                                                    <span>Study Mode</span>
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        handleCompiledExport();
                                                        setIsPlusMenuOpen(false);
                                                    }}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isDarkMode
                                                        ? 'hover:bg-slate-700 text-slate-200'
                                                        : 'hover:bg-slate-50 text-slate-700'
                                                        }`}
                                                >
                                                    <Download size={18} className="text-green-500" />
                                                    <span>Export chat</span>
                                                </button>
                                            </div>
                                        )}

                                        <button
                                            onClick={() => setIsPlusMenuOpen(!isPlusMenuOpen)}
                                            className={`p-1 rounded-lg transition-all active:scale-95 ${isPlusMenuOpen
                                                ? isDarkMode
                                                    ? 'bg-slate-700 text-white'
                                                    : 'bg-slate-200 text-slate-700'
                                                : isDarkMode
                                                    ? 'text-slate-400 hover:bg-slate-700 hover:text-white'
                                                    : 'text-slate-500 hover:bg-slate-200 hover:text-slate-700'
                                                }`}
                                            title="Quick actions"
                                        >
                                            <Plus size={20} />
                                        </button>
                                    </div>

                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 sm:right-3 flex items-center gap-2">
                                        {isLoading ? (
                                            <>
                                                <button
                                                    onClick={() => setIsPaused(!isPaused)}
                                                    className={`p-2 rounded-lg transition-all touch-manipulation active:scale-95 ${isPaused ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-amber-100 text-amber-600 hover:bg-amber-200'}`}
                                                    title={isPaused ? "Resume Generation" : "Pause Generation"}
                                                >
                                                    {isPaused ? <PlayCircle size={18} /> : <Pause size={18} />}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        if (abortControllerRef.current) {
                                                            abortControllerRef.current.abort();
                                                            abortControllerRef.current = null;
                                                            setIsLoading(false);
                                                            setIsPaused(false);
                                                        }
                                                    }}
                                                    className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors touch-manipulation active:scale-95"
                                                    title="Stop Generation"
                                                >
                                                    <Square size={18} className="fill-current" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <input
                                                    type="file"
                                                    multiple
                                                    accept="image/*,.pdf,.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                                    ref={fileInputRef}
                                                    onChange={handleFileSelect}
                                                    className="hidden"
                                                />
                                                <button
                                                    onClick={handleSendMessage}
                                                    disabled={(!inputValue.trim() && attachments.length === 0)}
                                                    className={`p-1 rounded-lg transition-all touch-manipulation active:scale-95 ${(!inputValue.trim() && attachments.length === 0)
                                                        ? 'opacity-30 cursor-not-allowed text-slate-500'
                                                        : isDarkMode
                                                            ? 'text-slate-400 hover:bg-amber-500 hover:text-white'
                                                            : 'text-slate-500 hover:bg-amber-400 hover:text-white'
                                                        }`}
                                                >
                                                    <Send size={20} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Disclaimer removed from footer - more space for messages */}
                            </div>

                        </div>

                    </div>


                    <React.Suspense fallback={<LoadingScreen isDarkMode={isDarkMode} />}>
                        {/* Premium Document Viewer - Buttery smooth, mobile-optimized, APK-ready */}
                        {activePdf && (
                            <PremiumDocumentViewer
                                attachment={{
                                    id: activePdf.name,
                                    name: activePdf.name,
                                    data: activePdf.data || '',
                                    mimeType: activePdf.mimeType || 'application/octet-stream',
                                    size: activePdf.data ? atob(activePdf.data).length : 0,
                                    uploadedAt: Date.now()
                                }}
                                onClose={() => setActivePdf(null)}
                                isDarkMode={isDarkMode}
                                user={user}
                                updateUser={(updates) => {
                                    if (user) {
                                        const updatedUser = { ...user, ...updates };
                                        setUser(updatedUser);
                                        localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
                                    }
                                }}
                                sessionId={currentSessionId ?? ''}
                                onSaveAnnotations={(annotations) => {
                                    console.log('[App] Document annotations saved:', annotations);
                                    // Annotations are auto-saved to session storage
                                }}
                            />
                        )}

                        <QuizConfigModal
                            isOpen={isQuizConfigOpen}
                            onClose={() => {
                                setIsQuizConfigOpen(false);
                                setQuizInitialTopic(''); // Reset topic on close
                            }}
                            onGenerate={handleGenerateQuiz}
                            initialTopic={quizInitialTopic || inputValue.trim() || "Accounting Basics"}
                        />

                        {isQuizActive && quizData && (
                            <QuizInterface
                                quiz={quizData}
                                onClose={() => {
                                    setIsQuizActive(false);
                                    setActiveMasteryNodeId(null);
                                }}
                                onRestart={() => setIsQuizConfigOpen(true)}
                                onComplete={async (result) => {
                                    // Save Quiz Result
                                    if (user && quizData) {
                                        const percentage = Math.round((result.score / result.total) * 100);
                                        saveQuizResult(user.id, {
                                            id: crypto.randomUUID(),
                                            topic: quizData.topic || quizData.title || 'Unknown Topic',
                                            score: result.score,
                                            totalQuestions: result.total,
                                            percentage: percentage,
                                            date: Date.now(),
                                            difficulty: 'medium' // Default for quick quizzes
                                        }).catch(e => console.error('Failed to save quiz result:', e));

                                        // Record quiz completion for smart recommendations
                                        try {
                                            smartRecommendationService.recordQuizComplete(result.score, result.total, quizData.topic || quizData.title || '');
                                        } catch (_) { /* non-critical */ }

                                        // MASTERY PATH: Award quiz mastery bonus
                                        try {
                                            const { awardQuizMasteryBonus, checkMasteryMilestones } = await import('./services/masteryPathService');
                                            const quizPct = Math.round((result.score / result.total) * 100);
                                            const bonusResult = awardQuizMasteryBonus(quizPct, result.total, 'medium', false);
                                            if (bonusResult.awarded) {
                                                showToast('info', `+${bonusResult.pointsAwarded} Mastery Points from quiz`);
                                                if (bonusResult.thresholdReached) {
                                                    setTimeout(() => showToast('success', 'Mastery threshold reached! You can now take the topic exam.'), 1500);
                                                }
                                                const milestones = checkMasteryMilestones();
                                                for (const m of milestones.newMilestones) {
                                                    setTimeout(() => showToast('success', `Milestone: ${m.title} — +${m.reward.tokens} tokens, +${m.reward.xp} XP`), 2500);
                                                }
                                            }
                                        } catch (e) { console.error('[MasteryPath] Quiz bonus error:', e); }
                                    }

                                    if (activeMasteryNodeId) {
                                        const passingScore = Math.ceil(result.total * 0.7); // 70% passing
                                        if (result.score >= passingScore) {
                                            const scorePercentage = Math.round((result.score / result.total) * 100);

                                            // Find next nodes to unlock (Legacy: placeholder)
                                            const nextNodeIds: string[] = [];
                                            // const nextNodeIds = CPA_MASTERY_PATH
                                            //     .filter(n => n.requires?.includes(activeMasteryNodeId))
                                            //     .map(n => n.id);

                                            completeMasteryNode(activeMasteryNodeId, scorePercentage, nextNodeIds);
                                            showToast('success', 'Mastery Node Completed! 🎉');
                                        } else {
                                            showToast('warning', `You need 70% to pass. Scored ${Math.round((result.score / result.total) * 100)}%`);
                                        }
                                        // Don't clear activeMasteryNodeId yet, wait for close? 
                                        // Actually, if they restart, they might need it? 
                                        // But completeMasteryNode is idempotent mostly.
                                        // Let's keep it until close.
                                    }
                                }}
                            />
                        )}

                        <MockExamConfigModal
                            isOpen={isMockExamConfigOpen}
                            onClose={() => setIsMockExamConfigOpen(false)}
                            onGenerate={handleGenerateMockExam}
                        />

                        {isPracticeProblemsConfigOpen && (
                            <PracticeProblemsConfigModal
                                isOpen={isPracticeProblemsConfigOpen}
                                onClose={() => setIsPracticeProblemsConfigOpen(false)}
                                onGenerate={async (config) => {
                                    try {
                                        setIsPracticeProblemsConfigOpen(false);
                                        setIsLoading(true);
                                        showToast('info', `Generating ${config.numberOfProblems} practice problems...`);

                                        const prompt = `Generate ${config.numberOfProblems} MOCKBOARD-LEVEL EXAMINATION practice problems for: ${config.topic}

🎯 CRITICAL REQUIREMENTS - MOCKBOARD EXAMINATION STANDARDS:

**Difficulty Level: ${config.difficulty.toUpperCase()}**
- These must be authentic CPA/Board Examination caliber questions
- Complex, multi-step problems that test deep understanding
- Include computational, theoretical, and application-based questions
- Problems should require 5-15 minutes each to solve properly
- Mirror actual board exam difficulty and format

**Question Types to Include:**
1. **Computational Problems** (40%) - Require calculations, journals, ledgers, or financial statements
2. **Situational Analysis** (30%) - Real-world scenarios requiring professional judgment
3. **Theoretical Applications** (20%) - Concept-based with practical implications  
4. **Regulatory/Standards** (10%) - Based on accounting standards, tax codes, or regulations

**Content Quality Standards:**
✓ Use realistic company names, amounts, and scenarios
✓ Include multiple concepts in each problem (integrated learning)
✓ Add red herrings and distractors (like real exams)
✓ Require critical thinking, not just memorization
✓ Test both technical knowledge and professional judgment
✓ Include current year standards and regulations

${config.includeStepByStep ? '**Solution Format:**\n- Provide comprehensive step-by-step solutions\n- Show all calculations with clear labels\n- Explain the reasoning behind each step\n- Reference applicable standards/regulations\n- Include journal entries, T-accounts, or schedules where applicable' : ''}

${config.includeExplanations ? '**Explanations Required:**\n- Explain WHY each answer is correct\n- Discuss common mistakes and misconceptions\n- Provide insights on exam strategy for similar questions\n- Connect concepts to broader accounting principles' : ''}

${config.includeHints ? '**Strategic Hints:**\n- Provide hints that guide thinking without giving away answers\n- Focus on approach and methodology\n- Highlight key concepts to recall\n- Suggest what to look for in the problem' : ''}

**Professional Format:**
## EXAMINATION PROBLEMS

### Problem [Number]: [Descriptive Title]
**Time Allocation:** [X] minutes | **Difficulty:** ${config.difficulty} | **Points:** [X]

[Complete problem statement with all necessary data]

**Required:**
a) [First requirement]
b) [Second requirement]
c) [Third requirement if applicable]

---

${config.includeStepByStep ? '\n## COMPLETE SOLUTIONS\n\n### Solution to Problem [Number]:\n[Step-by-step solution with calculations]\n\n**Answer Summary:**\n[Clear final answers]\n' : ''}

**MOCKBOARD QUALITY CHECKLIST:**
- [ ] Problem requires 5-15 minutes to solve
- [ ] Tests multiple concepts simultaneously
- [ ] Includes realistic data and scenarios
- [ ] Requires professional judgment
- [ ] Has clear, unambiguous requirements
- [ ] Solutions reference accounting standards
- [ ] Difficulty matches actual board exam questions

Generate ${config.numberOfProblems} examination-quality problems that truly prepare students for the CPA Board Examination on ${config.topic}.`;

                                        // Set pending PDF generation
                                        setPendingMaterialPDF({
                                            type: 'practice-problems',
                                            topic: config.topic
                                        });

                                        // Set input and trigger send
                                        setInputValue(prompt);
                                        setTimeout(() => {
                                            handleSendMessage();
                                        }, 100);

                                        showToast('info', 'Generating practice problems...');
                                    } catch (error) {
                                        console.error('Practice problems generation error:', error);
                                        showToast('error', 'Failed to generate practice problems');
                                        setIsLoading(false);
                                    }
                                }}
                                isDarkMode={isDarkMode}
                                user={user}
                                topic={materialGenerationTopic}
                            />
                        )}

                        {isStudyGuideConfigOpen && (
                            <StudyGuideConfigModal
                                isOpen={isStudyGuideConfigOpen}
                                onClose={() => setIsStudyGuideConfigOpen(false)}
                                onGenerate={async (config) => {
                                    try {
                                        const depthDescriptions = {
                                            overview: 'concise overview covering main topics',
                                            standard: 'balanced coverage with moderate detail',
                                            comprehensive: 'in-depth coverage of all concepts',
                                            intensive: 'complete mastery guide with extensive detail'
                                        };

                                        const prompt = `Create a ${config.depth.toUpperCase()} study guide for: ${config.topic}

Depth Level: ${depthDescriptions[config.depth]}

Enhanced Features:
${config.includeExamples ? '✓ Include practical examples and real-world applications' : ''}
${config.includeDiagrams ? '✓ Include visual diagrams, charts, and illustrations (describe them)' : ''}
${config.includeQuizQuestions ? '✓ Include practice questions with answers at the end' : ''}
${config.includeSummary ? '✓ Include chapter summaries and key takeaways' : ''}
${config.includeReferences ? '✓ Include references, citations, and further reading' : ''}

Structure Requirements:
1. Title Page with topic name
2. Table of Contents
3. Introduction/Overview
4. Main Content (organized by chapters/sections)
5. Key Concepts Summary
6. Practice Questions (if applicable)
7. References (if applicable)

Format as a professional, educational study guide that:
- Uses clear headings and subheadings
- Includes bullet points and numbered lists
- Has proper formatting for readability
- Is comprehensive and well-organized
- Helps students master ${config.topic}

Generate the complete study guide now.`;

                                        // Set pending PDF generation
                                        setPendingMaterialPDF({
                                            type: 'study-guide',
                                            topic: config.topic
                                        });

                                        // Set input and trigger send
                                        setInputValue(prompt);
                                        setTimeout(() => {
                                            handleSendMessage();
                                        }, 100);
                                    } catch (error) {
                                        console.error('Study guide generation error:', error);
                                    }
                                }}
                                isDarkMode={isDarkMode}
                                user={user}
                                topic={materialGenerationTopic}
                            />
                        )}

                        {
                            isExamActive && examData && (
                                <ExamInterface
                                    exam={examData}
                                    onClose={() => setIsExamActive(false)}
                                    onRestart={() => setIsMockExamConfigOpen(true)}
                                    onSubmit={handleExamSubmit}
                                    isDarkMode={isDarkMode}
                                />
                            )
                        }

                        {/* PWA Components - Update notification and install reminders are rendered separately */}

                        {/* Quiz/Exam Generation Progress Modal with Logo */}
                        <GenerationLoadingModal
                            isOpen={isGeneratingQuiz}
                            type={quizProgress.percent > 0 ? 'quiz' : 'exam'}
                            progress={quizProgress.percent > 0 ? quizProgress : examProgress}
                            isDarkMode={isDarkMode}
                        />

                        {/* Document Export Modal */}
                        <ExportModal
                            isOpen={isExportModalOpen}
                            onClose={() => {
                                setIsExportModalOpen(false);
                                setExportTargetMessage(undefined);
                            }}
                            messages={messages}
                            targetMessage={exportTargetMessage}
                            isDarkMode={isDarkMode}
                            user={user}
                            config={config}
                            updateUser={(updates) => {
                                if (user) {
                                    setUser({ ...user, ...updates });
                                }
                            }}
                            showToast={showToast}
                        />

                        {/* Token Cost Modal */}
                        {showTokenCostModal && pendingFile && user && (
                            <React.Suspense fallback={null}>
                                <TokenCostModal
                                    fileName={pendingFile.file.name}
                                    fileSize={pendingFile.file.size}
                                    estimatedCost={pendingFile.estimatedCost}
                                    currentBalance={(() => {
                                        const tokenLimit = user.isMax || user.accountTier === 'max'
                                            ? (config?.maxTokenLimit || 50000)
                                            : user.isPremium
                                                ? (config?.premiumTokenLimit || 15000)
                                                : (config?.freeTokenLimit || 2000);
                                        return user.tokenBalance ?? tokenLimit;
                                    })()}
                                    tokenLimit={user.isMax || user.accountTier === 'max'
                                        ? (config?.maxTokenLimit || 50000)
                                        : user.isPremium
                                            ? (config?.premiumTokenLimit || 15000)
                                            : (config?.freeTokenLimit || 2000)}
                                    onConfirm={handleTokenCostConfirm}
                                    onCancel={handleTokenCostCancel}
                                />
                            </React.Suspense>
                        )}

                        {/* Low Balance Upsell Modal */}
                        <React.Suspense fallback={null}>
                            <LowBalanceModal
                                isOpen={isLowBalanceModalOpen}
                                onClose={() => setIsLowBalanceModalOpen(false)}
                                neededAmount={lowBalanceNeeded}
                                currentBalance={user?.tokenBalance || 0}
                                isDarkMode={isDarkMode}
                                onBuyTokens={() => {
                                    setIsTokenPurchaseOpen(true);
                                    setTokenPurchaseInitialTab('tokens');
                                }}
                                onSubscribe={() => {
                                    setIsTokenPurchaseOpen(true);
                                    setTokenPurchaseInitialTab('premium');
                                }}
                                onWatchAd={() => {
                                    startAdWatch();
                                }}
                            />
                        </React.Suspense>

                        {/* Attachment Cost Modal */}
                        {showAttachmentCostModal && attachmentCostBreakdown && (
                            <React.Suspense fallback={null}>
                                <AttachmentCostModal
                                    files={pendingAttachments}
                                    costBreakdown={attachmentCostBreakdown}
                                    currentBalance={(() => {
                                        const tokenLimit = user?.isMax || user?.accountTier === 'max'
                                            ? (config?.maxTokenLimit || 50000)
                                            : user?.isPremium
                                                ? (config?.premiumTokenLimit || 15000)
                                                : (config?.freeTokenLimit || 2000);
                                        return user?.tokenBalance ?? tokenLimit;
                                    })()}
                                    tokenLimit={user?.isMax || user?.accountTier === 'max'
                                        ? (config?.maxTokenLimit || 50000)
                                        : user?.isPremium
                                            ? (config?.premiumTokenLimit || 15000)
                                            : (config?.freeTokenLimit || 2000)}
                                    onConfirm={handleAttachmentCostConfirm}
                                    onCancel={handleAttachmentCostCancel}
                                />
                            </React.Suspense>
                        )}

                        {/* Advanced Token Cost Confirmation Modal */}
                        {showAdvancedCostModal && advancedCostBreakdown && (
                            <React.Suspense fallback={null}>
                                <TokenCostConfirmationModal
                                    isOpen={showAdvancedCostModal}
                                    costBreakdown={advancedCostBreakdown}
                                    currentUsage={user?.dailyTokenUsage || guestTokenUsage}
                                    tokenLimit={user?.isMax || user?.accountTier === 'max'
                                        ? (config?.maxTokenLimit || 50000)
                                        : user?.isPremium
                                            ? (config?.premiumTokenLimit || 15000)
                                            : (config?.freeTokenLimit || 2000)}
                                    operationName={pendingOperation?.name || 'Operation'}
                                    onConfirm={handleAdvancedCostConfirm}
                                    onCancel={handleAdvancedCostCancel}
                                />
                            </React.Suspense>
                        )}

                        {/* Pre-emptive token warning removed - users proceed directly */}

                        {/* Connection Status Banner */}
                        <React.Suspense fallback={null}>
                            <ConnectionStatus
                                onStatusChange={(status) => {
                                    setIsOnline(status.isOnline);
                                    setConnectionSpeed(status.speed);
                                }}
                            />
                        </React.Suspense>

                        {/* PWA Install Prompt - Removed (integrated into UnifiedOnboarding) */}

                        {/* Session Content Modal */}
                        {selectedSessionForModal && (
                            <SessionContentModal
                                isOpen={sessionContentModalOpen}
                                onClose={() => {
                                    setSessionContentModalOpen(false);
                                    setSelectedSessionForModal(null);
                                }}
                                session={selectedSessionForModal}
                                sessions={displayedSessions}
                                isDarkMode={isDarkMode}
                                onJumpToMessage={handleJumpToMessage}
                                onUnpinMessage={handleTogglePin}
                                onLoadMaterial={loadMaterial}
                                onDeleteMaterial={deleteMaterial}
                                onSelectSession={(sessionId) => {
                                    // Get latest session data from sessionManager
                                    const session = sessionManager.getSession(sessionId);
                                    if (session) {
                                        setSelectedSessionForModal(session);
                                        handleSelectSession(sessionId);
                                    }
                                }}
                            />
                        )}

                        {/* Scroll to Top Button */}
                        <ScrollToTop isDarkMode={isDarkMode} />

                        {/* Toast Notifications */}
                        <ToastContainer toasts={toasts} onClose={removeToast} />



                        {/* Post-Onboarding PWA Install Prompt */}
                        <PostOnboardingPWAPrompt
                            isOpen={showPWAPrompt}
                            onClose={() => setShowPWAPrompt(false)}
                            onInstall={async () => {
                                if (deferredPWAPrompt) {
                                    try {
                                        await deferredPWAPrompt.prompt();
                                        const { outcome } = await deferredPWAPrompt.userChoice;
                                        if (outcome === 'accepted') {
                                            showToast('success', 'Installing Luca...');
                                            setShowPWALoading(true);
                                        }
                                        setDeferredPWAPrompt(null);
                                    } catch (error) {
                                        console.error('[PWA] Install error:', error);
                                    }
                                }
                                setShowPWAPrompt(false);
                            }}
                            isDarkMode={isDarkMode}
                            canInstallPWA={!!deferredPWAPrompt}
                            isIOS={/iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream}
                            config={config}
                            showToast={showToast}
                        />

                        {/* PWA Installation Loading Screen */}
                        <PWAInstallLoadingScreen
                            isVisible={showPWALoading}
                            isDarkMode={isDarkMode}
                            onComplete={handlePWALoadingComplete}
                        />

                        {/* Open PWA Prompt - Shows when user has PWA installed but is in browser */}
                        <OpenPWAPrompt isDarkMode={isDarkMode} />

                        {/* Active Events Banner */}
                        {activeEvents.length > 0 && (
                            <div className="fixed bottom-24 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)]">
                                <EventBanner
                                    events={activeEvents}
                                    isDarkMode={isDarkMode}
                                    onEventClick={(evt) => {
                                        setSelectedEvent(evt);
                                        setIsEventModalOpen(true);
                                    }}
                                />
                            </div>
                        )}

                        {/* Generate Materials Modal */}
                        {isGenerateMaterialsOpen && (
                            <GenerateMaterialsModal
                                isOpen={isGenerateMaterialsOpen}
                                onClose={() => setIsGenerateMaterialsOpen(false)}
                                onGenerate={(type) => {
                                    // Feature access gating
                                    const featureKeyMap: Record<string, string> = {
                                        quick_bites: 'quickBites',
                                        readable: 'readables',
                                        quiz: 'quiz',
                                        mock_exam: 'mockExam',
                                        practice_problems: 'practiceProblems',
                                        study_guide: 'studyGuide',
                                    };
                                    const featureKey = featureKeyMap[type] || type;
                                    const userTier = user ? determineUserTier(user) : 'standard';

                                    // Dynamic import for feature access check
                                    import('./services/featureAccessService').then(({ hasFeatureAccess, getFeatureMinimumTier }) => {
                                        const canAccess = hasFeatureAccess(featureKey, userTier, featureAccessConfig);

                                        if (!canAccess) {
                                            const minTier = getFeatureMinimumTier(featureKey, featureAccessConfig);
                                            const tierLabel = minTier === 'max' ? 'MAX' : 'Premium';
                                            const tierEmoji = minTier === 'max' ? '💎' : '👑';
                                            showToast('warning', `${tierEmoji} ${featureKey === 'quickBites' ? 'Quick Bites' : featureKey === 'readables' ? 'Readables' : type} requires ${tierLabel}. Upgrade to unlock!`);
                                            setTokenPurchaseInitialTab('premium');
                                            setIsTokenPurchaseOpen(true);
                                            return;
                                        }

                                        // Feature is accessible - open the appropriate config modal
                                        if (type === 'quick_bites') {
                                            setIsQuickBitesConfigOpen(true);
                                        } else if (type === 'readable') {
                                            setIsReadablesConfigOpen(true);
                                        } else if (type === 'quiz') {
                                            setIsQuizConfigOpen(true);
                                        } else if (type === 'mock_exam') {
                                            setIsMockExamConfigOpen(true);
                                        } else if (type === 'practice_problems') {
                                            setIsPracticeProblemsConfigOpen(true);
                                        } else if (type === 'study_guide') {
                                            setIsStudyGuideConfigOpen(true);
                                        }
                                    });
                                }}
                                isDarkMode={isDarkMode}
                                user={user}
                                featureAccess={featureAccessConfig}
                            />
                        )}

                        {/* Readables Config Modal */}
                        {isReadablesConfigOpen && (
                            <ReadablesConfigModal
                                isOpen={isReadablesConfigOpen}
                                onClose={() => {
                                    if (!readableGenerating) setIsReadablesConfigOpen(false);
                                }}
                                featureAccess={featureAccessConfig}
                                onGenerate={async (readableConfig) => {
                                    if (!user) return;
                                    const handleUserUpdate = (updates: Partial<User>) => setUser(prev => prev ? { ...prev, ...updates } : prev);
                                    setReadableGenerating(true);
                                    setReadableProgress(0);
                                    setReadableStatus('Starting...');
                                    try {
                                        const { generateReadableContent } = await import('./services/readablesService');
                                        const { generateReadablePDF, downloadReadablePDF } = await import('./services/readablesPDFService');
                                        const readable = await generateReadableContent(
                                            readableConfig,
                                            user,
                                            handleUserUpdate,
                                            (progress, status) => {
                                                setReadableProgress(progress);
                                                setReadableStatus(status);
                                            }
                                        );
                                        if (readable.status === 'completed') {
                                            setReadableStatus('Generating PDF...');
                                            const blob = await generateReadablePDF(readable);
                                            await downloadReadablePDF(blob, readable);
                                            setReadableStatus('Done!');
                                            setReadableProgress(100);
                                            setTimeout(() => {
                                                setIsReadablesConfigOpen(false);
                                                setReadableGenerating(false);
                                                setReadableProgress(0);
                                                setReadableStatus('');
                                            }, 1500);
                                        } else {
                                            throw new Error(readable.error || 'Generation failed');
                                        }
                                    } catch (error: any) {
                                        console.error('[Readables] Generation error:', error);
                                        setReadableStatus(`Error: ${error.message}`);
                                        setTimeout(() => {
                                            setReadableGenerating(false);
                                            setReadableProgress(0);
                                            setReadableStatus('');
                                        }, 3000);
                                    }
                                }}
                                isDarkMode={isDarkMode}
                                user={user}
                                isGenerating={readableGenerating}
                                generationProgress={readableProgress}
                                generationStatus={readableStatus}
                            />
                        )}

                        {/* QuickBites Config Modal */}
                        {isQuickBitesConfigOpen && (
                            <QuickBitesConfigModal
                                isOpen={isQuickBitesConfigOpen}
                                onClose={() => {
                                    if (!quickBitesGenerating) setIsQuickBitesConfigOpen(false);
                                }}
                                featureAccess={featureAccessConfig}
                                onGenerate={async (qbConfig) => {
                                    if (!user) return;
                                    const handleUserUpdate = (updates: Partial<User>) => setUser(prev => prev ? { ...prev, ...updates } : prev);
                                    setQuickBitesGenerating(true);
                                    setQuickBitesProgress(0);
                                    setQuickBitesStatus('Starting...');
                                    try {
                                        const { generateQuickBites } = await import('./services/quickBitesService');
                                        const result = await generateQuickBites(
                                            qbConfig,
                                            user,
                                            handleUserUpdate,
                                            (progress, status) => {
                                                setQuickBitesProgress(progress);
                                                setQuickBitesStatus(status);
                                            }
                                        );
                                        if (result.status === 'completed' && result.bites.length > 0) {
                                            setQuickBitesResult(result);
                                            // Persist to localStorage for Generated Materials library
                                            try {
                                                const qbKey = `luca_quickbites_${user.id}`;
                                                const existing = JSON.parse(localStorage.getItem(qbKey) || '[]');
                                                existing.unshift(result);
                                                localStorage.setItem(qbKey, JSON.stringify(existing.slice(0, 50)));
                                            } catch (e) { console.warn('[QuickBites] Failed to persist:', e); }
                                            setQuickBitesStatus('Done!');
                                            setQuickBitesProgress(100);
                                            setTimeout(() => {
                                                setIsQuickBitesConfigOpen(false);
                                                setQuickBitesGenerating(false);
                                                setQuickBitesProgress(0);
                                                setQuickBitesStatus('');
                                                setIsQuickBitesViewerOpen(true);
                                            }, 800);
                                            showToast('success', `⚡ ${result.bites.length} Quick Bites generated!`);
                                        } else {
                                            throw new Error(result.error || 'Generation failed');
                                        }
                                    } catch (error: any) {
                                        console.error('[QuickBites] Generation error:', error);
                                        setQuickBitesStatus(`Error: ${error.message}`);
                                        showToast('error', `Failed to generate Quick Bites: ${error.message}`);
                                        setTimeout(() => {
                                            setQuickBitesGenerating(false);
                                            setQuickBitesProgress(0);
                                            setQuickBitesStatus('');
                                        }, 3000);
                                    }
                                }}
                                isDarkMode={isDarkMode}
                                user={user}
                                isGenerating={quickBitesGenerating}
                                progress={quickBitesProgress}
                                status={quickBitesStatus}
                            />
                        )}

                        {/* QuickBites Viewer */}
                        {isQuickBitesViewerOpen && quickBitesResult && (
                            <QuickBitesViewer
                                result={quickBitesResult}
                                isDarkMode={isDarkMode}
                                onClose={() => {
                                    setIsQuickBitesViewerOpen(false);
                                }}
                            />
                        )}

                        {/* Event Modal */}
                        {isEventModalOpen && selectedEvent && (
                            <EventModal
                                event={selectedEvent}
                                user={user}
                                isDarkMode={isDarkMode}
                                onClose={() => {
                                    setIsEventModalOpen(false);
                                    setSelectedEvent(null);
                                }}
                                onStartQuiz={(evt) => {
                                    // Generate a quiz/exam for the event topic
                                    setIsEventModalOpen(false);
                                    if (evt.type.includes('quiz')) {
                                        setQuizInitialTopic(evt.subject);
                                        setIsQuizConfigOpen(true);
                                    } else {
                                        setIsMockExamConfigOpen(true);
                                    }
                                }}
                                updateUser={(updates) => setUser(prev => prev ? { ...prev, ...updates } : prev)}
                                showToast={showToast}
                            />
                        )}

                        {/* Generated Materials Library */}
                        {isReadablesLibraryOpen && (
                            <GeneratedMaterialsLibrary
                                isOpen={isReadablesLibraryOpen}
                                onClose={() => setIsReadablesLibraryOpen(false)}
                                onCreateNew={() => {
                                    setIsReadablesLibraryOpen(false);
                                    setIsGenerateMaterialsOpen(true);
                                }}
                                isDarkMode={isDarkMode}
                                user={user}
                                sessions={displayedSessions}
                                onViewQuickBites={(result) => {
                                    setQuickBitesResult(result);
                                    setIsQuickBitesViewerOpen(true);
                                    setIsReadablesLibraryOpen(false);
                                }}
                                onLoadMaterial={(materialId) => {
                                    loadMaterial(materialId);
                                    setIsReadablesLibraryOpen(false);
                                }}
                            />
                        )}

                        {/* Social Modal */}
                        {isSocialModalOpen && (
                            <SocialModal
                                isOpen={isSocialModalOpen}
                                onClose={() => { setIsSocialModalOpen(false); setMessagingFriend(null); }}
                                user={user}
                                isDarkMode={isDarkMode}
                                config={config}
                                onOpenDirectMessage={(friend) => {
                                    setMessagingFriend(friend);
                                    setIsDirectMessageOpen(true);
                                    setIsSocialModalOpen(false);
                                }}
                                initialTab={socialInitialTab}
                                initialDMFriend={messagingFriend}
                            />
                        )}

                        {/* Direct Message Modal */}
                        {isDirectMessageOpen && messagingFriend && user && (
                            <DirectMessageModal
                                isOpen={isDirectMessageOpen}
                                onClose={() => {
                                    setIsDirectMessageOpen(false);
                                    setMessagingFriend(null);
                                }}
                                friend={messagingFriend}
                                currentUser={user}
                                isDarkMode={isDarkMode}
                            />
                        )}

                        {/* Notifications Modal */}
                        <NotificationsModal
                            isOpen={isNotificationsModalOpen}
                            onClose={() => setIsNotificationsModalOpen(false)}
                            user={user}
                            isDarkMode={isDarkMode}
                            showToast={showToast}
                            onOpenDM={(friend) => {
                                setIsNotificationsModalOpen(false);
                                setMessagingFriend(friend);
                                setIsSocialModalOpen(true);
                            }}
                            onOpenSocial={(tab) => {
                                setIsNotificationsModalOpen(false);
                                if (tab) setSocialInitialTab(tab as any);
                                setIsSocialModalOpen(true);
                            }}
                        />

                        {/* Group Study Modal */}
                        <GroupStudyModal
                            isOpen={isGroupStudyModalOpen}
                            onClose={() => setIsGroupStudyModalOpen(false)}
                            user={user}
                            isDarkMode={isDarkMode}
                            updateUser={(updates) => {
                                if (user) {
                                    const updatedUser = { ...user, ...updates };
                                    setUser(updatedUser);
                                    // Sync to Firebase
                                    if (firebase.updateUser) {
                                        firebase.updateUser(user.id, updates);
                                    }
                                }
                            }}
                            showToast={showToast}
                        />

                        {/* Study Mode Modals */}
                        {user && (
                            <>
                                <StudyModeIntroModal
                                    isOpen={isStudyModeIntroOpen}
                                    onClose={() => setIsStudyModeIntroOpen(false)}
                                    isDarkMode={isDarkMode}
                                    examMode={user.examMode || 'cpa'}
                                    onConfirm={() => {
                                        setIsStudyModeIntroOpen(false);
                                        setIsStudyDashboardOpen(true);
                                    }}
                                />

                                <StudyDashboard
                                    isOpen={isStudyDashboardOpen}
                                    onClose={() => setIsStudyDashboardOpen(false)}
                                    user={user}
                                    examMode={user.examMode || 'cpa'}
                                    isDarkMode={isDarkMode}
                                    onOpenFlashcards={() => {
                                        setIsStudyDashboardOpen(false);
                                        setIsFlashcardModalOpen(true);
                                    }}
                                    onOpenErrorJournal={() => {
                                        setIsStudyDashboardOpen(false);
                                        setIsErrorJournalModalOpen(true);
                                    }}
                                    onOpenMootCourt={() => {
                                        setIsStudyDashboardOpen(false);
                                        setIsMootCourtModalOpen(true);
                                    }}
                                    onOpenDocumentDrafting={() => {
                                        setIsStudyDashboardOpen(false);
                                        setIsDocumentDraftingModalOpen(true);
                                    }}
                                    onOpenQuiz={() => {
                                        setIsStudyDashboardOpen(false);
                                        setIsQuizConfigOpen(true);
                                    }}
                                    onOpenMockExam={() => {
                                        setIsStudyDashboardOpen(false);
                                        setIsMockExamConfigOpen(true);
                                    }}
                                    onOpenCodalBrowser={() => {
                                        setIsStudyDashboardOpen(false);
                                        setIsCodalBrowserOpen(true);
                                    }}
                                    onOpenStandards={() => {
                                        setIsStudyDashboardOpen(false);
                                        setIsStandardsBrowserOpen(true);
                                    }}
                                />

                                <FlashcardReviewModal
                                    isOpen={isFlashcardModalOpen}
                                    onClose={() => setIsFlashcardModalOpen(false)}
                                    user={user}
                                    examMode={user.examMode || 'cpa'}
                                    isDarkMode={isDarkMode}
                                    showToast={showToast}
                                />

                                <ErrorJournalModal
                                    isOpen={isErrorJournalModalOpen}
                                    onClose={() => setIsErrorJournalModalOpen(false)}
                                    user={user}
                                    examMode={user.examMode || 'cpa'}
                                    isDarkMode={isDarkMode}
                                    showToast={showToast}
                                />

                                <MootCourtModal
                                    isOpen={isMootCourtModalOpen}
                                    onClose={() => setIsMootCourtModalOpen(false)}
                                    user={user}
                                    examMode={user.examMode || 'cpa'}
                                    isDarkMode={isDarkMode}
                                    showToast={showToast}
                                />

                                <DocumentDraftingModal
                                    isOpen={isDocumentDraftingModalOpen}
                                    onClose={() => setIsDocumentDraftingModalOpen(false)}
                                    user={user}
                                    isDarkMode={isDarkMode}
                                    showToast={showToast}
                                />

                                <QuizModal
                                    isOpen={isQuizModalOpen}
                                    onClose={() => setIsQuizModalOpen(false)}
                                    user={user}
                                    examMode={user.examMode || 'cpa'}
                                    isDarkMode={isDarkMode}
                                    showToast={showToast}
                                />

                                <MockExamModal
                                    isOpen={isMockExamModalOpen}
                                    onClose={() => setIsMockExamModalOpen(false)}
                                    user={user}
                                    examMode={user.examMode || 'cpa'}
                                    isDarkMode={isDarkMode}
                                    showToast={showToast}
                                />

                                <CodalBrowserModal
                                    isOpen={isCodalBrowserOpen}
                                    onClose={() => setIsCodalBrowserOpen(false)}
                                    examMode={user.examMode || 'cpa'}
                                    isDarkMode={isDarkMode}
                                />

                                <StandardsBrowserModal
                                    isOpen={isStandardsBrowserOpen}
                                    onClose={() => setIsStandardsBrowserOpen(false)}
                                    examMode={user.examMode || 'cpa'}
                                    isDarkMode={isDarkMode}
                                />

                                {/* Referral Modal */}
                                <ReferralModal
                                    isOpen={isReferralModalOpen}
                                    onClose={() => setIsReferralModalOpen(false)}
                                    user={user}
                                    isDarkMode={isDarkMode}
                                    config={config}
                                />

                                {/* Customer Support Modal - Re-adding as it appeared missing */}
                                <CustomerSupportModal
                                    isOpen={isSupportOpen}
                                    onClose={() => setIsSupportOpen(false)}
                                    user={user}
                                    config={config}
                                    isDarkMode={isDarkMode}
                                    showToast={showToast}
                                />


                            </>
                        )}

                        {/* Unified Onboarding Flow */}
                        {isOnboardingOpen && (
                            <StreamlinedOnboarding
                                isOpen={isOnboardingOpen}
                                onClose={handleOnboardingClose}
                                isDarkMode={isDarkMode}
                                config={config}
                            />
                        )}

                        {/* Mastery Checklist Widget */}
                        <MasteryChecklist
                            isOpen={isMasteryChecklistOpen && !isOnboardingOpen && !isInitialLoading && !!user}
                            onClose={() => setIsMasteryChecklistOpen(false)}
                            isDarkMode={isDarkMode}
                            onAction={(action) => {
                                console.log('Mastery Action:', action);
                                switch (action) {
                                    case 'start_tour':
                                        setIsGuidedTourOpen(true);
                                        break;
                                    case 'focus_chat':
                                        inputRef.current?.focus();
                                        break;
                                    case 'open_study_dashboard':
                                        setIsStudyDashboardOpen(true);
                                        break;
                                    case 'open_quiz_config':
                                        setIsQuizConfigOpen(true);
                                        break;
                                    case 'trigger_upload':
                                        fileInputRef.current?.click();
                                        break;
                                    case 'open_flashcards':
                                        setIsFlashcardModalOpen(true);
                                        break;
                                    case 'open_user_dashboard':
                                        setIsUserDashboardOpen(true);
                                        break;
                                    case 'open_notifications':
                                        setIsUserDashboardOpen(true); // Notifications are in dashboard/profile
                                        break;
                                    case 'open_profile':
                                        setIsProfileMenuOpen(true);
                                        break;
                                }
                            }}
                        />

                        {/* Interactive UI Tour - After onboarding/login */}
                        <GuidedTour
                            isOpen={isGuidedTourOpen}
                            onClose={() => setIsGuidedTourOpen(false)}
                            steps={defaultTourSteps}
                            onComplete={() => console.log('[Tour] Completed')}
                        />

                        {/* APK Auto-Update Modal */}
                        {showAPKUpdateModal && apkUpdateInfo && (
                            <APKUpdateModal
                                updateInfo={apkUpdateInfo}
                                onClose={() => {
                                    setShowAPKUpdateModal(false);
                                }}
                                onUpdateStarted={() => {
                                    console.log('[APKUpdate] Update download started');
                                }}
                            />
                        )}

                        {/* User Dashboard Modal */}
                        <UserDashboardModal
                            isOpen={isUserDashboardOpen}
                            onClose={() => { setIsUserDashboardOpen(false); setStoreInitialTab(undefined); }}
                            isDarkMode={isDarkMode}
                            user={user}
                            config={config}
                            promoCodes={promoCodes}
                            recentSessions={displayedSessions}
                            onOpenFeature={handleOpenFeature}
                            onOpenTokenPurchase={() => setIsTokenPurchaseOpen(true)}
                            onOpenSocial={() => handleOpenSocial()}
                            initialTab={dashboardInitialTab}
                            storeInitialTab={storeInitialTab}
                            tokenPurchaseProps={user ? commonStoreProps : undefined}
                            onStartPreliminaryExam={(subjectId) => {
                                console.log('[CPALE] Starting prelim exam for:', subjectId);
                                setSelectedPreliminarySubject(subjectId);
                                setIsPreliminaryExamOpen(true);
                                setDashboardInitialTab('mastery'); // Ensure we return to mastery tab
                                setIsUserDashboardOpen(false); // Close dashboard to show exam
                            }}
                            onStartTopicExam={(topicId, subjectId) => {
                                console.log('[CPALE] Starting topic exam:', topicId, subjectId);
                                setSelectedTopicExamId(topicId);
                                setSelectedTopicExamSubjectId(subjectId);
                                setIsUserDashboardOpen(false);
                            }}
                            onStartStudy={(topic) => {
                                setIsUserDashboardOpen(false);
                                setDashboardInitialTab('mastery'); // Return here later
                                setInputValue(`Help me study ${topic.title}. Let's focus on ${topic.description}.`);
                                // Ideally trigger a 'sendMessage' or specific context set here
                            }}
                        />

                        {/* CPALE Preliminary Exam Modal */}
                        {isPreliminaryExamOpen && selectedPreliminarySubject && (
                            <React.Suspense fallback={<LoadingScreen isDarkMode={isDarkMode} />}>
                                <PreliminaryExamModal
                                    isOpen={isPreliminaryExamOpen}
                                    onClose={() => {
                                        setIsPreliminaryExamOpen(false);
                                        setSelectedPreliminarySubject(null);
                                        setIsUserDashboardOpen(true); // Re-open dashboard on close
                                    }}
                                    subjectId={selectedPreliminarySubject}
                                    isDarkMode={isDarkMode}
                                    onPass={() => {
                                        showToast('success', 'You have passed the preliminary exam! Mastery Path Unlocked. 🏆');
                                        // Dashboard state will auto-update via local storage listener or we force refresh 
                                        // The modal onClose re-opening dashboard should suffice
                                    }}
                                />
                            </React.Suspense>
                        )}

                        {/* CPALE Topic Exam Modal */}
                        {selectedTopicExamId && (
                            <React.Suspense fallback={<LoadingScreen isDarkMode={isDarkMode} />}>
                                <TopicExamModal
                                    isOpen={!!selectedTopicExamId}
                                    onClose={() => {
                                        setSelectedTopicExamId(null);
                                        setSelectedTopicExamSubjectId(null);
                                        setIsUserDashboardOpen(true);
                                    }}
                                    topicId={selectedTopicExamId}
                                    subjectId={selectedTopicExamSubjectId || ''}
                                    isDarkMode={isDarkMode}
                                    onPass={() => {
                                        showToast('success', 'Topic Mastered! Next topic unlocked.');
                                        // Dashboard will refresh state automatically via service/local storage
                                    }}
                                />
                            </React.Suspense>
                        )}

                        {/* Mastery Path Modal */}
                        {isMasteryPathOpen && (
                            <React.Suspense fallback={<LoadingScreen isDarkMode={isDarkMode} />}>
                                <MasteryPathModal
                                    isOpen={isMasteryPathOpen}
                                    onClose={() => setIsMasteryPathOpen(false)}
                                    isDarkMode={isDarkMode}
                                    onStartExam={(topicId, subjectId) => {
                                        setIsMasteryPathOpen(false);
                                        setSelectedTopicExamId(topicId);
                                        setSelectedTopicExamSubjectId(subjectId);
                                    }}
                                    onStartStudy={(topic, subjectId) => {
                                        setIsMasteryPathOpen(false);
                                        showToast('info', `📚 Study mode: ${topic.title}. Chat with Luca about this topic to earn Mastery Points!`);
                                    }}
                                    onOpenGuide={(topic, subjectId) => {
                                        setActiveGuideTopic(topic);
                                        setActiveGuideSubjectId(subjectId);
                                    }}
                                    onOpenStore={() => {
                                        setDashboardInitialTab('store');
                                        setStoreInitialTab('mastery');
                                        setIsUserDashboardOpen(true);
                                    }}
                                />
                            </React.Suspense>
                        )}

                        {/* Full-Screen Study Guide */}
                        {activeGuideTopic && activeGuideSubjectId && (
                            <React.Suspense fallback={<LoadingScreen isDarkMode={isDarkMode} />}>
                                <TopicGuidePanel
                                    topic={activeGuideTopic}
                                    subjectId={activeGuideSubjectId}
                                    isDarkMode={isDarkMode}
                                    onClose={() => { setActiveGuideTopic(null); setActiveGuideSubjectId(null); }}
                                    onStartExam={(topicId, subjectId) => {
                                        setActiveGuideTopic(null);
                                        setActiveGuideSubjectId(null);
                                        setSelectedTopicExamId(topicId);
                                        setSelectedTopicExamSubjectId(subjectId);
                                    }}
                                    user={user}
                                    updateUser={(updates) => {
                                        if (user) {
                                            const updatedUser = { ...user, ...updates };
                                            setUser(updatedUser);
                                            if (firebase.updateUser) {
                                                firebase.updateUser(user.id, updates);
                                            }
                                        }
                                    }}
                                />
                            </React.Suspense>
                        )}

                        {/* Guest Limit Modal */}
                        <GuestLimitModal
                            isOpen={showGuestLimitModal}
                            onClose={() => setShowGuestLimitModal(false)}
                            onLogin={() => {
                                setShowGuestLimitModal(false);
                                setAuthMode('login');
                                setIsAuthModalOpen(true);
                            }}
                            onRegister={() => {
                                setShowGuestLimitModal(false);
                                setAuthMode('register');
                                setIsAuthModalOpen(true);
                            }}
                            isDarkMode={isDarkMode}
                        />


                        {/* Streak Frozen Modal */}
                        <StreakFrozenModal
                            isOpen={isStreakFrozenModalOpen}
                            onClose={() => setIsStreakFrozenModalOpen(false)}
                            user={user}
                            onUserUpdate={(updatedUser) => {
                                setUser(updatedUser);
                                // Ensure local storage is kept in sync if modal doesn't do it perfectly (though it does)
                                localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
                            }}
                        />

                        {/* Usage Alert Modal */}
                        <UsageAlertModal
                            isOpen={usageAlertModal.isOpen}
                            onClose={() => setUsageAlertModal(prev => ({ ...prev, isOpen: false }))}
                            type={usageAlertModal.type}
                            usagePercent={usageAlertModal.usagePercent}
                            tokenLimit={user?.isMax || user?.accountTier === 'max'
                                ? (config?.maxTokenLimit || 50000)
                                : user?.isPremium
                                    ? (config?.premiumTokenLimit || 15000)
                                    : (config?.freeTokenLimit || 2000)}
                            currentUsage={user?.dailyTokenUsage || 0}
                            isPremium={user?.isPremium || false}
                            isDarkMode={isDarkMode}
                        />

                        {/* Force Password Change Modal - Uncloseable */}
                        {user && user.requiresPasswordChange && (
                            <ChangePasswordModal
                                isOpen={true}
                                username={user.username || ''}
                                onSuccess={(updatedUser) => {
                                    setUser(updatedUser);
                                    localStorage.setItem('ledger_current_user', JSON.stringify(updatedUser));
                                    showToast('success', 'Password updated successfully!');
                                }}
                                onShowToast={showToast}
                            />
                        )}

                        {/* PWA notifications removed - updates happen silently in background */}


                    </React.Suspense>
                    {/* Loading Screen - Only show on initial mount, never during navigation/transitions */}
                    {isInitialLoading && sessionStorage.getItem('luca_initial_loading_dismissed') !== 'true' && (
                        <LoadingScreen isDarkMode={isDarkMode} />
                    )}
                </main >
            </div >
        </div >
    );
};

export default App;
